import { Fragment, lazy, Suspense, useEffect, useMemo, useRef, useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from './i18n';
import LanguageSwitcher from './LanguageSwitcher';
import { formatCurrency, setCurrency } from './currency';
import { applySeo, breadcrumbLd, categorySeo, PAGE_SEO } from './seo';
import { SESSION_EXPIRED_EVENT } from './apiInterceptor';
import './App.css';
import './GetTheApp.css';
import { AuthContext } from './AuthContext';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import SharedWishlistPage from './SharedWishlistPage';
import CartRecoveryPage from './CartRecoveryPage';
import CartRemindersOffPage from './CartRemindersOffPage';
import Dashboard from './Dashboard';
import HeroCarousel from './HeroCarousel';
import { isMobileUI, getSalesChannel } from './mobile/mobileUI';
import { recordCategoryView } from './mobile/categoryAffinity';
import { GreatFindsSection, TileSection, buildTiles, exactDiscountPct } from './mobile/MobileFeedSections';
import AdminForcePasswordReset from './AdminForcePasswordReset';
import { LogoIcon, BootScreen } from './Logo';
import { getDeviceFingerprint } from './deviceFingerprint';
import { SkeletonCard } from './Skeleton';
import { useFocusTrap } from './useFocusTrap';
import ProductImage from './ProductImage';
import BottomNav from './BottomNav';
import AccountSheet from './AccountSheet';
import MobileTopBar from './MobileTopBar';
import NotFound from './NotFound';
import Footer from './Footer';
import ChatWidget from './ChatWidget';
import InstallAppBanner from './InstallAppBanner';
import AppUpdateNotice from './AppUpdateNotice';
import PullToRefresh from './PullToRefresh';
import { API_BASE_URL, mediaUrl, canOverrideApiBase } from './config';
import DevApiSettings from './DevApiSettings';
import { NAVY, ORANGE, BLUE } from './theme';
import { RatingStars } from './ProductRating';
import { setupNativeChrome, useHardwareBackButton, useDeepLinks, usePushRegistration } from './nativeBridge';
// Whether to render as an installed app (packaged Capacitor build or a PWA
// opened from the home screen). Distinct from isNativePlatform(), which stays
// the right question for anything needing a Capacitor plugin.
import { isInstalledApp } from './mobile/mobileUI';
import { PRODUCT_IMAGE_PLACEHOLDER, fallbackToPlaceholder } from './productPlaceholder';
import UpdateAppBanner from './UpdateAppBanner';
import { productPath, productLinkId } from './productPath';
import { rankTopSales, DEFAULT_TOP_SALES_RULES } from './topSales';
import {
  IconSun, IconMoon, IconCart, IconCheck, IconCheckCircle, IconStar, IconShield,
  IconArrowDown, IconArrowRight, IconTruck, IconDownload, IconTrash, IconZap,
  IconCreditCard, IconDollarSign, IconGift, IconRocket, IconTag, IconMapPin,
  IconShieldCheck, IconTicket, IconAward,
  IconEdit, IconMap, IconPackage, IconMail, IconHeart, IconUser, IconX, IconBarChart, IconStore,
  IconBell, IconLogOut, IconFlame, IconSearch, IconSparkle, IconSettings,
  IconFootprint, IconShoppingBag2, IconLaptop, IconHeadphones, IconClock, IconHome, IconSofa,
  IconLeaf, IconBriefcase, IconPawPrint, IconPill, IconPlug, IconTeddyBear, IconBook, IconGem,
  IconBag, IconCar, IconTrophy, IconBaby, IconShirt, IconDumbbell, IconWatch,
  IconAlertTriangle, IconPhone, IconChevronDown, IconClipboard,
} from './Icons';

// Route-level chunks. None of these are on the first-paint path -- account
// pages, legal text, order tracking, the seller application -- so keeping them
// out of the main bundle costs one chunk fetch on first visit and saves it for
// every visitor who never opens them. The five LegalPages handles are separate
// exports of one module, so they all resolve the same chunk.
const OrderHistoryPage = lazy(() => import('./OrderHistoryPage'));
const DisputePage = lazy(() => import('./DisputePage'));
const SellerRegistration = lazy(() => import('./SellerRegistration'));
const BrandsPage = lazy(() => import('./BrandsPage'));
const CareersPage = lazy(() => import('./CareersPage'));
const AboutPage = lazy(() => import('./AboutPage'));
const OrderTrackingPage = lazy(() => import('./OrderTrackingPage'));
const PrivacyPolicyPage = lazy(() => import('./LegalPages').then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('./LegalPages').then((m) => ({ default: m.TermsPage })));
const CookiePolicyPage = lazy(() => import('./LegalPages').then((m) => ({ default: m.CookiePolicyPage })));
const ReturnsRefundsPage = lazy(() => import('./LegalPages').then((m) => ({ default: m.ReturnsRefundsPage })));
const ShippingPolicyPage = lazy(() => import('./LegalPages').then((m) => ({ default: m.ShippingPolicyPage })));

// Code-split: admin/seller code is dead weight for ordinary shoppers, so only
// fetch it when someone actually navigates to /admin or /seller-portal.
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const SuperAdminPortal = lazy(() => import('./SuperAdminPortal'));
const SellerPortal = lazy(() => import('./SellerPortal'));
const AdminLoginPage = lazy(() => import('./AdminLoginPage'));

// Account, support and product-detail pages. None of these paint on the
// storefront's first screen, but each was a static import, so its code -- and,
// more expensively, its dependencies -- sat in the main bundle that gates the
// very first render. Two dominated that cost:
//
//   DeliveryMapPicker   drags in leaflet + react-leaflet for a map that only
//                       appears after a shopper opens the address step and taps
//                       "pin your location on the map".
//   DeliveryConfirmPage drags in qr-scanner for a portal only delivery agents
//                       ever open -- no shopper loads that screen at all.
//
// Every render site below gets its own <Suspense> rather than leaning on the
// app-level boundary in App(), because these components sit *inside* the page
// chrome: suspending to the outer boundary would blank SubPageHeader and
// BottomNav too, so the whole screen would flash instead of just the slot that
// is actually still loading.
const ProductDetailPage = lazy(() => import('./ProductDetailPage'));
const SellerStorePage = lazy(() => import('./SellerStorePage'));
const WishlistPage = lazy(() => import('./WishlistPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const AddressBook = lazy(() => import('./AddressBook'));
const NotificationsPage = lazy(() => import('./NotificationsPage'));
const ApplicationTracker = lazy(() => import('./ApplicationTracker'));
const DeliveryMapPicker = lazy(() => import('./DeliveryMapPicker'));
const DeliveryConfirmPage = lazy(() => import('./DeliveryConfirmPage'));
const ContactPage = lazy(() => import('./ContactPage'));
const GetTheAppPage = lazy(() => import('./GetTheAppPage'));
// Six named exports of a single module, so all six resolve the same chunk --
// the same arrangement the LegalPages handles above use.
const FaqPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.FaqPage })));
const SellerFaqPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.SellerFaqPage })));
const HelpCenterPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.HelpCenterPage })));
const CommissionPlanPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.CommissionPlanPage })));
const SellerGuidelinesPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.SellerGuidelinesPage })));
const NewsUpdatesPage = lazy(() => import('./SupportPages').then((m) => ({ default: m.NewsUpdatesPage })));

// One shared element rather than a fresh <div> per boundary: these chunks are
// small and usually already cached, so the fallback is on screen for a frame or
// two, and anything more elaborate reads as a flash of junk.
const PAGE_FALLBACK = <div className="loading loading-inline">Loading...</div>;

// Category names are seller/admin-entered product data (stored in English),
// not app UI text -- they can't be translated the way a button label can
// without a real per-category translation table. This covers the small,
// known, fixed set of categories this storefront actually uses (see
// categories.js) with a real translation, and safely falls back to the raw
// English string for anything outside that set (a newly-added category, a
// seller-entered one-off) rather than showing a missing-translation error.
const translateCategory = (cat, t) => t(`categories.${cat.toLowerCase()}`, { defaultValue: cat });

// Categories are whatever string sellers/admins set on a product, not a fixed
// enum, so this only covers common storefront categories -- anything else
// falls back to a generic shopping-bag icon.
const CATEGORY_ICONS = {
  Shoes: IconFootprint,
  Accessories: IconShoppingBag2,
  Clothes: IconShirt,
  Clothing: IconShirt,
  Electronics: IconLaptop,
  Audio: IconHeadphones,
  Wearables: IconWatch,
  Fitness: IconDumbbell,
  Home: IconHome,
  Beauty: IconSparkle,
  Toys: IconTeddyBear,
  Books: IconBook,
  Sports: IconTrophy,
  Jewelry: IconGem,
  Bags: IconBag,
  Kids: IconBaby,
  Grocery: IconCart,
  Furniture: IconSofa,
  Garden: IconLeaf,
  Automotive: IconCar,
  Office: IconBriefcase,
  Pet: IconPawPrint,
  Health: IconPill,
  Gadgets: IconPlug,
};
const getCategoryIcon = (cat) => CATEGORY_ICONS[cat] || IconBag;

// How many cards one "page" of a category carousel advances by, on an
// autoplay tick or an arrow click -- matches the "at least 5 products"
// premise of each row without needing a fixed, non-responsive card width.
// The sort options, in the order they appear as chips on mobile. Same values
// the desktop <select> uses -- both write to `sortBy`, so the sorting logic
// itself has one code path. The two price options use short labels here
// ("Lowest price" rather than "Price: Low -> High"): the full ones are around
// seventeen characters and make a chip wider than a third of a phone screen.
const SORT_CHIPS = [
  { value: 'default',    key: 'filters.sortPopular' },
  { value: 'trending',   key: 'filters.sortTrending' },
  { value: 'newest',     key: 'filters.sortNewest' },
  { value: 'price-asc',  key: 'filters.sortPriceAscShort' },
  { value: 'price-desc', key: 'filters.sortPriceDescShort' },
  { value: 'rating',     key: 'filters.sortTopRated' },
];

const CAT_CAROUSEL_CARDS_PER_PAGE = 5;

// Auto-advancing product carousel for the homepage's per-category rows --
// mirrors HeroCarousel's timer/pause-on-hover behavior (same 1-carousel-per-
// category idea, just paging through product cards instead of full banners).
function CategoryCarousel({ items, onProductClick }) {
  const { t } = useTranslation();
  const outerRef = useRef(null);
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const pausedRef = useRef(false);  // user is actively hovering/touching this row
  const visibleRef = useRef(false); // this row is actually on screen right now
  // Dots are the touch-device equivalent of the arrow buttons (hidden there,
  // see the @media(hover:none) rule) -- a rough page-position indicator, not
  // an exact one-card-per-dot mapping, since card-per-page is fluid/responsive.
  const [pageCount, setPageCount] = useState(1);
  const [activePage, setActivePage] = useState(0);

  const pageDelta = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.children.length) return 0;
    const card = track.children[0];
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 12;
    const step = card.offsetWidth + gap;
    // A page is however many cards are actually on screen, not a fixed five.
    // The constant is a desktop number; on a phone three cards fit, so
    // scrolling by five skipped two products every time -- they could only
    // be reached by scrolling back. The comment above this component already
    // describes card-per-page as "fluid/responsive"; this measures it.
    const perPage = Math.max(1, Math.round(track.clientWidth / step));
    return step * Math.min(perPage, CAT_CAROUSEL_CARDS_PER_PAGE);
  }, []);

  const scrollByPage = useCallback((dir) => {
    const track = trackRef.current;
    const delta = pageDelta();
    if (!track || !delta) return;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    const atStart = track.scrollLeft <= 4;
    if (dir > 0 && atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
    else if (dir < 0 && atStart) track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
    else track.scrollBy({ left: dir * delta, behavior: 'smooth' });
  }, [pageDelta]);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (pausedRef.current || !visibleRef.current || items.length <= CAT_CAROUSEL_CARDS_PER_PAGE) return;
    timerRef.current = setInterval(() => scrollByPage(1), 6000);
  }, [items.length, scrollByPage]);

  // Every row used to start counting down the instant the page loaded, so
  // rows below the fold had already auto-advanced once or twice before the
  // customer ever scrolled to them, and everything moved in lockstep. Now
  // only the row actually in view runs its timer, and it gets a *fresh* 6s
  // window each time it enters view -- whichever category someone is
  // actually looking at is the one that gets time to browse before it moves.
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) startTimer();
        else clearInterval(timerRef.current);
      },
      { threshold: 0.55 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); clearInterval(timerRef.current); };
  }, [startTimer]);

  const pause = () => { pausedRef.current = true; clearInterval(timerRef.current); };
  const resume = () => { pausedRef.current = false; startTimer(); };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const recomputePageCount = () => {
      if (track.clientWidth > 0) setPageCount(Math.max(1, Math.round(track.scrollWidth / track.clientWidth)));
    };
    recomputePageCount();
    const onScroll = () => {
      if (track.clientWidth > 0) setActivePage(Math.round(track.scrollLeft / track.clientWidth));
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    const resizeObserver = new ResizeObserver(recomputePageCount);
    resizeObserver.observe(track);
    return () => { track.removeEventListener('scroll', onScroll); resizeObserver.disconnect(); };
  }, [items]);

  const goToPage = (i) => {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    startTimer();
  };

  if (!items.length) return null;
  const now = new Date();

  return (
    <div className="cat-carousel-outer-wrap">
    <div
      ref={outerRef}
      className="carousel-track-outer cat-carousel-outer"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
    >
      <div ref={trackRef} className="cat-carousel-track">
        {items.map((p) => {
          const saleNow = p.salePrice && p.saleEndsAt && new Date(p.saleEndsAt) > now;
          const effPrice = saleNow ? p.salePrice : p.price;
          const isOutOfStock = p.stock === 0;
          // Same <=10 threshold as the main grid's "Only N left" urgency cue
          // (see the pagedProducts card below) so the two never disagree.
          const isLowStock = p.stock != null && p.stock > 0 && p.stock <= 10;
          // No discount badge -- see the main product grid for the rationale.
          // salePct/discountPct stay in scope because the strikethrough price
          // below still depends on saleNow/originalPrice.
          const badge =
            isOutOfStock ? { label: t('product.outOfStock'), cls: 'oos' } :
            isLowStock   ? { label: t('product.onlyLeft', { count: p.stock }), cls: 'limited' } :
            (p.avgRating >= 4.5 && (p.reviewCount || 0) >= 50) ? { label: <><IconFlame size={10} /> {t('product.bestSeller')}</>, cls: 'bestseller' } :
            (p.avgRating >= 4.0 && (p.reviewCount || 0) >= 30) ? { label: <><IconStar size={10} /> {t('product.topRated')}</>, cls: 'toprated' } :
            null;
          return (
            <div
              key={p.id}
              className={`cat-carousel-card${isOutOfStock ? ' cat-carousel-card-oos' : ''}`}
              onClick={() => onProductClick(productLinkId(p))}
            >
              <div className="cat-carousel-card-img-wrap">
                {badge && <span className={`cat-carousel-badge cat-carousel-badge-${badge.cls}`}>{badge.label}</span>}
                {/* ProductImage, not a bare <img>: it shows a shimmer while
                    loading and a neutral package icon if the photo fails,
                    where a bare tag shows the browser's broken-image glyph and
                    the raw alt text. Every other product surface already used
                    it, so a failing image looked like a bug here and like an
                    empty slot everywhere else -- which is exactly what Top
                    Sales and Recently Viewed were doing while the external
                    image host was down.
                    `src` is the raw path: ProductImage runs mediaUrl and
                    mediaSrcSet itself. */}
                <ProductImage
                  className="cat-carousel-card-img"
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  sizes="(max-width: 640px) 45vw, 200px"
                />
                {isOutOfStock ? (
                  <div className="cat-carousel-oos-overlay"><span>{t('product.outOfStock')}</span></div>
                ) : (
                  <div className="cat-carousel-quickview">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    </svg>
                    {t('product.quickView')}
                  </div>
                )}
              </div>
              <div className="cat-carousel-card-body">
                {/* Crawlable link to the product -- see the product grid card. */}
                <p className="cat-carousel-card-name">
                  <Link to={productPath(p)} className="pcard-title-link" onClick={e => e.stopPropagation()}>{p.name}</Link>
                </p>
                <div className="cat-carousel-card-price">
                  <span className={saleNow ? 'cat-carousel-price-sale' : 'cat-carousel-price'}>{formatCurrency(effPrice)}</span>
                  {saleNow && <span className="cat-carousel-price-was">{formatCurrency(p.price)}</span>}
                  {!saleNow && p.originalPrice > p.price && <span className="cat-carousel-price-was">{formatCurrency(p.originalPrice)}</span>}
                </div>
                <div className="cat-carousel-card-rating-row">
                  {p.avgRating > 0 ? (
                    <>
                      <span className="cat-carousel-rating-pill">{p.avgRating.toFixed(1)} <IconStar size={9} /></span>
                      <span className="cat-carousel-review-cnt">({p.reviewCount || 0})</span>
                    </>
                  ) : (
                    <span className="cat-carousel-no-rating">{t('product.noReviews')}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {items.length > CAT_CAROUSEL_CARDS_PER_PAGE && (
        <>
          <button className="carousel-arrow carousel-arrow-left" onClick={(e) => { e.stopPropagation(); scrollByPage(-1); startTimer(); }}>&#8249;</button>
          <button className="carousel-arrow carousel-arrow-right" onClick={(e) => { e.stopPropagation(); scrollByPage(1); startTimer(); }}>&#8250;</button>
        </>
      )}
    </div>
    {pageCount > 1 && (
      <div className="cat-carousel-dots">
        {Array.from({ length: pageCount }).map((_, i) => (
          <button
            key={i}
            className={`cat-carousel-dot${i === activePage ? ' active' : ''}`}
            onClick={() => goToPage(i)}
            aria-label={`Go to page ${i + 1}`}
          />
        ))}
      </div>
    )}
    </div>
  );
}

// One "Shop by Category" homepage row: header (icon + label + View all) plus
// its CategoryCarousel. A thin wrapper so App.js's render body stays a plain
// .map() over categoryRows instead of repeating this markup per category.
function CategoryRow({ label, Icon, items, onProductClick, onViewAll }) {
  if (!items.length) return null;
  return (
    <section className="cat-row">
      <div className="cat-row-inner">
        <div className="cat-row-header">
          <div className="cat-row-title-group">
            {Icon && <span className="cat-row-icon"><Icon size={17} /></span>}
            <h2 className="cat-row-title">{label}</h2>
          </div>
          {onViewAll && (
            <button className="cat-row-viewall" onClick={onViewAll}>
              View all <IconArrowRight size={12} />
            </button>
          )}
        </div>
        <CategoryCarousel items={items} onProductClick={onProductClick} />
      </div>
    </section>
  );
}

// ── Checkout chrome ────────────────────────────────────────────────────────
// A fixed reassurance strip above the checkout header. Cash on delivery is the
// only payment method this store offers, so the third item is a statement of
// how it works rather than a promise: the point of the bar is that a shopper
// deciding whether to commit can see the answer without scrolling.
//
// Navy in both themes (it sits above the header, which is white in light
// mode), so its text uses --color-on-navy* rather than --text-*, which flips.
function CheckoutTrustBar({ t }) {
  return (
    <div className="checkout-trustbar">
      <div className="checkout-trustbar-inner">
        <span className="ctb-item"><IconShieldCheck size={15} /> {t('checkout.trustSecure')}</span>
        <span className="ctb-item"><IconShield size={15} /> {t('checkout.trustBuyerProtection')}</span>
        <span className="ctb-item"><IconDollarSign size={15} /> {t('checkout.trustCod')}</span>
      </div>
    </div>
  );
}

// Three-step progress rail. `current` is 1, 2 or 3; anything before it renders
// as completed with a check rather than its number, which is what makes the
// rail readable at a glance instead of asking the shopper to compare numbers.
function CheckoutSteps({ current, t }) {
  const steps = [
    { n: 1, label: t('checkout.stepAddress') },
    { n: 2, label: t('checkout.orderSummary') },
    { n: 3, label: t('checkout.stepPayment') },
  ];
  return (
    <div className="checkout-steps" role="list" aria-label={t('checkout.stepPayment')}>
      {steps.map((s) => {
        const state = s.n < current ? 'completed' : s.n === current ? 'active' : '';
        return (
          <div key={s.n} className={`step ${state}`.trim()} role="listitem" aria-current={s.n === current ? 'step' : undefined}>
            <div className="step-number">{s.n < current ? <IconCheck size={14} /> : s.n}</div>
            <div className="step-label">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function SubPageHeader({ title, user, darkMode, onToggleDark, onBack, backLabel = 'Back', mobileShowName, hideThemeOnMobile, hideLogoIcon, hideTheme, flush }) {
  return (
    <header className={`sub-page-header${flush ? ' sub-page-header-flush' : ''}`}>
      <div className="sub-page-header-inner">
        <div className={`sph-left${mobileShowName ? ' sph-mobile-name-mode' : ''}`}>
          {!hideLogoIcon && <span className="sph-logo-icon-wrap"><LogoIcon size={28} /></span>}
          <span className="sph-logo-name">Acy<span className="logo-store-text">Mart</span></span>
          {mobileShowName && user && (
            <span className="sph-mobile-user">
              <span className="sph-mobile-username">{user.name.split(' ')[0]}</span>
            </span>
          )}
          {title && (
            <>
              <span className="sph-sep">›</span>
              <span className="sph-title">{title}</span>
            </>
          )}
        </div>
        <div className="sph-right">
          {user && <span className="sph-user">{user.name.split(' ')[0]}</span>}
          {!hideTheme && (
            <button
              className={`sph-theme${hideThemeOnMobile ? ' sph-theme-hide-mobile' : ''}`}
              onClick={onToggleDark}
              aria-label="Toggle theme"
            >
              {darkMode ? <IconSun size={16} /> : <IconMoon size={16} />}
            </button>
          )}
          <button className="sph-back" onClick={onBack}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/>
            </svg>
            {backLabel}
          </button>
        </div>
      </div>
    </header>
  );
}

function renderAccountSettingsPage({ settings, toggleSetting, darkMode, setDarkMode, exportingData, onExportData, user, showDeleteAccount, setShowDeleteAccount, deleteAccountPassword, setDeleteAccountPassword, deleteAccountError, deleteAccountBusy, onDeleteAccount }) {
  const options = [
    { key: 'emailNotifications', title: 'Email Notifications', sub: 'Receive important updates via email.', icon: <IconMail size={20} /> },
    { key: 'orderUpdates', title: 'Order Updates', sub: 'Get notified about your orders and deliveries.', icon: <IconPackage size={20} /> },
    { key: 'promotionalEmails', title: 'Promotional Emails', sub: 'Receive offers, discounts, and promotional content.', icon: <IconTag size={20} /> },
  ];
  return (
    <main className="settings-page">
      <div className="settings-container">
        <header className="settings-page-heading"><h1>Settings</h1><p>Manage your account preferences, notifications, and privacy settings.</p></header>
        <div className="settings-grid">
          <section className="settings-panel settings-account-card">
            <div className="settings-section-head"><span className="settings-section-icon strong"><IconUser size={22} /></span><div><h2>Account Settings</h2><p>Manage how we communicate with you.</p></div></div>
            <div className="settings-options">
              {options.map(option => (
                <label className="setting-item" key={option.key}>
                  <span className="setting-icon">{option.icon}</span>
                  <span className="setting-copy"><strong>{option.title}</strong><small>{option.sub}</small></span>
                  <input type="checkbox" checked={settings[option.key]} onChange={() => toggleSetting(option.key)} />
                  <span className="setting-check" aria-hidden="true"><IconCheck size={15} /></span>
                </label>
              ))}
            </div>
          </section>
          <div className="settings-side-column">
            <section className="settings-panel settings-theme-card">
              <div className="settings-section-head"><span className="settings-section-icon"><IconSettings size={21} /></span><div><h2>Theme</h2><p>Choose your preferred appearance.</p></div></div>
              <div className="settings-theme-toggle" role="group" aria-label="Theme">
                <button className={!darkMode ? 'active' : ''} onClick={() => setDarkMode(false)}><IconSun size={19} /> Light Mode</button>
                <button className={darkMode ? 'active' : ''} onClick={() => setDarkMode(true)}><IconMoon size={19} /> Dark Mode</button>
              </div>
              <p className="settings-theme-note">Dark mode reduces eye strain in low-light environments.</p>
            </section>
            <section className="settings-panel settings-privacy-card">
              <div className="settings-section-head"><span className="settings-section-icon"><IconShield size={21} /></span><div><h2>Data &amp; Privacy</h2><p>Control your data and privacy preferences.</p></div></div>
              <div className="settings-privacy-note"><IconShieldCheck size={18} /><span>Download a copy of everything tied to your account,<br />or permanently delete it.</span></div>
              <div className="settings-privacy-actions">
                <button className="settings-download-btn" disabled={exportingData} onClick={onExportData}><IconDownload size={17} /> {exportingData ? 'Preparing…' : 'Download My Data'}</button>
                {user?.role === 'customer' && (!showDeleteAccount ? (
                  <button className="settings-delete-btn" onClick={() => setShowDeleteAccount(true)}><IconTrash size={17} /> Delete Account</button>
                ) : (
                  <div className="settings-delete-confirm">
                    <p>This permanently deletes your account and cannot be undone. Enter your password to confirm.</p>
                    <input type="password" placeholder="Password" value={deleteAccountPassword} onChange={(e) => setDeleteAccountPassword(e.target.value)} />
                    {deleteAccountError && <p className="settings-delete-error">{deleteAccountError}</p>}
                    <div><button className="settings-delete-danger" disabled={deleteAccountBusy} onClick={onDeleteAccount}>{deleteAccountBusy ? 'Deleting…' : 'Confirm Deletion'}</button><button className="settings-delete-cancel" disabled={deleteAccountBusy} onClick={() => { setShowDeleteAccount(false); setDeleteAccountPassword(''); }}>{'Cancel'}</button></div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
        <footer className="settings-privacy-footer"><IconShield size={16} /><span>Your privacy and security are our top priorities.</span><i /><Link to="/privacy-policy">Privacy Policy</Link></footer>
      </div>
    </main>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3.5v5c0 4.5-3.4 8.2-8 9.5-4.6-1.3-8-5-8-9.5v-5L12 3z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  );
}

function StarFilledIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.5l2.95 6.46 7.05.7-5.3 4.78 1.55 6.96L12 17.9l-6.25 3.5 1.55-6.96-5.3-4.78 7.05-.7L12 2.5z"/>
    </svg>
  );
}

function MessageSquareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 3h13v13H1z"/>
      <path d="M14 8h4l3 3v5h-7V8z"/>
      <circle cx="6" cy="18.5" r="1.8"/>
      <circle cx="17.5" cy="18.5" r="1.8"/>
    </svg>
  );
}

function normalizeCartItem(item) {
  const id = item?.id || item?.productId || null;
  if (!id) return null;
  const quantity = Number.isInteger(item?.quantity) ? item.quantity : parseInt(item?.quantity, 10);
  return {
    ...item,
    id,
    quantity: Math.min(3, Math.max(1, Number.isFinite(quantity) ? quantity : 1)),
    size: item?.size || null,
    color: item?.color || null,
  };
}

function normalizeCartItems(items) {
  return Array.isArray(items) ? items.map(normalizeCartItem).filter(Boolean) : [];
}

function AppShell({ onOpenChat }) {
  const { user, loading, logout, updateProfile, uploadAvatar } = useContext(AuthContext);
  const [bootMinElapsed, setBootMinElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setBootMinElapsed(true), 2500);
    return () => clearTimeout(timer);
  }, []);
  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = () => {
    setShowUserDropdown(false);
    setLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate('/');
      setLoggingOut(false);
    }, 900);
  };
  const [products, setProducts] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('apollostore_cart'));
      return normalizeCartItems(stored);
    } catch {
      return [];
    }
  });
  const [savedForLater, setSavedForLater] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('apollostore_saved_for_later'));
      return Array.isArray(stored) ? stored : [];
    } catch { return []; }
  });
  // Product comparison: purely local (no account needed to compare, unlike
  // the wishlist), so this lives in localStorage the same way cart does.
  const MAX_COMPARE = 4;
  const [compareIds, setCompareIds] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('acymart_compare'));
      return Array.isArray(stored) ? stored.slice(0, MAX_COMPARE) : [];
    } catch { return []; }
  });
  const [showCompareModal, setShowCompareModal] = useState(false);
  useEffect(() => {
    localStorage.setItem('acymart_compare', JSON.stringify(compareIds));
  }, [compareIds]);
  const toggleCompare = (productId) => {
    setCompareIds((prev) => {
      if (prev.includes(productId)) return prev.filter((id) => id !== productId);
      if (prev.length >= MAX_COMPARE) {
        pushToast(`You can compare up to ${MAX_COMPARE} products at a time.`, 'info');
        return prev;
      }
      return [...prev, productId];
    });
  };
  const [wishlistCount, setWishlistCount] = useState(0);
  const [rewards, setRewards] = useState({ points: 0, dollarValue: 0, history: [] });
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0, history: [] });
  const [recommendations, setRecommendations] = useState(null); // { topCategory, deals[], recommendations[] }
  const [walletAmountToRedeem, setWalletAmountToRedeem] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState([0, 0]); // [min, max]; 0,0 = no filter
  const [minRating, setMinRating] = useState(0); // 0 = no filter
  const [inStockOnly, setInStockOnly] = useState(false);
  const [checkout, setCheckout] = useState({ name: '', email: '', address: '', phone: '' });
  // Merchandising rule for the Top Sales rows, from GET /api/settings/top-sales.
  // Defaults until it arrives, so the row renders on first paint either way.
  const [topSalesRules, setTopSalesRules] = useState(DEFAULT_TOP_SALES_RULES);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [successRecs, setSuccessRecs] = useState([]);
  const [orderMessage, setOrderMessage] = useState('');
  // Set when the order was blocked by the cancellation-abuse/fraud-tracing
  // guard (see backend/routes/ordersNew.js) -- styled as a hard error and
  // given longer to read than a routine validation message, since it's the
  // one case where "try again" won't help.
  const [orderRestricted, setOrderRestricted] = useState(false);
  const msgTimerRef = useRef(null);
  const showOrderMessage = (msg, { restricted = false } = {}) => {
    setOrderMessage(msg);
    setOrderRestricted(restricted);
    clearTimeout(msgTimerRef.current);
    if (msg) msgTimerRef.current = setTimeout(() => { setOrderMessage(''); setOrderRestricted(false); }, restricted ? 15000 : 4000);
  };
  const [toasts, setToasts] = useState([]);
  const recentToastRef = useRef(new Map());
  const pushToast = useCallback((msg, type = 'info', action = null) => {
    const dedupeKey = `${type}:${msg}`;
    const now = Date.now();
    const lastShownAt = recentToastRef.current.get(dedupeKey) || 0;
    if (now - lastShownAt < 1400) return;
    recentToastRef.current.set(dedupeKey, now);
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, type, action }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), action ? 5000 : 3200);
  }, []);

  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null); // { discount, code, type, value }
  const [couponError, setCouponError] = useState('');
  const [deliveryType, setDeliveryType] = useState('standard'); // 'standard' | 'express'
  const [paymentMethod, setPaymentMethod] = useState('cod');    // 'cod' | 'online'

  // Which online methods the SERVER can actually take money through, asked at
  // runtime rather than hardcoded.
  //
  // This used to be `const ONLINE_PAYMENT_ENABLED = false`, which was honest
  // when no gateway existed but meant turning payments on required editing and
  // redeploying the frontend. The backend knows whether a provider has
  // credentials (see backend/utils/payments), so it is the only thing that can
  // answer this without the two drifting apart -- and the failure mode of
  // drifting is the worst kind: a checkout that offers to charge a card
  // through a provider that is not configured.
  //
  // Empty until the fetch resolves, so the chooser cannot flash into view and
  // then vanish on a store with no provider set up.
  const [onlineMethods, setOnlineMethods] = useState([]);
  const ONLINE_PAYMENT_ENABLED = onlineMethods.length > 0;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/payments/methods`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.enabled) setOnlineMethods(d.methods || []); })
      .catch(() => { /* offline, or an older backend -- cash on delivery still works */ });
    return () => { cancelled = true; };
  }, []);

  // Set once an order has been placed and is waiting on the customer to approve
  // the prompt on their handset: { orderId, status, message }. Drives the
  // waiting UI and the status poll below.
  const [pendingPayment, setPendingPayment] = useState(null);
  const [momoPhone, setMomoPhone] = useState('');

  // Polls until the payment settles.
  //
  // The server is the only thing that can say an order is paid -- it asks the
  // provider over its own authenticated connection (see
  // backend/routes/payments.js). This poll just reflects that answer; it never
  // decides anything, which is why it is safe for it to be a plain interval
  // with no signature checking of its own.
  useEffect(() => {
    if (!pendingPayment || pendingPayment.status !== 'pending') return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/payments/${pendingPayment.orderId}/status`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || data.status === 'pending') return;
        setPendingPayment((p) => (p ? { ...p, status: data.status } : p));
        if (data.status === 'paid') pushToast('Payment received. Thank you!', 'success');
        else if (data.status === 'failed') pushToast('That payment did not go through. You can try again.', 'error');
      } catch { /* transient -- the next tick retries */ }
    };

    const id = setInterval(tick, 3000);
    // Give the customer time to find their phone and key in a PIN before
    // giving up. Past this the order simply stays unpaid and reachable from
    // order history, rather than the page spinning forever.
    const stop = setTimeout(() => setPendingPayment((p) => (p && p.status === 'pending' ? { ...p, status: 'timeout' } : p)), 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); clearTimeout(stop); };
  }, [pendingPayment, pushToast]);

  // Starts the charge for an order that has already been created.
  //
  // Deliberately after order creation, not before it: the order is the record
  // of what was bought and at what price, and it is what the server reads the
  // amount from. Charging first would mean taking money against a basket that
  // does not exist yet.
  const startOnlinePayment = async (orderId, providerId) => {
    setPendingPayment({ orderId, status: 'starting' });
    try {
      const res = await fetch(`${API_BASE_URL}/api/payments/${orderId}/charge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider: providerId, phone: momoPhone || checkout.phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPendingPayment({ orderId, status: 'failed' });
        pushToast(data.error || 'Could not start the payment.', 'error');
        return;
      }
      setPendingPayment({ orderId, status: 'pending', message: data.message });
    } catch {
      setPendingPayment({ orderId, status: 'failed' });
      pushToast('Could not reach the payment service.', 'error');
    }
  };
  const [orderNotes, setOrderNotes] = useState('');
  const EXPRESS_FEE = 9.99;
  const deliveryFee = deliveryType === 'express' ? EXPRESS_FEE : 0;
  const navigate = useNavigate();
  const location = useLocation();
  const scrollPositionsRef = useRef({});
  const prevPathRef = useRef(location.pathname);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [listTransitioning, setListTransitioning] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [wishlistPendingIds, setWishlistPendingIds] = useState({});
  const overlayHadOpenRef = useRef(false);
  const overlayReturnFocusRef = useRef(null);
  const scrollLockYRef = useRef(0);
  const authModalRef = useRef(null);
  const catSheetRef = useRef(null);
  const catSheetDragRef = useRef({ startY: 0, dragging: false });

  // Native-shell wiring: no-ops on the web, active only inside the
  // Capacitor-wrapped iOS/Android app (see src/nativeBridge.js).
  useHardwareBackButton(navigate, location);
  useDeepLinks(navigate);
  useEffect(() => { setupNativeChrome(); }, []);

  // Ask for notification permission once an order exists, never at launch.
  //
  // Android 13+ shows a system dialog for this, and one fired on first launch
  // -- before the person has anything to be notified about -- is the reliable
  // way to earn a permanent denial that no later prompt can undo. Just after
  // checkout, "tell me when it ships" is something they actually want.
  //
  // A device that already granted permission re-registers silently here with
  // no dialog, which is also how a rotated FCM token gets refreshed: on the
  // customer's next order rather than on a schedule.
  usePushRegistration(Boolean(placedOrder));

  // Long-press on the header logo opens the API connection screen. Gated on
  // canOverrideApiBase(), so on the web the press does nothing and the logo
  // stays an ordinary home button.
  const [showDevApi, setShowDevApi] = useState(false);
  const logoPressTimer = useRef(null);
  const logoLongPressRef = useRef(false);

  const beginLogoPress = useCallback(() => {
    if (!canOverrideApiBase()) return;
    logoLongPressRef.current = false;
    logoPressTimer.current = setTimeout(() => {
      logoLongPressRef.current = true;
      setShowDevApi(true);
    }, 800);
  }, []);

  const endLogoPress = useCallback(() => {
    if (logoPressTimer.current) {
      clearTimeout(logoPressTimer.current);
      logoPressTimer.current = null;
    }
  }, []);

  // A timer still pending when the header unmounts would fire into a dead
  // component and pop the dialog open on whatever rendered next.
  useEffect(() => () => { if (logoPressTimer.current) clearTimeout(logoPressTimer.current); }, []);

  // Arabic renders right-to-left -- everything else (English, Swahili,
  // French) is left-to-right. Set on <html> rather than a wrapper div so
  // native form controls, scrollbars, and third-party widgets (Leaflet maps)
  // all pick it up consistently, not just this component's own markup.
  const { t, i18n: i18nInstance } = useTranslation();
  useEffect(() => {
    const isRtl = RTL_LANGUAGES.includes(i18nInstance.language);
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18nInstance.language;
  }, [i18nInstance.language]);

  const [pageTransitioning, setPageTransitioning] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setIsReducedMotion(Boolean(media.matches));
    sync();
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    setPageTransitioning(true);
    const t = setTimeout(() => setPageTransitioning(false), isReducedMotion ? 0 : 240);
    return () => clearTimeout(t);
  }, [location.pathname, isReducedMotion]);

  useEffect(() => {
    const previousPath = prevPathRef.current;
    const nextPath = location.pathname;

    scrollPositionsRef.current[previousPath] = window.scrollY;

    const returningToListing = nextPath === '/' && previousPath.startsWith('/product/');
    if (returningToListing) {
      const restoreY = scrollPositionsRef.current['/'] ?? 0;
      requestAnimationFrame(() => {
        window.scrollTo({ top: restoreY, left: 0, behavior: 'auto' });
      });
    } else if (nextPath !== previousPath) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    prevPathRef.current = nextPath;
  }, [location.pathname]);

  useEffect(() => () => {
    document.body.classList.remove('overlay-open');
  }, []);
  const shellClassName = `app-shell${pageTransitioning ? ' page-fade-in' : ''}`;

  const PAGE_URLS = {
    home: '/',
    orders: '/account/orders',
    dispute: '/account/disputes',
    wishlist: '/account/wishlist',
    address: '/account/addresses',
    settings: '/account/settings',
    profile: '/account/profile',
    dashboard: '/account/dashboard',
    notifications: '/account/notifications',
    cart: '/cart',
    'seller-portal': '/seller',
    admin: '/admin',
    'super-admin': '/super-admin',
    'seller-registration': '/become-a-seller',
    'track-application': '/track-application',
    'track-order': '/track',
    'delivery-confirm': '/delivery',
    privacy: '/privacy-policy',
    terms: '/terms-conditions',
    cookies: '/cookie-policy',
    returns: '/returns-refunds',
    shipping: '/shipping-policy',
    careers: '/careers',
    about: '/about',
    'commission-plan': '/commission-plan',
    'seller-guidelines': '/seller-guidelines',
    'news-updates': '/news-updates',
    brands: '/brands',
    contact: '/contact',
    faq: '/faq',
    helpCenter: '/help-center',
    sellerFaq: '/seller-faqs',
    'get-app': '/app',
  };
  const pageToUrl = (pageName, productId) => {
    if (pageName === 'product') return `/product/${productId}`;
    return PAGE_URLS[pageName] || '/';
  };

  const page = (() => {
    const p = location.pathname;
    if (p.startsWith('/product/')) return 'product';
    if (p.startsWith('/store/')) return 'seller-store';
    if (p.startsWith('/wishlist/shared/')) return 'shared-wishlist';
    if (p === '/account/orders') return 'orders';
    if (p === '/account/disputes') return 'dispute';
    if (p === '/account/wishlist') return 'wishlist';
    if (p === '/account/addresses') return 'address';
    if (p === '/account/settings') return 'settings';
    if (p === '/account/profile') return 'profile';
    if (p === '/account/dashboard') return 'dashboard';
    if (p === '/account/notifications') return 'notifications';
    if (p === '/deals') return 'deals';
    if (p.startsWith('/category/')) return 'category';
    // Tokenised, so it is matched by prefix and carries no entry in PAGE_URLS
    // -- same shape as /wishlist/shared/:token above. There is no static URL
    // for "the recovery page"; there is only a link from one specific email.
    if (p.startsWith('/cart/recover/')) return 'cart-recover';
    // Landed on from the unsubscribe link in a cart reminder. Like the
    // recovery page above it is reached only from an email, so it carries no
    // PAGE_URLS entry -- but it is a fixed path, because the token that
    // identifies the account travels in the query string instead.
    if (p === '/cart-reminders/off') return 'cart-reminders-off';
    if (p === '/cart') return 'cart';
    if (p === '/seller') return 'seller-portal';
    if (p === '/portal-access') return 'admin-login';
    if (p === '/admin') return 'admin';
    if (p === '/super-admin') return 'super-admin';
    if (p === '/become-a-seller') return 'seller-registration';
    if (p.startsWith('/track-application')) return 'track-application';
    if (p === '/track' || p.startsWith('/track/')) return 'track-order';
    if (p === '/delivery' || p.startsWith('/delivery/')) return 'delivery-confirm';
    if (p === '/privacy-policy') return 'privacy';
    if (p === '/terms-conditions') return 'terms';
    if (p === '/cookie-policy') return 'cookies';
    if (p === '/returns-refunds') return 'returns';
    if (p === '/shipping-policy') return 'shipping';
    if (p === '/careers') return 'careers';
    if (p === '/about') return 'about';
    if (p === '/commission-plan') return 'commission-plan';
    if (p === '/seller-guidelines') return 'seller-guidelines';
    if (p === '/news-updates') return 'news-updates';
    if (p === '/brands') return 'brands';
    if (p === '/contact') return 'contact';
    if (p === '/faq') return 'faq';
    if (p === '/help-center') return 'helpCenter';
    if (p === '/seller-faqs') return 'sellerFaq';
    if (p === '/app') return 'get-app';
    return 'home';
  })();

  const selectedProductId = page === 'product'
    ? location.pathname.replace('/product/', '')
    : null;
  const selectedSellerId = page === 'seller-store'
    ? location.pathname.replace('/store/', '')
    : null;
  const sharedWishlistToken = page === 'shared-wishlist'
    ? location.pathname.replace('/wishlist/shared/', '')
    : null;
  const cartRecoveryToken = page === 'cart-recover'
    ? location.pathname.replace('/cart/recover/', '')
    : null;
  const reminderOptOut = page === 'cart-reminders-off'
    ? {
      token: new URLSearchParams(location.search).get('t') || '',
      // The backend says so when it could not verify the link, rather than
      // this page guessing from an empty token.
      invalid: new URLSearchParams(location.search).get('state') === 'invalid',
    }
    : null;
  // Derived from the URL rather than its own toggle state, so "on the deals
  // page" and "showing deals" can never desync -- navigating to /deals *is*
  // what turns this on.
  const dealsOnly = page === 'deals';
  // The category page's own identity (used for its banner carousel + curated
  // rows below), independent of `selectedCategory` -- that stays the grid's
  // own filter state (still adjustable via the sidebar without leaving the
  // page), synced to match on arrival by the effect further down.
  const categorySlug = page === 'category'
    ? decodeURIComponent(location.pathname.replace('/category/', ''))
    : null;
  // Points the grid's own filter at this category the moment the page is
  // opened (including a fresh/bookmarked load) -- after that the sidebar's
  // category checkboxes can still be used to browse a different category's
  // products without leaving this page; the curated rows above stay locked
  // to categorySlug (the URL) either way.
  useEffect(() => {
    if (page === 'category' && categorySlug) setSelectedCategory(categorySlug);
  }, [page, categorySlug]);

  // ── SEO ──────────────────────────────────────────────────────────────────
  // Every route's <head> state in one place, driven by the same `page` value
  // the renderer switches on. Without this, client-side navigation left the
  // title, description and canonical frozen at index.html's generic defaults
  // on every page except the PDP -- so every URL on the site looked like
  // duplicate content with the identical title to a crawler.
  //
  // 'product' and 'seller-store' are deliberately excluded: those pages own
  // their own SEO because the real title/description/image only exist once
  // the fetch resolves, and a generic pass here would overwrite the specific
  // one whenever this effect re-ran.
  useEffect(() => {
    if (page === 'product' || page === 'seller-store') return;

    // Canonical uses the pathname only. Filters, sort order and the search
    // box are component state rather than query params today, but pathname-
    // only is also what keeps any tracking param (?utm_source=...) an
    // external link arrives with from splitting a page's ranking signals
    // across near-duplicate URLs.
    const path = location.pathname;

    if (page === 'category' && categorySlug) {
      const { title, description } = categorySeo(categorySlug);
      applySeo({
        title,
        description,
        path,
        jsonLd: {
          '@context': 'https://schema.org',
          '@graph': [
            breadcrumbLd([
              { name: 'Home', path: '/' },
              { name: categorySlug, path },
            ]),
          ].filter(Boolean),
        },
      });
      return;
    }

    // Unrecognised paths fall through to `page === 'home'` above and render
    // the storefront with a 200, so a typo'd or long-dead URL is a pixel-
    // perfect duplicate of the homepage. Self-canonicalising those would tell
    // Google to index unlimited copies of the home page under junk URLs, so
    // they're marked noindex instead -- the closest thing to a 404 available
    // without changing the routing behaviour itself.
    const isRealHome = page !== 'home' || path === '/';
    const meta = PAGE_SEO[page];
    if (!meta || !isRealHome) {
      applySeo({ title: 'Page Not Found', path, noindex: true });
      return;
    }
    applySeo({ ...meta, path });
  }, [page, categorySlug, location.pathname]);

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [showAuthModal, setShowAuthModal] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showMobileCatSheet, setShowMobileCatSheet] = useState(false);
  const [showMobileAccountSheet, setShowMobileAccountSheet] = useState(false);

  useEffect(() => {
    const isOverlayOpen = Boolean(showAuthModal || showMobileCatSheet || showMobileAccountSheet);
    if (isOverlayOpen && !overlayHadOpenRef.current) {
      overlayReturnFocusRef.current = document.activeElement;
      // `overflow:hidden` on body alone doesn't reliably stop touch-scroll
      // bleed-through to background content on iOS Safari. Pinning body to
      // `position:fixed` at its current scroll offset is the standard fix --
      // it removes body from the normal scroll flow entirely rather than
      // relying on overflow/touch-action being honored underneath a fixed
      // overlay.
      scrollLockYRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollLockYRef.current}px`;
      document.body.style.width = '100%';
      document.body.classList.add('overlay-open');
    }
    if (!isOverlayOpen && overlayHadOpenRef.current) {
      document.body.classList.remove('overlay-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollLockYRef.current);
      if (overlayReturnFocusRef.current && typeof overlayReturnFocusRef.current.focus === 'function') {
        overlayReturnFocusRef.current.focus();
      }
      overlayReturnFocusRef.current = null;
    }
    overlayHadOpenRef.current = isOverlayOpen;
  }, [showAuthModal, showMobileCatSheet, showMobileAccountSheet]);

  // Drag-to-dismiss for the mobile category sheet: tracks the pointer directly
  // via the DOM node (not React state) so the sheet follows the finger at 60fps
  // without a re-render per pixel. Only downward drags move it; releasing past
  // a distance/velocity threshold dismisses, otherwise it snaps back.
  const handleCatSheetDragStart = (e) => {
    catSheetDragRef.current = { startY: e.touches ? e.touches[0].clientY : e.clientY, dragging: true, startTime: Date.now() };
    if (catSheetRef.current) catSheetRef.current.style.transition = 'none';
  };
  const handleCatSheetDragMove = (e) => {
    if (!catSheetDragRef.current.dragging) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = Math.max(0, y - catSheetDragRef.current.startY);
    catSheetDragRef.current.lastDelta = delta;
    if (catSheetRef.current) catSheetRef.current.style.transform = `translateY(${delta}px)`;
  };
  const handleCatSheetDragEnd = () => {
    if (!catSheetDragRef.current.dragging) return;
    const delta = catSheetDragRef.current.lastDelta || 0;
    const elapsed = Date.now() - (catSheetDragRef.current.startTime || Date.now());
    const velocity = delta / Math.max(elapsed, 1);
    catSheetDragRef.current.dragging = false;
    if (catSheetRef.current) catSheetRef.current.style.transition = '';
    if (delta > 120 || velocity > 0.5) {
      setShowMobileCatSheet(false);
    } else if (catSheetRef.current) {
      catSheetRef.current.style.transform = '';
    }
  };

  const handleOpenAccountSheet = () => {
    if (!user) { setShowAuthModal('login'); return; }
    setShowMobileAccountSheet(true);
  };
  // Rendered from every page branch below (not just the fallback/home one) --
  // handleOpenAccountSheet can trigger it from any page, so it must be
  // reachable from any page's JSX, not just whichever branch happens to render.
  useFocusTrap(authModalRef, Boolean(showAuthModal));
  useFocusTrap(catSheetRef, showMobileCatSheet);
  const renderAuthModal = () => showAuthModal && (
    <div
      className="modal-overlay"
      onClick={() => setShowAuthModal(null)}
      onKeyDown={e => { if (e.key === 'Escape') setShowAuthModal(null); }}
    >
      <div
        ref={authModalRef}
        tabIndex={-1}
        className="modal-content modal-content-auth"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={showAuthModal === 'register' ? 'Create Account' : 'Sign In'}
      >
        <button className="modal-close" onClick={() => setShowAuthModal(null)} aria-label="Close dialog">×</button>
        <div className="modal-auth-tabs">
          <button
            className={`modal-auth-tab${showAuthModal !== 'register' ? ' active' : ''}`}
            onClick={() => setShowAuthModal('login')}
          >Sign In</button>
          <button
            className={`modal-auth-tab${showAuthModal === 'register' ? ' active' : ''}`}
            onClick={() => setShowAuthModal('register')}
          >Create Account</button>
        </div>
        {showAuthModal === 'register' ? (
          <RegisterPage onSuccess={() => { setShowAuthModal(null); }} />
        ) : (
          <LoginPage onSuccess={(loggedInUser) => {
            setShowAuthModal(null);
            if (loggedInUser?.role === 'admin') {
              navigate('/admin');
            } else if (loggedInUser?.role === 'seller' || loggedInUser?.role === 'seller_suspended') {
              navigate('/seller');
            } else if (loggedInUser?.role === 'delivery') {
              navigate('/delivery');
            }
          }} />
        )}
      </div>
    </div>
  );
  // Same reasoning as renderAuthModal above -- toasts are pushed from
  // handlers reachable on every page, so the stack must be renderable from
  // every page branch, not just the fallback/home one.
  const renderToasts = () => toasts.length > 0 && (
    <div className="toast-stack" role="status" aria-live="polite" aria-atomic="true">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span className="toast-icon">
            {t.type === 'success' ? <IconCheck size={13} /> : t.type === 'error' ? '!' : t.type === 'warning' ? '!' : 'i'}
          </span>
          <span>{t.msg}</span>
          {t.action && (
            <button
              type="button"
              className="toast-undo"
              onClick={() => {
                t.action.onClick();
                setToasts((current) => current.filter((item) => item.id !== t.id));
              }}
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            className="toast-dismiss"
            onClick={() => setToasts((current) => current.filter((item) => item.id !== t.id))}
            aria-label="Dismiss notification"
          >
            <IconX size={12} />
          </button>
        </div>
      ))}
    </div>
  );
  const searchInputRef = useRef(null);
  // "Still looking for these?" is meant as a short-lived nudge for a browsing
  // session, not a permanent history -- so each entry carries when it was
  // viewed and expires RECENTLY_VIEWED_TTL_MS after that. Stored as
  // {id, viewedAt} pairs (older bare-id-array entries are dropped on read,
  // not migrated, since they carry no timestamp to judge freshness by).
  const RECENTLY_VIEWED_TTL_MS = 30 * 60 * 1000; // 30 minutes
  const loadRecentlyViewed = () => {
    let raw;
    try { raw = JSON.parse(localStorage.getItem('apollostore_recently_viewed') || '[]'); } catch { return []; }
    if (!Array.isArray(raw)) return [];
    const cutoff = Date.now() - RECENTLY_VIEWED_TTL_MS;
    return raw.filter(e => e && typeof e === 'object' && new Date(e.viewedAt).getTime() > cutoff);
  };
  const [recentlyViewedEntries, setRecentlyViewedEntries] = useState(loadRecentlyViewed);
  const recentlyViewedIds = useMemo(() => recentlyViewedEntries.map(e => e.id), [recentlyViewedEntries]);

  const saveRecentlyViewed = (entries) => {
    setRecentlyViewedEntries(entries);
    try { localStorage.setItem('apollostore_recently_viewed', JSON.stringify(entries)); } catch {}
  };

  // Re-checks expiry on every visit/mount (e.g. the tab was left open, or the
  // site was reopened later) -- not just when a new item is viewed -- so a
  // stale entry actually disappears next time the customer is back, per the
  // "only show for a few minutes when the customer revisits" requirement.
  useEffect(() => { saveRecentlyViewed(loadRecentlyViewed()); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page !== 'product' || !selectedProductId) return;
    const fresh = loadRecentlyViewed().filter(e => e.id !== selectedProductId);
    saveRecentlyViewed([{ id: selectedProductId, viewedAt: new Date().toISOString() }, ...fresh].slice(0, 8));

    // Same moment, different horizon. The list above is a 30-minute memory of
    // what you were just looking at; this is a slow tally of which categories
    // you keep returning to, which is what "Great finds for you" ranks on.
    const viewed = products.find(p => String(p.id) === String(selectedProductId));
    if (viewed?.category) recordCategoryView(viewed.category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedProductId, products]);

  // Buying something is a much stronger signal than merely having viewed it
  // -- there's nothing left to nudge them back for, so it comes out of the
  // list immediately rather than waiting out the TTL.
  const removeFromRecentlyViewed = (ids) => {
    if (!ids?.length) return;
    const idSet = new Set(ids.map(String));
    saveRecentlyViewed(loadRecentlyViewed().filter(e => !idSet.has(String(e.id))));
  };
  const [checkoutStep, setCheckoutStep] = useState(null); // null | 'auth' | 1 | 2 | 3 | 'edit-address'

  // The green confirmation strip on the order-success page behaves like a
  // toast that happens to live inline: it says its piece and then gets out of
  // the way, rather than sitting there permanently restating what the heading
  // above it already says. It collapses in place rather than floating, so
  // nothing below it jumps when it goes.
  const [successNoticeHidden, setSuccessNoticeHidden] = useState(false);
  useEffect(() => {
    if (checkoutStep !== 'success') return undefined;
    setSuccessNoticeHidden(false);
    const t = setTimeout(() => setSuccessNoticeHidden(true), 5000);
    return () => clearTimeout(t);
  }, [checkoutStep]);
  // Order Summary shows the first cart line and collapses the rest behind a
  // "View items" toggle, so Price Details and the Continue button stay above
  // the fold however large the cart is.
  const [summaryItemsExpanded, setSummaryItemsExpanded] = useState(false);
  const [checkoutAuthMode, setCheckoutAuthMode] = useState('login');
  const [zoneShipping, setZoneShipping] = useState(null); // null | 'loading' | { breakdown, total }
  const [zoneCheck, setZoneCheck] = useState(null); // null (no coords / not applicable) | 'loading' | { withinZone, distanceKm, radiusKm, hubConfigured }
  const [savedAddress, setSavedAddress] = useState(() => {
    try {
      const saved = localStorage.getItem('userAddress');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [addressSavedMessage, setAddressSavedMessage] = useState('');

  // Map pin for the two address forms a *guest* sees (checkout step 1, and the
  // My Addresses page). A signed-in customer gets <AddressBook>, which has had
  // its own picker since the address book was built -- guests had no way to
  // drop a pin at all, so a guest order reached the courier with street text
  // and nothing else.
  //
  // That matters more here than the "(optional)" label suggests. This store
  // defaults its map to Juba, and the address form already puts landmark and
  // directions front-and-centre precisely because street addresses are not
  // reliable enough to navigate by. A pin is the one field that always
  // resolves to a real place, and it is what feeds the live delivery map.
  const [addressPin, setAddressPin] = useState(() => {
    const coords = savedAddress?.location?.coordinates;
    return Array.isArray(coords) ? { lat: coords[1], lng: coords[0], suggestedArea: savedAddress.area || null } : null;
  });
  const [showAddressMap, setShowAddressMap] = useState(false);

  // Shape matches what AddressBook stores, so both paths write one format:
  // GeoJSON Point, which is [lng, lat] -- the reverse of how every UI here
  // says it. The reverse-geocoded area is only a default; it never overwrites
  // an area the customer typed themselves.
  const addressPinFields = (typedArea) => (addressPin ? {
    location: { type: 'Point', coordinates: [addressPin.lng, addressPin.lat] },
    area: typedArea || addressPin.suggestedArea || undefined,
  } : {});

  // A dropped pin answers most of the address form on its own, so the form
  // stops insisting on answers the map already has: the customer gives a name,
  // an email and a phone number, and the pin supplies where to deliver.
  //
  // The fields stay required when there is no pin -- someone who never opens
  // the map is asked for exactly what they were asked for before.
  const hasPin = Boolean(addressPin);

  // Fills a field the customer left blank, and never overwrites one they
  // typed: a reverse lookup is a decent guess, and a person who has bothered
  // to write their own street name knows the place better than Nominatim does.
  const pinValue = (key, typed) => {
    if (typed && typed.trim()) return typed.trim();
    return addressPin?.suggestion?.[key] || '';
  };

  // What the server stores as `customer.address`. It requires at least three
  // characters, so a pin in an unmapped area -- coordinates but no street --
  // still has to produce something a human can read. The area name, the city,
  // or failing both, the coordinates themselves.
  const composedAddressFrom = (parts) => {
    const written = parts.filter(Boolean).join(', ').trim();
    if (written.length >= 3) return written;
    if (addressPin?.suggestedArea) return addressPin.suggestedArea;
    if (addressPin) return `Pinned location ${addressPin.lat.toFixed(5)}, ${addressPin.lng.toFixed(5)}`;
    return written;
  };

  // Collapsed by default: a tile map is heavy to mount and most of the form is
  // above it. The summary line does the persuading instead -- once a pin is
  // set it reads back the area name, so the customer can see it took without
  // reopening the map.
  const renderAddressMapPicker = () => (
    <div className="addr-pin-block">
      {!showAddressMap ? (
        <button type="button" className={`addr-pin-toggle${addressPin ? ' addr-pin-toggle-set' : ''}`} onClick={() => setShowAddressMap(true)}>
          <span className="addr-pin-toggle-icon" aria-hidden="true"><IconMapPin size={18} /></span>
          <span className="addr-pin-toggle-text">
            <strong>{addressPin ? 'Location pinned' : 'Pin your location on the map'}</strong>
            <small>
              {addressPin
                ? `${addressPin.suggestedArea ? `${addressPin.suggestedArea} · ` : ''}${addressPin.lat.toFixed(5)}, ${addressPin.lng.toFixed(5)} — tap to adjust`
                : 'Helps the courier find you when the street address is hard to follow'}
            </small>
          </span>
        </button>
      ) : (
        <>
          <label className="addr-pin-label">Exact delivery location</label>
          <Suspense fallback={<div className="dmp-map-box dmp-map-loading">Loading map...</div>}>
            <DeliveryMapPicker
              initialLocation={addressPin ? { lat: addressPin.lat, lng: addressPin.lng } : null}
              // Replaced wholesale rather than merged: the picker reports a new
              // pin immediately with suggestedArea null, then again ~600ms later
              // once the reverse lookup returns. Carrying the previous area
              // forward would leave the *old* neighbourhood attached to the new
              // pin whenever that lookup fails.
              onChange={({ lat, lng, suggestedArea, suggestion }) => setAddressPin({ lat, lng, suggestedArea, suggestion })}
            />
          </Suspense>
          <button type="button" className="addr-pin-done" onClick={() => setShowAddressMap(false)}>
            {addressPin ? 'Done' : 'Close map'}
          </button>
        </>
      )}
    </div>
  );
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('userSettings');
      return saved
        ? JSON.parse(saved)
        : { emailNotifications: true, orderUpdates: true, promotionalEmails: false };
    } catch {
      return { emailNotifications: true, orderUpdates: true, promotionalEmails: false };
    }
  });
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // ── Seller applications ────────────────────────────────────────────
  // Backed by the sellerApplications collection (see backend/routes/sellerApplications.js
  // and backend/routes/admin.js) -- a sellerStores record, and therefore role:'seller',
  // is only ever created once an admin verifies the application, so this is the
  // complete check (no localStorage fallback needed).
  const [trackingId, setTrackingId] = useState('');
  const isSeller = user?.role === 'seller' || user?.role === 'seller_suspended';

  const navigateTo = (pageName, productId) => navigate(pageToUrl(pageName, productId));

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const onScroll = () => {
      document.body.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const update = () =>
      document.documentElement.style.setProperty('--topbar-h', `${header.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    async function loadProducts() {
      try {
        setPageLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/products`);
        if (!response.ok) throw new Error('Unable to load products');
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setPageLoading(false);
      }
    }
    loadProducts();
  }, []);

  // Store-configured tax rate (percent, e.g. 8.25) -- defaults to 0 until
  // loaded so the cart/checkout previews never show a made-up number before
  // this resolves. The actual charge is still computed authoritatively on
  // the server at order-creation time regardless of what this shows.
  const [taxRatePct, setTaxRatePct] = useState(0);
  useEffect(() => {
    // Merchandising rule for the Top Sales rows. Public, because the homepage
    // renders it for visitors who are not signed in. A failure here is not
    // worth surfacing: topSales.js falls back to the defaults, which is the
    // row the site had before this was configurable.
    fetch(`${API_BASE_URL}/api/settings/top-sales`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTopSalesRules(d); })
      .catch(() => { /* defaults stand */ });

    fetch(`${API_BASE_URL}/api/settings/tax-rate`)
      .then((r) => r.json())
      .then((d) => setTaxRatePct(d.taxRate || 0))
      .catch(() => {});
  }, []);

  // Same fetch-once pattern as the tax rate above -- updates the shared
  // currency.js module (imported by every page that formats money) rather
  // than local component state, since those pages aren't all descendants
  // this component can prop-drill into.
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings/currency`)
      .then((r) => r.json())
      .then((d) => setCurrency(d))
      .catch(() => {});
  }, []);

  // See apiInterceptor.js -- fires once a stale/invalid token gets a real
  // 401 back from any authenticated request, anywhere in the app. Surfaces
  // it to the user (rather than leaving whatever widget just failed looking
  // silently broken) and prompts a fresh login.
  useEffect(() => {
    const onExpired = () => {
      pushToast('Your session has expired. Please log in again.', 'info');
      navigate('/');
      setShowAuthModal('login');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [pushToast, navigate]);

  // Push notification subscription. Runs after a logged-in user lands on the
  // storefront: fetches the VAPID public key, asks browser permission (only
  // shows the browser prompt once — subsequent calls are instant if already
  // granted), and registers the subscription with the backend so the server
  // can push order and status notifications to this device.
  useEffect(() => {
    if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission === 'denied') return;

    (async () => {
      try {
        const keyRes = await fetch(`${API_BASE_URL}/api/notifications/vapid-public-key`);
        const { publicKey } = await keyRes.json();
        if (!publicKey) return; // push not configured server-side

        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) return; // already subscribed on this device

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: publicKey,
        });
        const { endpoint, keys } = sub.toJSON();
        await fetch(`${API_BASE_URL}/api/notifications/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ endpoint, keys }),
        });
      } catch { /* push subscription should never crash the app */ }
    })();
  }, [user]);

  // Anonymous storefront traffic, for the admin "Traffic" tab and the
  // Operations funnel. No PII -- visitorId is a random ID kept in this
  // browser's localStorage, never tied to an account, and the device category
  // is derived here so the raw user-agent never reaches the server.
  //
  // Two things are reported and the server keeps them apart (see
  // backend/db/siteVisitsRepo.js): the first storefront page of a session is
  // the `session` -- the visit itself, which is all this used to send -- and
  // every further page that session opens is a `pageview`.
  //
  // The second half is new, and it is why the funnel had nothing to show. This
  // is one React app that changes the URL without reloading, so a single
  // mount-time ping recorded only where somebody landed: 671 of the first 796
  // rows were "/", every product page in the shop had fifteen between them, and
  // the funnel's product-views step could only say "not tracked".
  //
  // Never on /seller or /admin: neither is storefront traffic, and the check
  // comes before the session flag is claimed, so working in the admin does not
  // consume the visit that the same tab's later shopping should be counted as.
  useEffect(() => {
    try {
      const path = location.pathname;
      if (!path || path.startsWith('/seller') || path.startsWith('/admin')) return;

      // One row per distinct path per session. Somebody going back and forth
      // between two products should not read as a busier shop than somebody who
      // looked at each once, and it puts a ceiling on the rows one session can
      // write.
      const seenKey = `apollostore_seen_${path}`;
      if (sessionStorage.getItem(seenKey)) return;

      const first = !sessionStorage.getItem('apollostore_visit_logged');

      let visitorId = localStorage.getItem('apollostore_visitor_id');
      if (!visitorId) {
        visitorId = (crypto.randomUUID?.() ??
          `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem('apollostore_visitor_id', visitorId);
      }

      // Coarse device category only (mobile/tablet/desktop) -- derived from the
      // user-agent right here in the browser and never sent anywhere else, so
      // the raw user-agent string itself still never reaches the server.
      const ua = navigator.userAgent || '';
      const isTablet = /iPad|Tablet|Kindle|Silk/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
      const isMobile = !isTablet && /Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua);
      const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

      fetch(`${API_BASE_URL}/api/traffic/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId,
          path,
          referrer: document.referrer || null,
          deviceType,
          kind: first ? 'session' : 'pageview',
        }),
      }).catch(() => {});

      sessionStorage.setItem(seenKey, '1');
      if (first) sessionStorage.setItem('apollostore_visit_logged', '1');
    } catch { /* analytics should never crash the app */ }
  }, [location.pathname]);

  // Deals banner: what the announcement bar may honestly say right now.
  //
  // This used to count down to a server "flash sale" window that renewed itself
  // every 24 hours, beside "30% Off Everything" -- a sale that did not exist,
  // with a deadline that never arrived. The server now derives both figures
  // from real product sales (productsRepo.liveSaleSummary): how many products
  // are on sale, and when the soonest of those sales really ends. With nothing
  // on sale, dealCount is 0, endsAt is null and the bar makes no sale claim.
  const [deals, setDeals] = useState({ dealCount: 0, endsAt: null });
  const [dealsRemaining, setDealsRemaining] = useState(null);
  // The last deadline this clock watched expire. If the server hands the same
  // one back (a client clock running ahead of the server's), it is dropped
  // instead of being re-fetched on every one-second tick.
  const lastExpiredDealRef = useRef(null);

  const loadDeals = useCallback(() => {
    fetch(`${API_BASE_URL}/api/banners/flash-sale`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const endsAt = d.endsAt && d.endsAt !== lastExpiredDealRef.current ? d.endsAt : null;
        setDeals({ dealCount: Number(d.dealCount) || 0, endsAt });
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  useEffect(() => {
    if (!deals.endsAt) { setDealsRemaining(null); return undefined; }
    const tick = () => {
      const ms = new Date(deals.endsAt).getTime() - Date.now();
      if (ms <= 0) {
        // That sale genuinely ended. Ask once for the next real one.
        lastExpiredDealRef.current = deals.endsAt;
        setDeals((prev) => ({ ...prev, endsAt: null }));
        loadDeals();
        return;
      }
      const days = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      const clock = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      setDealsRemaining(days > 0 ? `${days}d ${clock}` : clock);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deals.endsAt, loadDeals]);

  useEffect(() => {
    localStorage.setItem('userSettings', JSON.stringify(settings));
  }, [settings]);

  // Zone-based shipping lookup: runs when entering checkout step 2.
  // Fetches the matching delivery zone + fee for every seller in the cart
  // based on the customer's saved address. Express overrides this.
  useEffect(() => {
    if (checkoutStep !== 2 || deliveryType === 'express') { setZoneShipping(null); return; }
    const sellerIds = [...new Set(cart.filter(i => i.sellerId).map(i => i.sellerId))];
    if (!sellerIds.length) { setZoneShipping(null); return; }
    const address = [savedAddress?.addressLine1, savedAddress?.city, savedAddress?.state].filter(Boolean).join(' ');
    setZoneShipping('loading');
    Promise.all(sellerIds.map(async (sid) => {
      const item = cart.find(i => i.sellerId === sid);
      try {
        const d = await fetch(`${API_BASE_URL}/api/shipping/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sellerId: sid, address }),
        }).then(r => r.json());
        return { sellerId: sid, storeName: item?.sellerName || 'Seller', zone: d.zone, message: d.message };
      } catch {
        return { sellerId: sid, storeName: item?.sellerName || 'Seller', zone: null };
      }
    })).then(results => {
      setZoneShipping({ breakdown: results, total: results.reduce((s, r) => s + (r.zone?.fee || 0), 0) });
    });
  }, [checkoutStep, deliveryType, cart, savedAddress]);

  // Delivery-zone gate: only runs for addresses saved via the map-pin picker
  // (savedAddress.location present). Every address without coordinates -- i.e.
  // every address before this feature existed, and every guest/manual entry --
  // leaves zoneCheck at null and never fetches anything, identical to today.
  useEffect(() => {
    if (!savedAddress?.location?.coordinates) { setZoneCheck(null); return; }
    const [lng, lat] = savedAddress.location.coordinates;
    setZoneCheck('loading');
    fetch(`${API_BASE_URL}/api/delivery/check-zone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lng }),
    }).then(r => r.json()).then(setZoneCheck).catch(() => setZoneCheck(null));
  }, [savedAddress]);

  // When a logged-in user's profile loads, use their server-stored settings.
  useEffect(() => {
    if (user?.settings) {
      setSettings(user.settings);
    }
  }, [user]);

  const toggleSetting = (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    if (user) {
      fetch(`${API_BASE_URL}/api/auth/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settings: next })
      }).catch((err) => console.error('Failed to save settings:', err));
    }
  };

  // Data-rights: export a JSON dump of everything tied to this account, or
  // permanently delete (anonymize) it -- see GET/DELETE /api/auth/me[/export].
  const [exportingData, setExportingData] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountBusy, setDeleteAccountBusy] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  const handleExportData = async () => {
    if (!user) return;
    setExportingData(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me/export`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { pushToast(data.error || 'Could not export your data.', 'error'); return; }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `acymart-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      pushToast('Network error. Please try again.', 'error');
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) { setDeleteAccountError('Enter your password to confirm.'); return; }
    setDeleteAccountBusy(true);
    setDeleteAccountError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: deleteAccountPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setDeleteAccountError(data.error || 'Could not delete your account.'); return; }
      logout();
      navigate('/');
      pushToast('Your account has been deleted.', 'success');
    } catch {
      setDeleteAccountError('Network error. Please try again.');
    } finally {
      setDeleteAccountBusy(false);
    }
  };

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(products.map((product) => product.category)))],
    [products]
  );

  // Homepage "Top Sales" row -- one single carousel spanning every category,
  // not one row per category. The ranking itself lives in topSales.js, shared
  // with each category page's own "Top Sales in X" row, so the two rows cannot
  // disagree about what qualifies -- a product in one and missing from the
  // other reads as a bug rather than as a rule.
  //
  // Which products qualify is configurable from the admin settings screen
  // (GET /api/settings/top-sales); topSalesRules holds the answer, and the
  // defaults reproduce the previous behaviour until it arrives.
  const topSalesItems = useMemo(
    () => rankTopSales(products, topSalesRules),
    [products, topSalesRules]
  );

  useEffect(() => {
    setListTransitioning(true);
    const t = setTimeout(() => setListTransitioning(false), isReducedMotion ? 0 : 180);
    return () => clearTimeout(t);
  }, [searchTerm, selectedCategory, sortBy, priceRange, minRating, inStockOnly, dealsOnly, isReducedMotion]);

  const recentlyViewedProducts = useMemo(
    () => recentlyViewedIds.map(id => products.find(p => String(p.id) === String(id))).filter(Boolean).slice(0, 6),
    [recentlyViewedIds, products]
  );

  const priceMax = useMemo(
    () => products.length ? Math.ceil(Math.max(...products.map(p => p.price))) : 1000,
    [products]
  );

  const SHOP_PAGE_SIZE = 24;

  const filteredProducts = useMemo(() => {
    const [pMin, pMax] = priceRange;
    const filtered = products.filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
      const matchesPrice = (pMin === 0 && pMax === 0) || (product.price >= pMin && product.price <= pMax);
      const matchesRating = minRating === 0 || (product.avgRating || 0) >= minRating;
      const matchesStock = !inStockOnly || product.stock === null || product.stock > 0;
      // A product actively on sale (a live salePrice + not-yet-expired saleEndsAt --
      // distinct from a merely-lower originalPrice/compare-at price, which is just
      // regular catalogue pricing) lives on the dedicated /deals page, not the
      // regular catalogue -- so the two views are exact complements of each other.
      const hasSaleNow = product.salePrice && product.saleEndsAt && new Date(product.saleEndsAt) > new Date();
      const matchesDeals = dealsOnly ? hasSaleNow : !hasSaleNow;
      return matchesSearch && matchesCategory && matchesPrice && matchesRating && matchesStock && matchesDeals;
    });
    if (sortBy === 'price-asc')  return [...filtered].sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') return [...filtered].sort((a, b) => b.price - a.price);
    if (sortBy === 'rating')     return [...filtered].sort((a, b) => (b.avgRating || 0) - (a.avgRating || 0));
    if (sortBy === 'newest')     return [...filtered].sort((a, b) => new Date(b.createdAt||0) - new Date(a.createdAt||0));
    if (sortBy === 'trending')   return [...filtered].sort((a, b) => (b.sales || 0) - (a.sales || 0));
    return filtered;
  }, [products, searchTerm, selectedCategory, sortBy, priceRange, minRating, inStockOnly, dealsOnly]);

  // ── Infinite scroll ──────────────────────────────────────────────────────
  // Every width now grows the list instead of paging it. A "Next" button at
  // the bottom of 24 cards is a wall wherever it appears: you scroll, you hit
  // it, you click, and you are thrown back to the top of a fresh page having
  // lost your place in the old one. Growing the list never asks for that.
  //
  // The list still ends. The sentinel below becomes "N products · that's
  // everything" once the last batch is in, so the page stops growing and the
  // footer is reachable -- which is the usual objection to infinite scroll on
  // a desktop site, and the reason the end marker is not optional.
  const [shopVisible, setShopVisible] = useState(SHOP_PAGE_SIZE);
  const shopObserverRef = useRef(null);

  // Any change to the filters is a new list, so the growth resets with it --
  // otherwise switching category would open already scrolled 200 items deep.
  useEffect(() => {
    setShopVisible(SHOP_PAGE_SIZE);
  }, [searchTerm, selectedCategory, sortBy, priceRange, minRating, inStockOnly, dealsOnly, SHOP_PAGE_SIZE]);

  // A CALLBACK ref, not a useRef + useEffect pair.
  //
  // The effect version did not work: the sentinel only renders once products
  // have loaded, so on the first pass ref.current was still null and the
  // effect returned early -- and by the time the node existed, the effect's
  // dependencies had not necessarily changed to make it run again. A callback
  // ref fires exactly when React attaches and detaches the node, which is the
  // moment that actually matters here.
  const shopSentinelRef = useCallback((node) => {
    if (shopObserverRef.current) {
      shopObserverRef.current.disconnect();
      shopObserverRef.current = null;
    }
    if (!node || typeof IntersectionObserver === 'undefined') return;

    // rootMargin starts the next batch while the sentinel is still a screen
    // below the fold, so the products are already there by the time the
    // customer scrolls to where they would have been waiting.
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // Reads length off the ref rather than closing over it, so the
        // callback never needs re-creating as the list grows.
        setShopVisible((n) => n + SHOP_PAGE_SIZE);
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(node);
    shopObserverRef.current = observer;
  }, [SHOP_PAGE_SIZE]);

  const pagedProducts = filteredProducts.slice(0, shopVisible);
  const shopHasMore = shopVisible < filteredProducts.length;

  const searchSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const q = searchTerm.toLowerCase();
    const now = new Date();
    return products
      .filter(p => {
        const matchesQuery = p.name.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
        // Same sale/non-sale split as filteredProducts below -- otherwise
        // autocomplete could suggest a product that then appears missing
        // once the search is actually applied to the grid.
        const hasSaleNow = p.salePrice && p.saleEndsAt && new Date(p.saleEndsAt) > now;
        return matchesQuery && (dealsOnly ? hasSaleNow : !hasSaleNow);
      })
      .slice(0, 6)
      .map(p => ({ id: p.id, name: p.name, category: p.category, price: p.price, image: p.image }));
  }, [searchTerm, products, dealsOnly]);

  const statsBarData = useMemo(() => {
    const count = filteredProducts.length;
    const sellerCount = new Set(
      filteredProducts.map(p => p.sellerId || p.sellerName).filter(Boolean)
    ).size;
    const ratedProducts = filteredProducts.filter(p => (p.avgRating || 0) > 0);
    const positivePercent = ratedProducts.length > 0
      ? Math.round(ratedProducts.filter(p => p.avgRating >= 4).length / ratedProducts.length * 100)
      : null;
    const totalReviews = filteredProducts.reduce((sum, p) => sum + (p.reviewCount || 0), 0);
    return { count, sellerCount, positivePercent, totalReviews };
  }, [filteredProducts]);

  const statsBarItems = useMemo(() => [
    {
      icon: <ShieldCheckIcon />, colorClass: 'ssb-positive',
      value: statsBarData.sellerCount > 0 ? `${statsBarData.sellerCount}+` : '—',
      label: 'Verified Sellers',
    },
    {
      icon: <StarFilledIcon />, colorClass: 'ssb-attention',
      value: statsBarData.positivePercent !== null ? `${statsBarData.positivePercent}%` : '—',
      label: 'Positive Reviews',
    },
    {
      icon: <MessageSquareIcon />, colorClass: 'ssb-info',
      value: statsBarData.totalReviews.toLocaleString(),
      label: 'Total Reviews',
    },
    // Was "Fast Delivery / Across the country". Nothing measures delivery
    // speed, and delivery only reaches each seller's configured zones, so it
    // was neither measured nor true. Cash on delivery genuinely is accepted on
    // every order (routes/ordersNew.js falls back to it unconditionally).
    {
      icon: <TruckIcon />, colorClass: 'ssb-blue',
      value: 'Cash on Delivery',
      label: 'Pay when it arrives',
    },
  ], [statsBarData]);

  const cartQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // The shop page no longer carries an inline cart sidebar, so every entry point
  // (navbar cart icon, bottom nav) opens the full-screen /cart page. The running
  // item count lives on the navbar cart badge instead.
  //
  // The cart opens in a new tab rather than navigating in place, so the page the
  // shopper came from keeps everything it had: scroll position, active filters,
  // pagination, a half-read product page. Navigating away and pressing Back
  // rebuilds that page from scratch and loses all of it.
  //
  // `noopener` is not optional here: without it the new tab receives a
  // window.opener handle to this one and could navigate it elsewhere.
  const openCartPanel = () => {
    // Already looking at the cart: don't spawn a duplicate tab of the page
    // you are standing on.
    if (page === 'cart') return;
    window.open(pageToUrl('cart'), '_blank', 'noopener,noreferrer');
  };

  // Persist cart locally so it survives a page refresh, for guests and logged-in users alike.
  useEffect(() => {
    localStorage.setItem('apollostore_cart', JSON.stringify(normalizeCartItems(cart)));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('apollostore_saved_for_later', JSON.stringify(savedForLater));
  }, [savedForLater]);

  const saveForLater = (item) => {
    setSavedForLater(prev => prev.some(p => sameCartLine(p, item.id, item.size, item.color)) ? prev : [...prev, item]);
    setCart(prev => prev.filter(c => !sameCartLine(c, item.id, item.size, item.color)));
  };
  const moveToCart = (item) => {
    setSavedForLater(prev => prev.filter(p => !sameCartLine(p, item.id, item.size, item.color)));
    addToCart(item, item.size, item.color);
  };
  const removeSaved = (id) => setSavedForLater(prev => prev.filter(p => p.id !== id));

  // On login (or app load with a stored session), reconcile with the server-side cart
  // so a cart started on another device — or before signing in — isn't lost.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/cart`, { credentials: 'include' });
        if (!res.ok) return;
        const { items: serverCart } = await res.json();
        if (!Array.isArray(serverCart) || serverCart.length === 0) return;
        setCart((local) => {
          const merged = [...normalizeCartItems(local)];
          normalizeCartItems(serverCart).forEach((sItem) => {
            const idx = merged.findIndex((i) => i.id === sItem.id);
            if (idx === -1) merged.push(sItem);
            else merged[idx] = { ...merged[idx], quantity: Math.max(merged[idx].quantity, sItem.quantity) };
          });
          return merged;
        });
      } catch { /* silent */ }
    })();
  }, [user]);

  // Keep the server copy of the cart in sync (debounced) so the abandoned-cart
  // reminder job has an accurate "last updated" timestamp to work from.
  const cartSyncTimer = useRef(null);
  useEffect(() => {
    if (!user) return;
    clearTimeout(cartSyncTimer.current);
    cartSyncTimer.current = setTimeout(() => {
      const cleanCart = normalizeCartItems(cart);
      fetch(`${API_BASE_URL}/api/cart`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: cleanCart }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(cartSyncTimer.current);
  }, [cart, user]);

  // Two cart lines for the same product but different sizes and/or colours
  // are distinct -- "size"/"color" (undefined for products without that
  // option) are part of the identity key everywhere the cart is matched,
  // updated, or removed.
  const sameCartLine = (item, productId, size, color) =>
    item.id === productId
    && (item.size || undefined) === (size || undefined)
    && (item.color || undefined) === (color || undefined);

  // A successful cart change is already reported by the bottom nav's cart
  // badge, which is on screen the whole time in the app. Saying it again in a
  // toast was not just redundant: the toast rises from the bottom of the
  // screen and sat directly over the badge, hiding the very count it was
  // confirming. The web keeps the toast, where there is no persistent badge in
  // view to carry the message.
  //
  // Only the successful cases are silenced. Failures still speak, because a
  // badge that does not move cannot report why.
  const confirmCartChange = (message) => {
    if (isInstalledApp()) return;
    pushToast(message, 'success');
  };

  const addToCart = (product, size, color) => {
    const saleActive = product.salePrice && product.saleEndsAt && new Date(product.saleEndsAt) > new Date();
    // A selected colour+size variant's own price (when explicitly set)
    // overrides the flat/sale price -- same precedence as the product page.
    const variant = (color && size && product.variants?.length)
      ? product.variants.find(v => v.color === color && v.size === size)
      : null;
    let cartItem = saleActive
      ? { ...product, price: product.salePrice, originalPrice: product.price }
      : product;
    if (variant?.price != null) cartItem = { ...cartItem, price: variant.price };
    setCart((current) => {
      const existing = current.find((item) => sameCartLine(item, product.id, size, color));
      if (existing) {
        if (existing.quantity >= 3) {
          // Not silenced in the app: nothing changed, so the badge cannot say
          // anything. A cart that simply does not move is indistinguishable
          // from a tap that missed, which is the one case that does need
          // words. See confirmCartChange below for the successful ones.
          pushToast('Maximum quantity reached for this item.', 'warning');
          return current;
        }
        confirmCartChange('Cart quantity updated.');
        return current.map((item) =>
          sameCartLine(item, product.id, size, color) ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      confirmCartChange('Added to cart.');
      return [...current, { ...cartItem, size, color, quantity: 1 }];
    });
    setOrderMessage('');
  };

  const updateQuantity = (productId, value, size, color) => {
    const clamped = Math.min(3, Math.max(0, value));
    if (clamped === 0) {
      removeFromCart(productId, size, color);
      return;
    }
    setCart((current) =>
      current
        .map((item) => (sameCartLine(item, productId, size, color) ? { ...item, quantity: clamped } : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId, size, color) => {
    const removed = cart.find((item) => sameCartLine(item, productId, size, color));
    setCart((current) => current.filter((item) => !sameCartLine(item, productId, size, color)));
    if (removed) {
      pushToast('Item removed from cart.', 'info', {
        label: 'Undo',
        onClick: () => setCart((current) => (current.some(i => sameCartLine(i, productId, size, color)) ? current : [...current, removed])),
      });
    }
  };

  const fetchWishlistCount = useCallback(async () => {
    if (!user) { setWishlistCount(0); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/wishlist`, { credentials: 'include' });
      if (res.ok) setWishlistCount((await res.json()).length);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchWishlistCount(); }, [fetchWishlistCount]);

  const fetchRewards = useCallback(async () => {
    if (!user) { setRewards({ points: 0, dollarValue: 0, history: [] }); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/rewards`, { credentials: 'include' });
      if (res.ok) setRewards(await res.json());
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  const fetchWallet = useCallback(async () => {
    if (!user) { setWallet({ balance: 0, history: [] }); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/wallet`, { credentials: 'include' });
      if (res.ok) setWallet(await res.json());
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Purchase-history-based "for you" feed (see GET /api/products/recommendations).
  // Only meaningful for a logged-in customer with order history -- guests and
  // brand-new accounts get an empty payload and the homepage sections hide.
  const fetchRecommendations = useCallback(async () => {
    if (!user) { setRecommendations(null); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/recommendations`, { credentials: 'include' });
      if (res.ok) setRecommendations(await res.json());
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchRecommendations(); }, [fetchRecommendations]);

  const addToWishlist = async (productId) => {
    if (!user) {
      setShowAuthModal('login');
      return;
    }
    if (wishlistPendingIds[productId]) return;
    setWishlistPendingIds((prev) => ({ ...prev, [productId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/wishlist/${productId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Unable to add to wishlist.');
      showOrderMessage('Added to wishlist! View it from the menu.');
      pushToast('Added to wishlist.', 'success');
      fetchWishlistCount();
    } catch (err) {
      showOrderMessage(err.message);
      pushToast(err.message, 'error');
    } finally {
      setWishlistPendingIds((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const handleStartCheckout = () => {
    if (cart.length === 0) {
      showOrderMessage(t('cart.addProductsBeforeCheckout'));
      return;
    }
    setCheckoutStep(1);
  };

  // Shared between the desktop cart sidebar and the full-screen mobile cart
  // page so both stay in sync with zero duplication.
  // The /cart page is the only surface that renders this. Filled carts get a
  // two-column split (items left, order summary right) that stacks below 1000px;
  // a fully empty cart drops to one centred column instead, since a two-column
  // split with nothing on the left just frames the emptiness.
  function renderCartBody() {
    const isEmpty = cart.length === 0;
    // Delivery is intentionally NOT estimated here: the real fee is zone-based
    // on the delivery address (see checkout, which uses seller/zone shipping or
    // a flat express upgrade), so any flat number shown pre-address would just
    // contradict what the customer actually pays -- a real trust problem for a
    // cash-on-delivery buyer planning how much to bring. So the cart shows an
    // honest estimate excluding delivery, and delivery is resolved at checkout.
    const tax = isEmpty ? 0 : cartTotal * (taxRatePct / 100);
    const estimatedTotal = cartTotal + tax;

    const emptyState = (
      <div className="cs-empty">
        <div className="cs-empty-illustration">
          <span className="cs-empty-sparkle cs-empty-sparkle-1"><IconSparkle size={13} /></span>
          <span className="cs-empty-sparkle cs-empty-sparkle-2"><IconSparkle size={15} /></span>
          <span className="cs-empty-sparkle cs-empty-sparkle-3"><IconSparkle size={13} /></span>
          <svg className="cs-empty-cart-icon" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1.4" fill="currentColor" stroke="none"/>
            <circle cx="18" cy="21" r="1.4" fill="currentColor" stroke="none"/>
            <path d="M2.5 3h2.5l2.7 13.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.6L22 8H6.2"/>
          </svg>
          <span className="cs-empty-heart-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="white" stroke="none">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </span>
        </div>
        <h3 className="cs-empty-title">{t('cart.emptyTitle')}</h3>
        <p className="cs-empty-sub">{t('cart.emptySub1')}<br />{t('cart.emptySub2')}</p>
        <button type="button" className="cs-empty-continue-btn" onClick={() => navigate('/')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          {t('cart.continueShopping')}
        </button>
        <button type="button" className="cs-empty-categories-btn" onClick={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
          {t('nav.viewCategories')}
        </button>
      </div>
    );

    const trustCard = (
      <div className="cartpg-card cs-trust">
        <div className="cs-trust-item">
          <span className="cs-trust-icon cs-trust-icon--blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </span>
          <div>
            <p className="cs-trust-title">{t('cart.secureCheckout')}</p>
            <p className="cs-trust-sub">{t('cart.secureCheckoutSub')}</p>
          </div>
        </div>
        <div className="cs-trust-item">
          <span className="cs-trust-icon cs-trust-icon--positive">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.61"/>
            </svg>
          </span>
          <div>
            <p className="cs-trust-title">{t('cart.easyReturns')}</p>
            <p className="cs-trust-sub">{t('cart.easyReturnsSub')}</p>
          </div>
        </div>
        <div className="cs-trust-item">
          <span className="cs-trust-icon cs-trust-icon--info">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
          </span>
          <div>
            <p className="cs-trust-title">{t('cart.support247')}</p>
            <p className="cs-trust-sub">{t('cart.support247Sub')}</p>
          </div>
        </div>
      </div>
    );

    if (isEmpty && savedForLater.length === 0) {
      return (
        <div className="cartpg-empty-layout">
          <div className="cartpg-card">{emptyState}</div>
          {trustCard}
        </div>
      );
    }

    return (
      <div className="cartpg-grid">
        {/* ── LEFT: items ── */}
        <div className="cartpg-col">
          <div className="cartpg-hd">
            <span className="cartpg-hd-icon"><IconCart size={20} /></span>
            {/* {{num}} rather than {{count}} on purpose: i18next treats `count`
                as a plural selector, which would need zero/two/few/many forms
                in Arabic for a string that never actually changes shape. */}
            <h2 className="cartpg-hd-title">{t('cart.itemsHeading', { num: cart.length })}</h2>
          </div>

          {isEmpty ? (
            <div className="cartpg-card">{emptyState}</div>
          ) : (
            <div className="cs-items">
              {cart.map(item => (
                <div key={item.id + (item.size ? `-${item.size}` : '') + (item.color ? `-${item.color}` : '')} className="cs-item">
                  <img src={mediaUrl(item.image)} alt={item.name} className="cs-item-img" onError={fallbackToPlaceholder} />
                  <div className="cs-item-info">
                    <p className="cs-item-name">{item.name}</p>
                    {(item.color || item.size) && (
                      <p className="cs-item-size">
                        {item.color && <>{t('cart.colorLabel')}: <strong>{item.color}</strong></>}
                        {item.color && item.size && ' · '}
                        {item.size && <>{t('cart.sizeLabel')}: <strong>{item.size}</strong></>}
                      </p>
                    )}
                    <p className="cs-item-price">{formatCurrency(item.price)}</p>
                    <div className="cs-qty-row">
                      <div className="cs-qty-group">
                        <button type="button" className="cs-qty-btn" onClick={() => updateQuantity(item.id, item.quantity - 1, item.size, item.color)} aria-label={t('common.remove')}>−</button>
                        <span className="cs-qty">{item.quantity}</span>
                        <button type="button" className="cs-qty-btn" onClick={() => updateQuantity(item.id, item.quantity + 1, item.size, item.color)} disabled={item.quantity >= 3} title={t('cart.maxPerItem', { count: 3 })}>+</button>
                      </div>
                      {item.quantity >= 3 && <span className="cs-qty-max-hint">{t('cart.maxPerItem', { count: 3 })}</span>}
                    </div>
                    <div className="cs-item-actions">
                      <button type="button" className="cs-item-action cs-item-action--danger" onClick={() => removeFromCart(item.id, item.size, item.color)}>
                        <IconTrash size={14} />{t('common.remove')}
                      </button>
                      <span className="cs-item-action-sep" aria-hidden="true" />
                      <button type="button" className="cs-item-action" onClick={() => saveForLater(item)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                        </svg>
                        {t('cart.saveForLater')}
                      </button>
                    </div>
                  </div>
                  <div className="cs-item-aside">
                    <span className="cs-item-qty-tag">{t('cart.qtyLabel', { count: item.quantity })}</span>
                    <span className="cs-item-line-total">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {savedForLater.length > 0 && (
            <div className="cartpg-card">
              <p className="cs-saved-title">{t('cart.savedForLater', { count: savedForLater.length })}</p>
              {savedForLater.map(item => (
                <div key={item.id} className="cs-saved-item">
                  <img src={mediaUrl(item.image)} alt={item.name} className="cs-item-img" onError={fallbackToPlaceholder} />
                  <div className="cs-item-info">
                    <p className="cs-item-name">{item.name}</p>
                    <p className="cs-item-price">{formatCurrency(item.price)}</p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button type="button" className="cs-save-later-btn" onClick={() => moveToCart(item)}>{t('cart.moveToCart')}</button>
                      <button type="button" className="cs-save-later-btn" style={{ color: ORANGE }} onClick={() => removeSaved(item.id)}>{t('common.remove')}</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="cartpg-card cartpg-help">
            <span className="cartpg-help-icon"><IconPackage size={20} /></span>
            <div className="cartpg-help-text">
              <p className="cartpg-help-title">{t('cart.needHelpTitle')}</p>
              <p className="cartpg-help-sub">{t('cart.needHelpSub')}</p>
            </div>
            <button type="button" className="cartpg-help-btn" onClick={() => navigateTo('contact')}>
              {t('cart.contactSupport')}
            </button>
          </div>
        </div>

        {/* ── RIGHT: order summary (sticky) ── */}
        <aside className="cartpg-side">
          {/* A "spend $50 for free shipping" progress bar used to sit here. No
              such rule exists -- delivery is priced by the seller's zone at any
              order size (routes/ordersNew.js) -- so it promised a reward the
              checkout would never apply, and was removed. */}
          <div className="cartpg-card">
            <div className="cartpg-hd">
              <span className="cartpg-hd-icon"><IconTag size={20} /></span>
              <h2 className="cartpg-hd-title">{t('cart.orderSummary')}</h2>
            </div>

            <div className="cs-totals">
              <div className="cs-total-row">
                <span>{t('cart.subtotal')}</span>
                <span>{formatCurrency(cartTotal)}</span>
              </div>
              <div className="cs-total-row">
                <span className="cs-shipping-label">{t('cart.shipping')}</span>
                <span className="cs-shipping-tbd">{isEmpty ? '—' : t('cart.deliveryAtCheckout')}</span>
              </div>
              {taxRatePct > 0 && (
                <div className="cs-total-row">
                  <span>{t('cart.taxWithRate', { rate: taxRatePct })}</span>
                  <span>{isEmpty ? '—' : formatCurrency(tax)}</span>
                </div>
              )}
              <div className="cs-total-row cs-total-final">
                <span>{t('cart.estimatedTotal')}</span>
                <strong style={{ color: 'var(--accent)' }}>{formatCurrency(estimatedTotal)}</strong>
              </div>
              {!isEmpty && <p className="cs-total-note">{t('cart.estimatedTotalNote')}</p>}
            </div>

            <button className="cs-checkout-btn" disabled={isEmpty} onClick={handleStartCheckout}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              {t('cart.proceedToCheckout')}
            </button>
            {isEmpty ? (
              <p className="cs-empty-checkout-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {t('cart.emptyHint')}
              </p>
            ) : (
              /* This button has always gone to order history, not the cart --
                 it used to sit in the shop sidebar where "View Cart" made sense.
                 On the cart page itself the old label was just wrong. */
              <button className="cs-view-cart-btn" onClick={() => navigateTo('orders')}>
                {t('cart.viewOrders')}
              </button>
            )}
          </div>

          {trustCard}

          {orderMessage && <div className="alert info" style={{ fontSize: '0.82rem' }}>{orderMessage}</div>}
        </aside>
      </div>
    );
  }

  // Save the address from the "My Address" page without advancing checkout.
  const handleSaveAddressBook = (event) => {
    event.preventDefault();
    const form = event.target;
    const addressData = {
      fullName: form.fullName.value,
      phone: form.phone.value,
      addressLine1: form.addressLine1.value,
      addressLine2: form.addressLine2.value,
      city: form.city.value,
      state: form.state.value,
      postalCode: form.postalCode.value,
      landmark: form.landmark?.value || '',
      deliveryInstructions: form.deliveryInstructions?.value || '',
      email: savedAddress?.email || user?.email || '',
      ...addressPinFields(),
    };
    localStorage.setItem('userAddress', JSON.stringify(addressData));
    setSavedAddress(addressData);
    setAddressSavedMessage('Address saved.');
  };

  const handleSaveAddress = (addressData) => {
    // Save to localStorage
    localStorage.setItem('userAddress', JSON.stringify(addressData));
    setSavedAddress(addressData);
    // Update checkout state
    setCheckout((current) => ({
      ...current,
      name: addressData.fullName,
      email: addressData.email,
      phone: addressData.phone || '',
      city: addressData.city || '',
      state: addressData.state || '',
      // `area` only exists on addresses saved via the map-pin picker -- when
      // absent (every address before this feature) .filter(Boolean) drops it
      // and this produces the exact same string as before.
      // The server requires at least three characters here, so a customer who
      // only dropped a pin -- in a place Nominatim has no street for -- still
      // has to produce something readable. composedAddressFrom falls back to
      // the area name, then to the coordinates themselves, which is what the
      // courier navigates by anyway.
      address: composedAddressFrom([
        addressData.addressLine1,
        addressData.addressLine2,
        addressData.area,
        addressData.city,
        `${addressData.state || ''} ${addressData.postalCode || ''}`.trim(),
      ]),
      // Additive map-pin fields -- undefined for every address that didn't use
      // the picker, so they simply don't appear on the order's customer object.
      location: addressData.location || undefined,
      area: addressData.area || undefined,
      landmark: addressData.landmark || undefined,
      deliveryInstructions: addressData.deliveryInstructions || undefined,
    }));
    setZoneCheck(null);
    setZoneShipping(null);
    setCheckoutStep(2); // Move to Order Summary
  };

  const handlePlaceOrder = async (event) => {
    event?.preventDefault();
    if (placingOrder) return;
    if (cart.length === 0) {
      showOrderMessage('Add products to the cart before checkout.');
      return;
    }
    if (!checkout.name || !checkout.email || !checkout.address) {
      showOrderMessage('Please complete all checkout fields.');
      return;
    }

    try {
      setPlacingOrder(true);
      const headers = { 'Content-Type': 'application/json' };
      const deviceFingerprint = await getDeviceFingerprint();

      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          customer: checkout, items: cart, userId: user?.id,
          // Which surface placed this order. Only Capacitor reports 'app' --
          // a narrow browser window is still the website. The server
          // validates this against a fixed set and falls back to the
          // request's Origin if it is missing or not a value it recognises,
          // so an older build that doesn't send it is still attributed.
          channel: getSalesChannel(),
          couponCode: couponResult?.code || undefined,
          deliveryType, deliveryFee,
          pointsToRedeem: usePoints ? pointsToRedeem : 0,
          walletAmountToRedeem: useWallet ? walletAmountToRedeem : 0,
          paymentMethod,
          // The order-notes textarea is shown for every order (including COD),
          // so send whatever the customer typed regardless of payment method --
          // previously COD notes were silently dropped even though the field
          // was right there on the payment step.
          orderNotes: orderNotes.trim() || undefined,
          deviceFingerprint,
        })
      });
      const data = await response.json();
      if (!response.ok) {
        const err = new Error(data.error || 'Unable to place order.');
        err.restricted = !!data.restricted;
        throw err;
      }
      setPlacedOrder(data);

      // The order exists now, so it is safe to ask for the money -- the server
      // reads the amount from that record rather than from anything this
      // client sends. Charging before the order existed would mean taking
      // payment against a basket with no server-side record of what was in it.
      //
      // Not awaited: the order is placed either way. A customer whose payment
      // prompt fails still has an order they can pay for from order history,
      // which is a far better outcome than a checkout that appears to fail
      // after the order was already written.
      //
      // `data.paymentMethod` rather than the local `paymentMethod` state --
      // the server has the final say on whether online payment was actually
      // accepted, and falls back to cash on delivery when no provider is
      // configured.
      if (data.paymentMethod === 'online' && onlineMethods.length) {
        startOnlinePayment(data.id, onlineMethods[0].id);
      }

      setCart([]);
      setCheckout({ name: '', email: '', address: '', phone: '' });
      setCheckoutStep('success');
      setCouponCode('');
      setCouponResult(null);
      setCouponError('');
      setDeliveryType('standard');
      setPaymentMethod('cod');
      setOrderNotes('');
      setUsePoints(false);
      setPointsToRedeem(0);
      setUseWallet(false);
      setWalletAmountToRedeem(0);
      fetchRewards();
      fetchWallet();
      // This order just changed the customer's purchase history, so refresh the
      // "for you" feed -- a first-time buyer gets recommendations immediately,
      // and the top category shifts as their buying pattern does.
      fetchRecommendations();
      // Bought it -- no reason to keep nudging them back to it.
      removeFromRecentlyViewed((data.items || []).map(i => i.id));
      // Refresh products so stock counts reflect the placed order
      fetch(`${API_BASE_URL}/api/products`).then((r) => r.json()).then(setProducts).catch(() => {});

      // Fetch a few related products for the success page's "You May Also Like" strip.
      //
      // This asks for a page rather than the whole category. It used to pull
      // every product in the category down just to show four of them, which on
      // a large category meant the success page -- the one screen where the
      // customer has already paid and only wants confirmation -- was the
      // heaviest request in the whole checkout.
      //
      // limit=8 rather than 4 because up to four of them may be the items just
      // purchased, which are filtered out below.
      const firstItemId = data.items?.[0]?.id;
      if (firstItemId) {
        const purchasedIds = new Set(data.items.map((i) => i.id));
        fetch(`${API_BASE_URL}/api/products/${firstItemId}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => p ? fetch(`${API_BASE_URL}/api/products?category=${encodeURIComponent(p.category)}&limit=8`) : null)
          .then((r) => (r && r.ok ? r.json() : null))
          .then((res) => setSuccessRecs((res?.items || []).filter((p) => !purchasedIds.has(p.id)).slice(0, 4)))
          .catch(() => setSuccessRecs([]));
      } else {
        setSuccessRecs([]);
      }
    } catch (err) {
      showOrderMessage(err.message, { restricted: !!err.restricted });
      pushToast(err.message, 'error');
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading || !bootMinElapsed) {
    return <BootScreen />;
  }

  if (loggingOut) {
    return <BootScreen message="Signing you out…" />;
  }

  // ── Product detail — public, no login required ──────────────
  if (page === 'seller-store' && selectedSellerId) {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Store" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <Suspense fallback={PAGE_FALLBACK}>
          <SellerStorePage
            sellerId={selectedSellerId}
            onNavigateToProduct={(id) => navigate(`/product/${id}`)}
            onAddToCart={addToCart}
          />
        </Suspense>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  // ── Abandoned-cart recovery landing ──────────────────────────────────────
  // Reached only from a link in a recovery email. Rendered before the generic
  // auth gate below (and listed in GUEST_ACCESSIBLE_PAGES) so a signed-out
  // customer sees their saved cart instead of a login wall.
  // Reached from the unsubscribe link in a cart reminder, by someone who is
  // very likely not signed in -- so, like the recovery page, it renders above
  // the auth gate.
  if (page === 'cart-reminders-off') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Email preferences" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate('/')} backLabel="Back to Home" mobileShowName hideThemeOnMobile />
        <CartRemindersOffPage token={reminderOptOut.token} invalid={reminderOptOut.invalid} onGoHome={() => navigate('/')} />
      </div>
    );
  }

  if (page === 'cart-recover' && cartRecoveryToken) {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Your saved cart" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate('/')} backLabel="Back to Home" mobileShowName hideThemeOnMobile />
        <CartRecoveryPage
          token={cartRecoveryToken}
          user={user}
          onRestored={(items) => setCart(normalizeCartItems(items))}
          onContinueToCheckout={() => {
            // Leaving this URL is what actually hands control to the checkout:
            // this branch returns early, so setting checkoutStep while still on
            // /cart/recover/:token would set a step nothing ever renders.
            navigate('/cart');
            // Straight to Order Summary when we already know where to deliver;
            // otherwise the address step, which flows into it. Landing on step
            // 2 with no saved address would render an empty "Delivering to"
            // card, which is worse than asking for the address first.
            setCheckoutStep(savedAddress ? 2 : 1);
          }}
          onSignIn={() => setShowAuthModal('login')}
          onNavigateToProduct={(id) => navigate(`/product/${id}`)}
          onGoHome={() => navigate('/')}
        />
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'shared-wishlist' && sharedWishlistToken) {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Shared Wishlist" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate('/')} mobileShowName hideThemeOnMobile />
        <SharedWishlistPage
          token={sharedWishlistToken}
          onAddToCart={addToCart}
          onNavigateToProduct={(id) => navigate(`/product/${id}`)}
        />
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'product' && selectedProductId) {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Product" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <Suspense fallback={PAGE_FALLBACK}>
          <ProductDetailPage
            productId={selectedProductId}
            onBack={() => navigate(-1)}
            onAddToCart={addToCart}
            onBuyNow={(product, size, color) => {
              addToCart(product, size, color);
              navigate('/');
              setCheckoutStep(user ? 1 : 'auth');
            }}
            onNavigateToProduct={(id) => navigate(`/product/${id}`)}
            onNavigate={navigateTo}
          />
        </Suspense>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  // ── Checkout auth gate — sign in before placing order ────────
  if (checkoutStep === 'auth') {
    if (user) { setCheckoutStep(1); return null; }
    return (
      <div className={shellClassName}>
        <SubPageHeader title="Checkout" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => setCheckoutStep(null)} backLabel="Back to Cart" mobileShowName hideThemeOnMobile />
        <div className="auth-gate-page">
          <div className="auth-gate-box">
            <div className="auth-gate-icon-wrap"><IconCart size={34} /></div>
            <h2>Sign in to place your order</h2>
            <p className="auth-gate-sub">Sign in to save your address, earn reward points, and track orders — or continue as a guest.</p>
            <div className="auth-gate-tabs">
              <button
                className={`auth-gate-tab${checkoutAuthMode === 'login' ? ' active' : ''}`}
                onClick={() => setCheckoutAuthMode('login')}
              >Sign In</button>
              <button
                className={`auth-gate-tab${checkoutAuthMode === 'register' ? ' active' : ''}`}
                onClick={() => setCheckoutAuthMode('register')}
              >Create Account</button>
            </div>
            {checkoutAuthMode === 'register' ? (
              <RegisterPage onSuccess={() => setCheckoutStep(1)} />
            ) : (
              <LoginPage onSuccess={() => setCheckoutStep(1)} />
            )}
            <div className="auth-gate-divider"><span>or</span></div>
            <button className="auth-gate-guest-btn" onClick={() => setCheckoutStep(1)}>
              Continue as Guest
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Legal + support pages — public, no login required ──────────
  if (page === 'privacy' || page === 'terms' || page === 'cookies' || page === 'returns' || page === 'shipping'
    || page === 'careers' || page === 'about' || page === 'commission-plan' || page === 'seller-guidelines'
    || page === 'news-updates' || page === 'brands' || page === 'contact' || page === 'faq' || page === 'helpCenter' || page === 'sellerFaq'
    || page === 'get-app') {
    const LEGAL_PAGES = {
      privacy: { title: 'Privacy Policy', Content: PrivacyPolicyPage },
      terms: { title: 'Terms & Conditions', Content: TermsPage },
      cookies: { title: 'Cookie Policy', Content: CookiePolicyPage },
      returns: { title: 'Returns & Refunds', Content: ReturnsRefundsPage },
      shipping: { title: 'Shipping Policy', Content: ShippingPolicyPage },
      careers: { title: 'Careers', Content: CareersPage },
      about: { title: 'About Us', Content: AboutPage },
      'commission-plan': { title: 'Commission Plan', Content: CommissionPlanPage },
      'seller-guidelines': { title: 'Seller Guidelines', Content: SellerGuidelinesPage },
      'news-updates': { title: 'News & Updates', Content: NewsUpdatesPage },
      brands: { title: 'Brands', Content: BrandsPage },
      contact: { title: 'Contact Us', Content: ContactPage },
      faq: { title: 'FAQ', Content: FaqPage },
      helpCenter: { title: 'Help Center', Content: HelpCenterPage },
      sellerFaq: { title: 'Seller FAQs', Content: SellerFaqPage },
      'get-app': { title: 'Get the App', Content: GetTheAppPage },
    };
    const { title, Content } = LEGAL_PAGES[page];
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title={title} user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <Suspense fallback={PAGE_FALLBACK}>
            <Content onNavigate={navigateTo} onViewStore={(id) => navigate(`/store/${id}`)} />
          </Suspense>
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  // ── Application tracker — public, no login required. An applicant
  // requesting/uploading documents follows an emailed link (?id=&token=)
  // that may land in a browser where they're logged out entirely, so this
  // can never sit behind the auth gate below.
  if (page === 'track-application') {
    const trackParams = new URLSearchParams(location.search);
    return (
      <div className={shellClassName}>
        <Suspense fallback={PAGE_FALLBACK}>
          <ApplicationTracker
            initialId={trackParams.get('id') || trackingId}
            initialToken={trackParams.get('token') || ''}
            user={user}
            onGoToPortal={() => navigate('/seller')}
            onBack={() => navigate(-1)}
          />
        </Suspense>
      </div>
    );
  }

  if (page === 'track-order') {
    const trackOrderId = location.pathname === '/track' ? '' : location.pathname.replace('/track/', '');
    return (
      <div className={shellClassName}>
        <OrderTrackingPage
          orderId={trackOrderId}
          onBack={() => navigate(-1)}
          onTrackAnother={(id) => navigate(`/track/${id}`)}
        />
      </div>
    );
  }

  // ── Delivery portal — requires a logged-in 'delivery' (or admin) account.
  // Standalone (no app shell/nav) since this is used by a delivery agent,
  // not a shopper; the component itself shows a login form when signed out.
  if (page === 'delivery-confirm') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <DeliveryConfirmPage user={user} onLogout={handleLogout} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // ── Cart — public, no login required (same as the desktop sidebar).
  // Skipped once checkout has actually started so checkoutStep 1/2/3 below
  // can take over (checkoutStep === 'auth' is handled earlier, above).
  if (page === 'cart' && !checkoutStep) {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <header className="cart-page-header">
          <div className="cph-inner">
            <button type="button" className="cph-back" onClick={() => navigate(-1)} aria-label="Go back">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 className="cph-title">{t('cart.title')}</h1>
            <button type="button" className="cph-close" onClick={() => navigate('/')} aria-label="Close cart">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </header>
        <div className="cart-page-body">
          {renderCartBody()}
        </div>
        <BottomNav
          active="cart"
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  // ── Page-level auth gate — all protected pages ───────────────
  // Excludes checkoutStep: the checkout flow (1/2/3/'auth'/'edit-address'/
  // 'success') has its own dedicated blocks below that already support guest
  // checkout, and "Proceed to Checkout" from the cart page sets checkoutStep
  // without changing `page` away from 'cart' -- without this exclusion, this
  // generic gate would intercept guests before they ever reach that flow.
  //
  // Also excludes deals/category/seller-registration: these are public
  // storefront pages (the whole point of /deals and /category/:name is that
  // a banner link or shared URL lands anyone there, and "Become a Seller" is
  // open to anyone, not just existing customers) that don't have their own
  // early-return above this gate the way product/cart/track-order do -- so
  // without this exclusion they silently 404'd into a login wall for every
  // guest, which is how this went unnoticed all session in an
  // already-logged-in browser.
  //
  // admin-login (/portal-access) needs the same exclusion for the opposite
  // reason: it exists specifically so a signed-out admin can reach a login
  // form at all -- gating it behind the generic customer login wall would
  // make the page permanently unreachable for the one visitor it's for.
  // 'cart-recover' is here for a specific reason: the whole point of a recovery
  // link is that it opens the saved cart without a login wall in front of it.
  // The page itself still cannot WRITE the cart without a session -- the API
  // requires one and returns requiresLogin -- so this only lets someone arriving
  // from their inbox see what they left behind before deciding to sign in.
  const GUEST_ACCESSIBLE_PAGES = ['home', 'deals', 'category', 'seller-registration', 'admin-login', 'cart-recover', 'cart-reminders-off'];
  // Staff consoles are not places you can sign up for. An admin or super-admin
  // account is created by another admin, never self-served, so offering
  // "Create Account" on the way into /admin advertises a door that does not
  // exist -- anyone who takes it registers an ordinary customer account and
  // arrives back at the same wall, no closer to getting in.
  //
  // The seller portal is deliberately NOT in this list: sellers do apply for
  // themselves, at /become-a-seller, so a sign-up route is real for them.
  const STAFF_CONSOLE_PAGES = ['admin', 'super-admin'];
  const staffSignInOnly = STAFF_CONSOLE_PAGES.includes(page);
  // With registration removed there is only one panel left, so the tab state
  // must not be able to strand the page on a form it no longer renders.
  const gateMode = staffSignInOnly ? 'login' : showAuthModal;

  if (!user && !GUEST_ACCESSIBLE_PAGES.includes(page) && !checkoutStep) {
    return (
      <div className={shellClassName}>
        <SubPageHeader user={user} onBack={() => navigate('/')} backLabel="Back to Home" hideLogoIcon hideTheme flush />
        <div className="auth-gate-page">
          <div className="auth-gate-box">
            <div className="auth-gate-icon-wrap">{staffSignInOnly ? <IconShield size={32} /> : <IconUser size={32} />}</div>
            <h2>
              {staffSignInOnly ? 'Admin sign in'
                : gateMode === 'register' ? 'Create your account' : 'Welcome back'}
            </h2>
            <p className="auth-gate-sub">
              {staffSignInOnly ? 'This area is for store staff. Sign in with your admin account to continue.'
                : gateMode === 'register' ? 'Sign up to save your address, earn reward points, and track orders.'
                : 'Sign in to access your account and continue.'}
            </p>
            {!staffSignInOnly && (
              <div className="auth-gate-tabs">
                <button
                  className={`auth-gate-tab${gateMode !== 'register' ? ' active' : ''}`}
                  onClick={() => setShowAuthModal('login')}
                >Sign In</button>
                <button
                  className={`auth-gate-tab${gateMode === 'register' ? ' active' : ''}`}
                  onClick={() => setShowAuthModal('register')}
                >Create Account</button>
              </div>
            )}
            {gateMode === 'register' ? (
              <RegisterPage onSuccess={() => setShowAuthModal(null)} />
            ) : (
              <LoginPage onSuccess={() => setShowAuthModal(null)} />
            )}
            {!staffSignInOnly && (
              <p className="auth-gate-switch">
                {gateMode === 'register' ? (
                  <>Already have an account? <button type="button" onClick={() => setShowAuthModal('login')}>Sign in</button></>
                ) : (
                  <>Don't have an account? <button type="button" onClick={() => setShowAuthModal('register')}>Create one</button></>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (page === 'dashboard') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="My Dashboard" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <Dashboard onAddToCart={addToCart} onNavigate={navigateTo} onNavigateHome={() => navigate('/')} />
        </div>
        <BottomNav
          active="dashboard"
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'orders') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="My Orders" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <OrderHistoryPage
            onAddToCart={addToCart}
            onNavigateHome={() => navigate('/')}
            onRaiseDispute={(orderId) => navigate(`/account/disputes?order=${orderId}`)}
            onNavigate={(target) => {
              if (target === 'contact') navigate('/contact');
              else if (target === 'returns') navigate('/returns-refunds');
            }}
          />
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'dispute') {
    const presetOrderId = new URLSearchParams(location.search).get('order') || null;
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Dispute" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <DisputePage presetOrderId={presetOrderId} onNavigateOrders={() => navigate('/account/orders')} />
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'wishlist') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Wishlist" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <Suspense fallback={PAGE_FALLBACK}>
            <WishlistPage onAddToCart={addToCart} onNavigate={navigateTo} onCountChange={setWishlistCount} />
          </Suspense>
        </div>
        <BottomNav
          active="wishlist"
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'address') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="My Addresses" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        {user ? (
          <Suspense fallback={PAGE_FALLBACK}>
            <AddressBook />
          </Suspense>
        ) : (
          <div style={{ padding: '20px', maxWidth: 560, margin: '0 auto' }}>
            <form className="address-form" onSubmit={handleSaveAddressBook}>
              <label><span>Full Name</span><input type="text" name="fullName" placeholder="Enter your full name" defaultValue={savedAddress?.fullName || ''} required /></label>
              <label><span>Phone Number</span><input type="tel" name="phone" placeholder="Enter your phone number" defaultValue={savedAddress?.phone || ''} required /></label>
              <label><span>Address Line 1</span><input type="text" name="addressLine1" placeholder="House No., Building Name" defaultValue={savedAddress?.addressLine1 || ''} required /></label>
              <label><span>Address Line 2</span><input type="text" name="addressLine2" placeholder="Road name, Area, Colony" defaultValue={savedAddress?.addressLine2 || ''} /></label>
              <label><span>City</span><input type="text" name="city" placeholder="Enter your city" defaultValue={savedAddress?.city || ''} required /></label>
              <label><span>State</span><input type="text" name="state" placeholder="Enter your state" defaultValue={savedAddress?.state || ''} required /></label>
              <label><span>Postal Code <span className="field-optional">(optional)</span></span><input type="text" name="postalCode" placeholder="Enter postal code" defaultValue={savedAddress?.postalCode || ''} /></label>
              <label><span>Nearby Landmark <span className="field-optional">(helps the courier find you)</span></span><input type="text" name="landmark" placeholder="e.g. next to Nyakuron Cultural Centre" defaultValue={savedAddress?.landmark || ''} /></label>
              <label><span>Delivery Instructions <span className="field-optional">(optional)</span></span><textarea name="deliveryInstructions" rows={2} placeholder="e.g. Call on arrival, blue gate, ask for…" defaultValue={savedAddress?.deliveryInstructions || ''} /></label>
              {renderAddressMapPicker()}
              <button type="submit" className="btn">Save Address</button>
              {addressSavedMessage && <div className="alert info" style={{ marginTop: '12px' }}>{addressSavedMessage}</div>}
            </form>
          </div>
        )}
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'settings') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Settings" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        {renderAccountSettingsPage({ settings, toggleSetting, darkMode, setDarkMode, exportingData, onExportData: handleExportData, user, showDeleteAccount, setShowDeleteAccount, deleteAccountPassword, setDeleteAccountPassword, deleteAccountError, deleteAccountBusy, onDeleteAccount: handleDeleteAccount })}
        <div hidden>
          <div className="settings-container">
            <h1>Settings</h1>
            <div className="settings-panel">
              <h2>Account Settings</h2>
              <label className="setting-item">
                <span>Email Notifications</span>
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={() => toggleSetting('emailNotifications')}
                />
              </label>
              <label className="setting-item">
                <span>Order Updates</span>
                <input
                  type="checkbox"
                  checked={settings.orderUpdates}
                  onChange={() => toggleSetting('orderUpdates')}
                />
              </label>
              <label className="setting-item">
                <span>Promotional Emails</span>
                <input
                  type="checkbox"
                  checked={settings.promotionalEmails}
                  onChange={() => toggleSetting('promotionalEmails')}
                />
              </label>
              <hr />
              <h3>Theme</h3>
              <button className="btn" onClick={() => setDarkMode(!darkMode)}>
                Switch to {darkMode ? 'Light' : 'Dark'} Mode
              </button>
              <hr />
              <h2>Data &amp; Privacy</h2>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                Download a copy of everything tied to your account, or permanently delete it.
              </p>
              <button className="btn" disabled={exportingData} onClick={handleExportData} style={{ marginBottom: 10 }}>
                {exportingData ? 'Preparing…' : 'Download My Data'}
              </button>
              {user?.role === 'customer' && (
                !showDeleteAccount ? (
                  <button className="btn" style={{ background: ORANGE, display: 'block' }} onClick={() => setShowDeleteAccount(true)}>
                    Delete My Account
                  </button>
                ) : (
                  <div className="setting-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                    <p style={{ fontSize: '0.85rem', color: ORANGE, fontWeight: 600, margin: 0 }}>
                      This permanently deletes your account and cannot be undone. Enter your password to confirm.
                    </p>
                    <input
                      type="password"
                      placeholder="Password"
                      value={deleteAccountPassword}
                      onChange={(e) => setDeleteAccountPassword(e.target.value)}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)' }}
                    />
                    {deleteAccountError && <p style={{ color: ORANGE, fontSize: '0.8rem', margin: 0 }}>{deleteAccountError}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn" style={{ background: ORANGE }} disabled={deleteAccountBusy} onClick={handleDeleteAccount}>
                        {deleteAccountBusy ? 'Deleting…' : 'Confirm Deletion'}
                      </button>
                      <button className="btn" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} disabled={deleteAccountBusy}
                        onClick={() => { setShowDeleteAccount(false); setDeleteAccountPassword(''); setDeleteAccountError(''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'profile') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="My Profile" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <Suspense fallback={PAGE_FALLBACK}>
            <ProfilePage user={user} updateProfile={updateProfile} uploadAvatar={uploadAvatar} onBack={() => navigate(-1)} />
          </Suspense>
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  if (page === 'notifications') {
    return (
      <div className={`${shellClassName} app-shell-with-bottom-nav`}>
        <SubPageHeader title="Notifications" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => navigate(-1)} mobileShowName hideThemeOnMobile />
        <div className="page-shell-pad">
          <Suspense fallback={PAGE_FALLBACK}>
            <NotificationsPage onUnreadCountChange={setUnreadNotifCount} />
          </Suspense>
        </div>
        <BottomNav
          active={null}
          cartQuantity={cartQuantity}
          wishlistCount={wishlistCount}
          onNavigate={(target) => {
            if (target === 'home') { navigate('/'); }
            else if (target === 'wishlist') { navigateTo('wishlist'); }
            else if (target === 'cart') { openCartPanel(); }
          }}
          onOpenCategories={() => { navigate('/'); setTimeout(() => setShowMobileCatSheet(true), 50); }}
          onOpenAccount={handleOpenAccountSheet}
        />
        {showMobileAccountSheet && (
          <AccountSheet
            user={user}
            wishlistCount={wishlistCount}
            unreadNotifCount={unreadNotifCount}
            isSeller={isSeller}
            onNavigate={navigateTo}
            onLogout={handleLogout}
            onClose={() => setShowMobileAccountSheet(false)}
          />
        )}
        {renderAuthModal()}
        {renderToasts()}
      </div>
    );
  }

  // Redirect admin away from seller-portal
  if (page === 'seller-portal' && user?.role === 'admin') {
    navigate('/admin', { replace: true });
    return null;
  }

  if (page === 'seller-portal' && isSeller) {
    return (
      <Suspense fallback={<div className="loading loading-inline">Loading seller dashboard...</div>}>
        <SellerPortal
          user={user}
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(!darkMode)}
          onLogout={handleLogout}
        />
      </Suspense>
    );
  }

  // Seller portal URL accessed by a customer/guest — not a seller, not a 404 either,
  // since /become-a-seller is a legitimate next step for them.
  if (page === 'seller-portal') {
    navigate('/', { replace: true });
    return null;
  }

  // /portal-access -- already-signed-in admin has nothing to do here, send
  // them straight to the dashboard instead of showing the form again.
  if (page === 'admin-login') {
    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
      return null;
    }
    return (
      <Suspense fallback={<div className="loading loading-inline">Loading...</div>}>
        <AdminLoginPage onSuccess={() => navigate('/admin', { replace: true })} />
      </Suspense>
    );
  }

  if (page === 'admin') {
    if (user?.role !== 'admin') {
      return <NotFound onGoHome={() => navigate('/', { replace: true })} />;
    }
    
    // SECURITY: Force password reset for admin accounts created with temporary password
    if (user?.forcePasswordReset) {
      return (
        <div style={{ padding: '40px', maxWidth: '500px', margin: '100px auto' }}>
          <h1>Reset Your Password</h1>
          <p>Your admin account was created with a temporary password. You must change it before accessing the admin dashboard.</p>
          <AdminForcePasswordReset
            user={user}
            onResetComplete={() => window.location.href = '/admin'}
          />
        </div>
      );
    }
    
    return (
      <Suspense fallback={<div className="loading loading-inline">Loading admin dashboard...</div>}>
        <AdminDashboard
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(!darkMode)}
          onLogout={handleLogout}
          onBack={() => navigate(-1)}
        />
      </Suspense>
    );
  }

  if (page === 'super-admin') {
    // Reachable only by 'full'-tier admins (this repo's "Super Admin") --
    // everyone else, including a 'write'/'read'-tier admin, 404s here same
    // as any other unauthorized route, not a 403 (no point confirming the
    // route exists to someone who can never use it).
    if (user?.role !== 'admin' || (user?.permissionLevel || 'full') !== 'full') {
      return <NotFound onGoHome={() => navigate('/', { replace: true })} />;
    }
    return (
      <Suspense fallback={<div className="loading loading-inline">Loading Super Admin portal...</div>}>
        <SuperAdminPortal
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(!darkMode)}
          onLogout={handleLogout}
          onBack={() => navigate('/admin')}
        />
      </Suspense>
    );
  }

  if (page === 'seller-registration') {
    return (
      <div className={shellClassName}>
        <SellerRegistration
          user={user}
          onBack={() => navigate(-1)}
          onTrackApp={(id) => { setTrackingId(id); navigate('/track-application'); }}
        />
      </div>
    );
  }

  if (checkoutStep === 1) {
    return (
      <div className={shellClassName}>
        <SubPageHeader title="Checkout · Address" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => setCheckoutStep(null)} backLabel="Back to Cart" mobileShowName hideThemeOnMobile />
        <div className="checkout-step-pad">
          <div className="checkout-steps">
            <div className="step active">
              <div className="step-number"><IconCheck size={14} /></div>
              <div className="step-label">{t('checkout.stepAddress')}</div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-label">{t('checkout.orderSummary')}</div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-label">{t('checkout.stepPayment')}</div>
            </div>
          </div>

          <div className="checkout-container checkout-address-container">
            {user ? (
              <div className="address-section">
                <h2>Select Delivery Address</h2>
                <Suspense fallback={PAGE_FALLBACK}>
                  <AddressBook
                    selectMode
                    onSelect={(addr) => handleSaveAddress(addr)}
                  />
                </Suspense>
              </div>
            ) : savedAddress ? (
              <div className="address-section">
                <h2>Deliver to:</h2>
                <div className="address-display">
                  <div className="address-info">
                    <p><strong>{savedAddress.fullName}</strong></p>
                    <p>{savedAddress.addressLine1}{savedAddress.addressLine2 ? `, ${savedAddress.addressLine2}` : ''}</p>
                    <p>{savedAddress.city}, {savedAddress.state} {savedAddress.postalCode}</p>
                    <p>Phone: {savedAddress.phone}</p>
                  </div>
                  <button className="btn-link" onClick={() => setCheckoutStep('edit-address')}>
                    Change
                  </button>
                </div>
                <button className="btn" onClick={() => handleSaveAddress(savedAddress)}>
                  Confirm Address & Continue
                </button>
              </div>
            ) : (
              <div className="address-form-section">
                <h2>Add Delivery Address</h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = {
                    fullName: e.target.fullName.value,
                    phone: e.target.phone.value,
                    // Anything the customer left blank is answered by the
                    // pin's reverse lookup; anything they typed wins.
                    addressLine1: pinValue('addressLine1', e.target.addressLine1.value),
                    addressLine2: e.target.addressLine2.value,
                    city: pinValue('city', e.target.city.value),
                    state: pinValue('state', e.target.state.value),
                    postalCode: pinValue('postalCode', e.target.postalCode.value),
                    landmark: e.target.landmark.value,
                    deliveryInstructions: e.target.deliveryInstructions.value,
                    email: e.target.email?.value || '',
                    ...addressPinFields(),
                  };
                  handleSaveAddress(formData);
                }}>
                  <div className="form-group"><label>Full Name</label><input type="text" name="fullName" required placeholder="Enter your full name" /></div>
                  <div className="form-group"><label>Email</label><input type="email" name="email" required placeholder="Enter your email" /></div>
                  <div className="form-group"><label>Phone Number</label><input type="tel" name="phone" required placeholder="Enter your phone number" /></div>
                  {/* Once a pin is dropped these stop being required: the map
                      already knows the street, city and state, and asking the
                      customer to retype them is asking for what we have. They
                      stay required without a pin, so nothing changes for
                      someone who never opens the map. */}
                  <div className="form-group"><label>Address Line 1 {hasPin && <span className="field-optional">(from your pin)</span>}</label><input type="text" name="addressLine1" required={!hasPin} placeholder={hasPin ? (addressPin?.suggestion?.addressLine1 || 'Optional — the pin covers this') : 'House No., Building Name'} /></div>
                  <div className="form-group"><label>Address Line 2</label><input type="text" name="addressLine2" placeholder="Road name, Area, Colony" /></div>
                  <div className="form-row">
                    <div className="form-group"><label>City</label><input type="text" name="city" required={!hasPin} placeholder={hasPin ? (addressPin?.suggestion?.city || 'From pin') : 'City'} /></div>
                    <div className="form-group"><label>State</label><input type="text" name="state" required={!hasPin} placeholder={hasPin ? (addressPin?.suggestion?.state || 'From pin') : 'State'} /></div>
                    <div className="form-group"><label>Postal Code <span className="field-optional">(optional)</span></label><input type="text" name="postalCode" placeholder="Postal code" /></div>
                  </div>
                  {/* Where street addresses are unreliable, a nearby landmark and
                      directions are what actually let the courier find a COD
                      customer -- so they're front-and-center, not buried. */}
                  <div className="form-group"><label>Nearby Landmark <span className="field-optional">(helps the courier find you)</span></label><input type="text" name="landmark" placeholder="e.g. next to Nyakuron Cultural Centre" /></div>
                  <div className="form-group"><label>Delivery Instructions <span className="field-optional">(optional)</span></label><textarea name="deliveryInstructions" rows={2} placeholder="e.g. Call on arrival, blue gate, ask for…" /></div>
                  {renderAddressMapPicker()}
                  <button type="submit" className="btn">Save Address & Continue</button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (checkoutStep === 'edit-address') {
    return (
      <div className={shellClassName}>
        <SubPageHeader title="Edit Address" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => setCheckoutStep(1)} mobileShowName hideThemeOnMobile />
        <div className="checkout-step-pad">
          <h2>Edit Delivery Address</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = {
              fullName: e.target.fullName.value,
              phone: e.target.phone.value,
              addressLine1: e.target.addressLine1.value,
              addressLine2: e.target.addressLine2.value,
              city: e.target.city.value,
              state: e.target.state.value,
              postalCode: e.target.postalCode.value,
              landmark: e.target.landmark.value,
              deliveryInstructions: e.target.deliveryInstructions.value,
              email: e.target.email.value
            };
            handleSaveAddress(formData);
          }}>
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" name="fullName" required placeholder="Enter your full name" defaultValue={savedAddress?.fullName || ''} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" required placeholder="Enter your email" defaultValue={savedAddress?.email || user?.email || ''} />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input type="tel" name="phone" required placeholder="Enter your phone number" defaultValue={savedAddress?.phone || ''} />
            </div>
            <div className="form-group">
              <label>Address Line 1</label>
              <input type="text" name="addressLine1" required placeholder="House No., Building Name" defaultValue={savedAddress?.addressLine1 || ''} />
            </div>
            <div className="form-group">
              <label>Address Line 2 <span className="field-optional">(optional)</span></label>
              <input type="text" name="addressLine2" placeholder="Road name, Area, Colony" defaultValue={savedAddress?.addressLine2 || ''} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" required placeholder="Enter your city" defaultValue={savedAddress?.city || ''} />
              </div>
              <div className="form-group">
                <label>State</label>
                <input type="text" name="state" required placeholder="Enter your state" defaultValue={savedAddress?.state || ''} />
              </div>
              <div className="form-group">
                <label>Postal Code <span className="field-optional">(optional)</span></label>
                <input type="text" name="postalCode" placeholder="Enter postal code" defaultValue={savedAddress?.postalCode || ''} />
              </div>
            </div>
            <div className="form-group">
              <label>Nearby Landmark <span className="field-optional">(helps the courier find you)</span></label>
              <input type="text" name="landmark" placeholder="e.g. next to Nyakuron Cultural Centre" defaultValue={savedAddress?.landmark || ''} />
            </div>
            <div className="form-group">
              <label>Delivery Instructions <span className="field-optional">(optional)</span></label>
              <textarea name="deliveryInstructions" rows={2} placeholder="e.g. Call on arrival, blue gate, ask for…" defaultValue={savedAddress?.deliveryInstructions || ''} />
            </div>
            <button type="submit" className="btn">
              Save Address & Continue to Summary
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (checkoutStep === 2) {
    return (
      <div className={shellClassName}>
        <CheckoutTrustBar t={t} />
        <SubPageHeader title={`Checkout / ${t('checkout.orderSummary')}`} user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => setCheckoutStep(1)} mobileShowName hideThemeOnMobile />
        <div className="checkout-step-pad checkout-summary-pad">
          <CheckoutSteps current={2} t={t} />

          <div className="order-summary-container">
            <div className="summary-left co-panel">
              <h2 className="co-panel-title">{t('checkout.deliveryOrderItems')}</h2>
              <div className="address-section">
                <h3 className="co-field-label"><IconMapPin size={15} /> {t('checkout.deliveringTo')}</h3>
                <div className="address-display">
                  <div className="address-info">
                    <p><strong>{savedAddress?.fullName}</strong></p>
                    <p>{savedAddress?.addressLine1}{savedAddress?.addressLine2 ? `, ${savedAddress.addressLine2}` : ''}{savedAddress?.area ? `, ${savedAddress.area}` : ''}</p>
                    <p>{savedAddress?.city}, {savedAddress?.state} {savedAddress?.postalCode}</p>
                    <p className="co-address-detail"><IconPhone size={14} /> <span>{savedAddress?.phone}</span></p>
                    {savedAddress?.landmark && <p className="co-address-detail"><IconMapPin size={14} /> <span>Near {savedAddress.landmark}</span></p>}
                    {savedAddress?.deliveryInstructions && <p className="co-address-note"><IconClipboard size={14} /> <span>{savedAddress.deliveryInstructions}</span></p>}
                    {zoneCheck === 'loading' && (
                      <p className="zone-check-status">Checking delivery availability…</p>
                    )}
                    {zoneCheck && zoneCheck !== 'loading' && zoneCheck.hubConfigured && (
                      <p className={`zone-check-status ${zoneCheck.withinZone ? 'zone-ok' : 'zone-warn'}`}>
                        {zoneCheck.withinZone
                          ? <><IconCheckCircle size={15} /> Within our delivery zone</>
                          : <><IconAlertTriangle size={15} /> Outside our delivery zone ({zoneCheck.distanceKm}km away, we deliver within {zoneCheck.radiusKm}km)</>}
                      </p>
                    )}
                  </div>
                  <button className="co-change-btn" onClick={() => setCheckoutStep(1)}>
                    {t('checkout.changeAddress')}
                  </button>
                </div>
              </div>

              <div className="order-items-section">
                <h3 className="co-field-label">{t('checkout.orderItemsCount', { count: cartQuantity })}</h3>
                <div className="order-items">
                  {/* Only the first line is expanded by default. A cart of any
                      size otherwise pushes Price Details and the Continue
                      button below the fold on this step, which is the one
                      screen where the shopper is trying to confirm a total
                      rather than browse what they already chose. */}
                  {(summaryItemsExpanded ? cart : cart.slice(0, 1)).map((item) => {
                    return (
                      <div key={item.id + (item.size ? `-${item.size}` : '') + (item.color ? `-${item.color}` : '')} className="fi-item">
                        <div className="fi-content-row">
                          {/* Left: image + qty selector */}
                          <div className="fi-left">
                            <img src={mediaUrl(item.image)} alt={item.name} className="fi-img" onError={fallbackToPlaceholder} />
                            <div className="fi-qty-wrap">
                              <span className="fi-qty-label">Qty:</span>
                              <select
                                className="fi-qty-select"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value), item.size, item.color)}
                              >
                                {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                              </select>
                              <span className="fi-qty-arrow" aria-hidden="true"><IconChevronDown size={12} /></span>
                            </div>
                          </div>
                          {/* Right: product details */}
                          <div className="fi-right">
                            <p className="fi-name">{item.name}</p>
                            {item.color && <p className="fi-size">Color: <strong>{item.color}</strong></p>}
                            {item.size && <p className="fi-size">Size: <strong>{item.size}</strong></p>}
                            {/* An "Assured" badge sat on every line here, naming a
                                guarantee programme that does not exist. Removed. */}
                            {item.avgRating && (
                              <div className="fi-meta-row">
                                <span className="fi-rating-pill"><IconStar size={11} /> {item.avgRating}</span>
                                <span className="fi-review-cnt">({(item.reviewCount || 0).toLocaleString()})</span>
                              </div>
                            )}
                            <div className="fi-price-row">
                              {item.originalPrice && item.originalPrice > item.price && (
                                <span className="fi-mrp">{formatCurrency(item.originalPrice)}</span>
                              )}
                              <span className="fi-price">{formatCurrency(item.price)}</span>
                            </div>
                            {item.quantity > 1 && (
                              <p className="fi-line-total">
                                {item.quantity} × {formatCurrency(item.price)} = <strong>{formatCurrency(item.price * item.quantity)}</strong>
                              </p>
                            )}
                            {(() => {
                              // Only what this line will actually get. Every item
                              // used to read "EXPRESS" and "Delivery in 2–3 days"
                              // whatever the address, seller or chosen method.
                              // Express is shown only when the shopper picked it,
                              // and a delivery window only when the seller's zone
                              // for this address quotes one; otherwise nothing is
                              // promised.
                              if (deliveryType === 'express') {
                                return (
                                  <div className="fi-delivery">
                                    <span className="fi-express-badge"><IconTruck size={11} /> EXPRESS</span>
                                  </div>
                                );
                              }
                              const zone = item.sellerId && zoneShipping && zoneShipping !== 'loading'
                                ? zoneShipping.breakdown.find((b) => b.sellerId === item.sellerId)?.zone
                                : null;
                              return zone?.deliveryDays ? (
                                <div className="fi-delivery">
                                  <span className="fi-delivery-text">Estimated delivery: {zone.deliveryDays}</span>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        {/* Action row */}
                        <div className="fi-actions">
                          <button className="fi-action-btn" onClick={() => saveForLater(item)}>
                            <IconArrowDown size={13} /> Save for later
                          </button>
                          <span className="fi-action-sep" />
                          <button className="fi-action-btn fi-action-remove" onClick={() => removeFromCart(item.id, item.size, item.color)}>
                            <IconTrash size={13} /> Remove
                          </button>
                          <span className="fi-action-sep" />
                          <button
                            className="fi-action-btn fi-action-buy"
                            disabled={zoneCheck && zoneCheck !== 'loading' && zoneCheck.withinZone === false}
                            onClick={() => {
                              if (zoneCheck && zoneCheck !== 'loading' && zoneCheck.withinZone === false) {
                                setOrderMessage('This address is outside our delivery area — please choose a different address.');
                                return;
                              }
                              setCheckoutStep(3);
                            }}
                          >
                            <IconZap size={13} /> Buy this now
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Collapsed remainder. Shows a stack of thumbnails and the
                      combined value so the shopper can tell at a glance that
                      nothing is missing, without the list itself costing them
                      the screen. Hidden entirely for a single-line cart. */}
                  {cart.length > 1 && (
                    <div className="co-more-items">
                      <div className="co-more-thumbs" aria-hidden="true">
                        {cart.slice(1, 4).map((item, i) => (
                          <img
                            key={`${item.id}-${item.size || ''}-${item.color || ''}-${i}`}
                            src={mediaUrl(item.image)}
                            alt=""
                            className="co-more-thumb"
                          />
                        ))}
                      </div>
                      <div className="co-more-text">
                        <span className="co-more-count">
                          {summaryItemsExpanded
                            ? t('checkout.orderItemsCount', { count: cartQuantity })
                            : t('checkout.additionalItems', { count: cart.length - 1 })}
                        </span>
                        <span className="co-more-sub">{t('checkout.includedInOrder')}</span>
                      </div>
                      <span className="co-more-price">
                        {formatCurrency(cart.slice(1).reduce((s, i) => s + i.price * i.quantity, 0))}
                      </span>
                      <button
                        type="button"
                        className="co-more-btn"
                        aria-expanded={summaryItemsExpanded}
                        onClick={() => setSummaryItemsExpanded(v => !v)}
                      >
                        {summaryItemsExpanded ? t('checkout.hideItems') : t('checkout.viewItems')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="co-protection-note">
                <IconShieldCheck size={15} /> {t('checkout.protectedAllItems')}
              </div>
            </div>

            <div className="summary-right">
              {(() => {
                const totalMRP = cart.reduce((s, i) => s + (i.originalPrice || i.price) * i.quantity, 0);
                const totalDiscount = totalMRP - cartTotal;
                const zoneTotal = zoneShipping && zoneShipping !== 'loading' ? zoneShipping.total : 0;
                const effectiveFee = deliveryType === 'express' ? EXPRESS_FEE : zoneTotal;
                const stepTotal = cartTotal + effectiveFee;
                return (
                  <div className="price-details co-panel">
                    <h2 className="co-panel-title">Price Details</h2>
                    <div className="price-row">
                      <span>Price ({cartQuantity} item{cartQuantity !== 1 ? 's' : ''})</span>
                      <span>{formatCurrency(totalMRP)}</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="price-row" style={{ color: BLUE }}>
                        <span>Discount</span>
                        <span>− {formatCurrency(totalDiscount)}</span>
                      </div>
                    )}

                    {/* Delivery type selector */}
                    <div className="price-row co-delivery-group" style={{ flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
                      <span className="co-field-label">{t('checkout.deliveryMethod')}</span>
                      <label className="checkout-del-opt" style={{ border: deliveryType === 'standard' ? '1.5px solid var(--accent)' : '1px solid var(--border)' }}>
                        <input type="radio" name="checkoutDelivery" checked={deliveryType === 'standard'} onChange={() => setDeliveryType('standard')} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 700 }}>Seller Delivery</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>Fee based on your area</span>
                        </div>
                        {zoneShipping === 'loading'
                          ? <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>calculating…</span>
                          : <span style={{ color: zoneTotal === 0 ? BLUE : ORANGE, fontWeight: 700, fontSize: '0.84rem' }}>
                              {zoneTotal === 0 ? 'FREE' : formatCurrency(zoneTotal)}
                            </span>
                        }
                      </label>
                      <label className="checkout-del-opt" style={{ border: deliveryType === 'express' ? '1.5px solid var(--accent)' : '1px solid var(--border)' }}>
                        <input type="radio" name="checkoutDelivery" checked={deliveryType === 'express'} onChange={() => setDeliveryType('express')} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 700 }}>Express Delivery</span>
                          {/* Was "Tomorrow by 9 PM". Nothing schedules or tracks an
                              express delivery time, so only the flat fee is stated. */}
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>Flat rate</span>
                        </div>
                        <span style={{ fontWeight: 700, fontSize: '0.84rem' }}>{formatCurrency(EXPRESS_FEE)}</span>
                      </label>
                    </div>

                    {/* Zone breakdown: show per-seller fees when using seller delivery */}
                    {deliveryType === 'standard' && zoneShipping && zoneShipping !== 'loading' && zoneShipping.breakdown.length > 0 && (
                      <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 12px', fontSize: '0.82rem' }}>
                        {zoneShipping.breakdown.map((b, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', color: 'var(--text-secondary)' }}>
                            <span><IconTruck size={12} /> {b.storeName}{b.zone && <> <IconArrowRight size={11} /> {b.zone.name}{b.zone.deliveryDays ? ` (${b.zone.deliveryDays})` : ''}</>}</span>
                            <span style={{ fontWeight: 600, color: b.zone ? 'var(--text-primary)' : ORANGE, whiteSpace: 'nowrap' }}>
                              {b.zone ? (b.zone.fee === 0 ? 'FREE' : formatCurrency(b.zone.fee)) : 'No zone'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {deliveryType === 'express' && effectiveFee > 0 && (
                      <div className="price-row">
                        <span>Express Delivery Fee</span>
                        <span style={{ color: ORANGE, fontWeight: 700 }}>{formatCurrency(EXPRESS_FEE)}</span>
                      </div>
                    )}
                    <hr />
                    <div className="price-row total">
                      <span>Total Amount</span>
                      <span>{formatCurrency(stepTotal)}</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="fi-savings-banner">
                        You will save {formatCurrency(totalDiscount)} on this order!
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="co-protection-note">
                <IconShieldCheck size={15} /> {t('checkout.protectedSecureCheckout')}
              </div>
              <button
                className="btn btn-place-order"
                disabled={zoneCheck && zoneCheck !== 'loading' && zoneCheck.withinZone === false}
                onClick={() => {
                  if (zoneCheck && zoneCheck !== 'loading' && zoneCheck.withinZone === false) {
                    setOrderMessage(t('checkout.addressOutsideZone'));
                    return;
                  }
                  setCheckoutStep(3);
                }}
              >
                {t('checkout.continueToPayment')} <IconArrowRight size={16} />
              </button>
              <p className="co-under-cta">{t('checkout.reviewPaymentNext')}</p>
              {orderMessage && <div className="alert info" style={{ marginTop: '15px' }}>{orderMessage}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (checkoutStep === 3) {
    const subtotalAfterCoupon = couponResult ? Math.max(0, cartTotal - couponResult.discount) : cartTotal;
    const maxRedeemablePoints = Math.max(0, Math.min(rewards.points, Math.floor(subtotalAfterCoupon * 0.5 * 100)));
    const pointsApplied = usePoints ? Math.min(pointsToRedeem || 0, maxRedeemablePoints) : 0;
    const rewardsDiscountPreview = parseFloat((pointsApplied / 100).toFixed(2));
    // Wallet credit may cover up to the full remaining amount (unlike reward
    // points, capped at 50% above) since it's real cash-equivalent balance,
    // not a promotional discount.
    const maxRedeemableWallet = Math.max(0, Math.min(wallet.balance, subtotalAfterCoupon - rewardsDiscountPreview));
    const walletApplied = useWallet ? Math.min(walletAmountToRedeem || 0, maxRedeemableWallet) : 0;
    const zoneTotal3 = zoneShipping && zoneShipping !== 'loading' ? zoneShipping.total : 0;
    const effectiveFee3 = deliveryType === 'express' ? EXPRESS_FEE : zoneTotal3;
    // Mirrors the server's own tax computation in routes/ordersNew.js
    // (post-discount/post-rewards/post-wallet, pre-shipping) so this preview
    // matches what the order actually gets charged.
    const taxableAmount3 = Math.max(0, subtotalAfterCoupon - rewardsDiscountPreview - walletApplied);
    const taxPreview3 = parseFloat((taxableAmount3 * (taxRatePct / 100)).toFixed(2));
    const finalTotal = taxableAmount3 + taxPreview3 + effectiveFee3;

    const applyCoupon = async () => {
      setCouponError('');
      setCouponResult(null);
      if (!couponCode.trim()) { setCouponError('Enter a coupon code.'); return; }
      try {
        const cartItemsForCoupon = cart.map((i) => ({ sellerId: i.sellerId || null, price: i.price, quantity: i.quantity }));
        const res  = await fetch(`${API_BASE_URL}/api/coupons/validate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: couponCode.trim(), orderTotal: cartTotal, cartItems: cartItemsForCoupon }) });
        const data = await res.json();
        if (!res.ok) { setCouponError(data.error || 'Invalid coupon.'); return; }
        setCouponResult(data);
      } catch { setCouponError('Could not validate coupon.'); }
    };

    return (
      <div className={shellClassName}>
        <CheckoutTrustBar t={t} />
        <SubPageHeader title={`Checkout / ${t('checkout.stepPayment')}`} user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => setCheckoutStep(2)} mobileShowName hideThemeOnMobile />
        <div className="checkout-step-pad">
          <CheckoutSteps current={3} t={t} />

          <div className="checkout-container">
            <div className="cod-payment-section co-panel">
              <h2 className="co-panel-title">{t('checkout.paymentOrderDetails')}</h2>
              <h3 className="co-field-label">{t('checkout.paymentMethod')}</h3>
              {/* When online payment isn't offered, "Select Payment Method" over a
                  single always-selected card is a dead click -- so for the
                  cash-on-delivery-only case this is just a clear confirmation of
                  how payment works, not a chooser. The selectable cards come back
                  automatically once ONLINE_PAYMENT_ENABLED is turned on. */}
              {ONLINE_PAYMENT_ENABLED ? (
                <>
                  {/* The same shape as the confirmation card below -- marker,
                      name, and a Selected chip on the one in force -- so these
                      two read as one set with it rather than as a different
                      component that happens to sit on the same screen.

                      Selection is carried by the .co-selected class now, not by
                      an inline navy border. Two reasons. The card no longer
                      changes size when you pick it: the base card's 2px border
                      and 18px/20px padding against .co-selected's 1.5px and
                      16px/18px made the chosen card visibly jump. And blue is
                      the selection colour in this palette; navy is structure.

                      The line under each name is gone, with the blurb on the
                      confirmation card. What the online method actually
                      involves is still said where it matters -- the mobile
                      money number field appears directly below this pair when
                      it is chosen, and states where the prompt goes. */}
                  <div className={`payment-method-card co-choice${paymentMethod === 'cod' ? ' co-selected' : ''}`}
                    onClick={() => setPaymentMethod('cod')}
                    style={{ cursor: 'pointer' }}>
                    <span className={`co-radio${paymentMethod === 'cod' ? ' co-radio-on' : ''}`} aria-hidden="true" />
                    <h3 className="co-selected-title">Cash on Delivery</h3>
                    {paymentMethod === 'cod' && <span className="co-selected-chip"><IconCheckCircle size={13} /> {t('checkout.selected')}</span>}
                  </div>
                  <div className={`payment-method-card co-choice${paymentMethod === 'online' ? ' co-selected' : ''}`}
                    onClick={() => setPaymentMethod('online')}
                    style={{ cursor: 'pointer', marginTop: 10 }}>
                    <span className={`co-radio${paymentMethod === 'online' ? ' co-radio-on' : ''}`} aria-hidden="true" />
                    {/* Named from what the server reports rather than a
                        generic "card or mobile money": the customer needs to
                        know it is MTN before they discover their Airtel line
                        cannot approve the prompt. */}
                    <h3 className="co-selected-title">{onlineMethods[0]?.label || 'Online Payment'}</h3>
                    {paymentMethod === 'online' && <span className="co-selected-chip"><IconCheckCircle size={13} /> {t('checkout.selected')}</span>}
                  </div>
                  {paymentMethod === 'online' && (
                    <div className="payment-method-phone">
                      <label className="co-field-label" htmlFor="momo-phone">Mobile money number</label>
                      <input
                        id="momo-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        className="co-input"
                        placeholder="e.g. 0912 345 678"
                        value={momoPhone}
                        onChange={(e) => setMomoPhone(e.target.value)}
                      />
                      {/* The delivery number is often not the one holding the
                          mobile money wallet, so this defaults to it but stays
                          editable rather than assuming. */}
                      <p className="co-field-hint">
                        Defaults to your delivery number if left blank. The prompt goes to this number.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                // Cash on delivery is the only method, so this reads as a
                // confirmed selection rather than a chooser: the marker and the
                // "Selected" chip say which method is in force without
                // implying there is another one to pick.
                //
                // Three things and nothing else: the marker, the name of the
                // method, and the fact that it is the one in force.
                //
                // What stood here was a blurb ("pay with cash, have the amount
                // ready, no online payment needed") and, in the installed app,
                // three icon-and-label reassurances -- all of it restating a
                // heading that already reads "Cash on Delivery". The amount is
                // not lost with the blurb: it is on the Place Order button and
                // in the order summary directly below this card.
                //
                // Those extra children were also breaking the card outright in
                // the app, where the mobile layer places this by column
                // position -- see the .co-selected note in mobile/screens.css.
                // One markup for web, PWA and the installed app; there is no
                // isInstalledApp() branch left here for the three surfaces to
                // drift apart on.
                //
                // A JSX {/* */} comment cannot sit here, by the way: this is a
                // ternary branch, which takes one expression, and a comment
                // beside the <div> makes two.
                <div className="payment-method-card payment-method-cod-confirm co-selected">
                  <span className="co-radio co-radio-on" aria-hidden="true" />
                  <h3 className="co-selected-title">{t('checkout.codTitle')}</h3>
                  <span className="co-selected-chip"><IconCheckCircle size={13} /> {t('checkout.selected')}</span>
                </div>
              )}

              <div className="order-notes-section">
                <h3 className="co-field-label">
                  {t('checkout.orderNotesOptional')} <span className="co-optional">({t('checkout.optional')})</span>
                </h3>
                <textarea
                  className="order-notes-textarea"
                  rows={3}
                  placeholder={t('checkout.orderNotesPlaceholder')}
                  value={orderNotes}
                  onChange={e => setOrderNotes(e.target.value)}
                  maxLength={500}
                />
                {orderNotes.length > 0 && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'right', margin: '4px 0 0' }}>{orderNotes.length}/500</p>
                )}
              </div>

              {/* Coupon section */}
              <div className="coupon-section">
                <h3 className="co-field-label">{t('checkout.haveACoupon')}</h3>
                <div className="coupon-input-row">
                  <span className="coupon-ticket-icon" aria-hidden="true"><IconTicket size={20} /></span>
                  <input
                    type="text"
                    className="coupon-input"
                    placeholder={t('checkout.couponPlaceholder')}
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); setCouponError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                  />
                  <button className="btn coupon-apply-btn" onClick={applyCoupon}>{t('checkout.apply')}</button>
                </div>
                {couponError  && <div className="coupon-feedback coupon-error"><IconX size={12} /> {couponError}</div>}
                {couponResult && (
                  <div className="coupon-feedback coupon-success">
                    <IconCheck size={12} /> <strong>{couponResult.code}</strong> applied — {couponResult.type === 'percent' ? `${couponResult.value}% off` : `${formatCurrency(couponResult.value)} off`} (−{formatCurrency(couponResult.discount)})
                    <button className="coupon-remove" onClick={() => { setCouponResult(null); setCouponCode(''); }}><IconX size={12} /></button>
                  </div>
                )}
              </div>

              {rewards.points > 0 && (
                <div className="coupon-section rewards-redeem-section">
                  <h3 className="co-field-label">{t('checkout.rewardPointsTitle')}</h3>
                  {/* The balance is the reason to opt in, so it gets its own
                      card rather than being folded into the heading: points
                      and their cash value are the two numbers a shopper
                      actually weighs before ticking the box. */}
                  <div className="co-points-row">
                    <div className="co-points-card">
                      <span className="co-points-medal"><IconAward size={20} /></span>
                      <span className="co-points-figures">
                        <span className="co-points-count">{rewards.points.toLocaleString()} pts</span>
                        <span className="co-points-value">{t('checkout.pointsValue', { amount: formatCurrency(rewards.dollarValue) })}</span>
                      </span>
                    </div>
                    <div className="co-points-opt">
                      <label className="rewards-redeem-toggle">
                        <input
                          type="checkbox"
                          checked={usePoints}
                          onChange={(e) => { setUsePoints(e.target.checked); if (e.target.checked) setPointsToRedeem(maxRedeemablePoints); }}
                        />
                        {t('checkout.usePointsOnOrder')}
                      </label>
                      <p className="rewards-redeem-hint">{t('checkout.pointsCoverHalf')}</p>
                    </div>
                  </div>
                  {usePoints && (
                    <div className="rewards-redeem-controls">
                      <input
                        type="range"
                        min={0}
                        max={maxRedeemablePoints}
                        step={100}
                        value={Math.min(pointsToRedeem, maxRedeemablePoints)}
                        onChange={(e) => setPointsToRedeem(parseInt(e.target.value, 10))}
                      />
                      <span className="rewards-redeem-value">{pointsApplied} pts = −{formatCurrency(rewardsDiscountPreview)}</span>
                    </div>
                  )}
                </div>
              )}

              {wallet.balance > 0 && (
                <div className="coupon-section rewards-redeem-section">
                  <h3><IconGift size={16} /> Wallet Balance — you have {formatCurrency(wallet.balance)}</h3>
                  <label className="rewards-redeem-toggle">
                    <input
                      type="checkbox"
                      checked={useWallet}
                      onChange={(e) => { setUseWallet(e.target.checked); if (e.target.checked) setWalletAmountToRedeem(maxRedeemableWallet); }}
                    />
                    Use my wallet balance on this order
                  </label>
                  {useWallet && (
                    <div className="rewards-redeem-controls">
                      <input
                        type="range"
                        min={0}
                        max={maxRedeemableWallet}
                        step={0.01}
                        value={Math.min(walletAmountToRedeem, maxRedeemableWallet)}
                        onChange={(e) => setWalletAmountToRedeem(parseFloat(e.target.value))}
                      />
                      <span className="rewards-redeem-value">−{formatCurrency(walletApplied)}</span>
                    </div>
                  )}
                  <p className="rewards-redeem-hint">Store credit from refunded returns. It can cover up to your full order total.</p>
                </div>
              )}
            </div>

            {/* Right rail. The running total, the address it ships to and the
                commit button travel together and stay visible while the left
                column is being filled in, which is the whole point of the
                two-column step: the shopper can see what they are agreeing to
                without scrolling back up past the coupon and points controls. */}
            <div className="checkout-side">
              <div className="cod-order-recap co-panel">
                <h3 className="co-panel-title">{t('checkout.orderSummary')}</h3>
                <div className="price-row"><span>{t('checkout.itemsCount', { count: cartQuantity })}</span><span>{formatCurrency(cartTotal)}</span></div>
                {/* The one place the discount RATE is disclosed. Product cards
                    deliberately no longer show it, so this row has to say both
                    the rate and the money.

                    Every value here comes from the server: /api/coupons/validate
                    returns { type, value, discount } and the order is created
                    from the same coupon code server-side, so the rate shown and
                    the amount deducted can't disagree. Nothing about the total
                    is recomputed on the client. Percentage coupons name their
                    rate; fixed-amount coupons have no rate to name and just read
                    "Discount". Hidden entirely when no coupon applies. */}
                {couponResult && couponResult.discount > 0 && (
                  <div className="price-row discount-row">
                    <span>
                      {couponResult.type === 'percent'
                        ? t('checkout.discountPercent', { percent: couponResult.value })
                        : t('checkout.discountFlat')}
                    </span>
                    <span>−{formatCurrency(couponResult.discount)}</span>
                  </div>
                )}
                {pointsApplied > 0 && <div className="price-row discount-row"><span>{t('checkout.rewardPoints', { points: pointsApplied })}</span><span>−{formatCurrency(rewardsDiscountPreview)}</span></div>}
                {walletApplied > 0 && <div className="price-row discount-row"><span>{t('checkout.walletCredit')}</span><span>−{formatCurrency(walletApplied)}</span></div>}
                <div className="price-row">
                  <span>{deliveryType === 'express' ? <><IconRocket size={13} /> {t('checkout.expressDelivery')}</> : <><IconTruck size={13} /> {t('checkout.sellerDelivery')}</>}</span>
                  {effectiveFee3 > 0
                    ? <span style={{ color: ORANGE, fontWeight: 700 }}>{formatCurrency(effectiveFee3)}</span>
                    : <span className="free">{t('cart.free')}</span>}
                </div>
                {taxPreview3 > 0 && (
                  <div className="price-row"><span>{t('cart.taxWithRate', { rate: taxRatePct })}</span><span>{formatCurrency(taxPreview3)}</span></div>
                )}
                <hr />
                <div className="price-row total"><span>{paymentMethod === 'online' ? t('checkout.totalAmount') : t('checkout.amountPayableOnDelivery')}</span><span>{formatCurrency(finalTotal)}</span></div>
              </div>

              <div className="cod-address-recap">
                <div className="co-recap-head">
                  <h3><IconMapPin size={16} /> {t('checkout.deliveringTo')}</h3>
                  {/* Step 1 owns the address form, so "Change" is a jump back
                      rather than an edit-in-place: the shopper lands on the
                      screen that can actually change it. */}
                  <button type="button" className="co-change-btn" onClick={() => setCheckoutStep(1)}>
                    {t('checkout.change')}
                  </button>
                </div>
                <p><strong>{savedAddress?.fullName}</strong></p>
                <p>{savedAddress?.addressLine1}{savedAddress?.addressLine2 ? `, ${savedAddress.addressLine2}` : ''}{savedAddress?.area ? `, ${savedAddress.area}` : ''}</p>
                <p>{savedAddress?.city}, {savedAddress?.state} {savedAddress?.postalCode}</p>
                <p className="co-address-detail"><IconPhone size={14} /> <span>{savedAddress?.phone}</span></p>
                {savedAddress?.landmark && <p className="co-address-detail"><IconMapPin size={14} /> <span>{t('checkout.nearLabel')}: {savedAddress.landmark}</span></p>}
                {savedAddress?.deliveryInstructions && <p className="co-address-note"><IconClipboard size={14} /> <span>{savedAddress.deliveryInstructions}</span></p>}
                {zoneCheck && zoneCheck !== 'loading' && zoneCheck.hubConfigured && (
                  <p className={`zone-check-status ${zoneCheck.withinZone ? 'zone-ok' : 'zone-warn'}`}>
                    {zoneCheck.withinZone
                      ? <><IconCheckCircle size={15} /> {t('checkout.withinDeliveryZone')}</>
                      : <><IconAlertTriangle size={15} /> {t('checkout.outsideDeliveryZone', { distance: zoneCheck.distanceKm, radius: zoneCheck.radiusKm })}</>}
                  </p>
                )}
              </div>

              {orderMessage && (
                <div className={`alert ${orderRestricted ? 'danger' : 'info'}`} style={{ marginBottom: '15px' }}>
                  {orderRestricted && <strong style={{ display: 'block', marginBottom: 4 }}>{t('checkout.orderRestricted')}</strong>}
                  {orderMessage}
                </div>
              )}
              <div className="co-protection-note">
                <IconShieldCheck size={15} /> {t('checkout.protectedYourOrder')}
              </div>
              <button
                className="btn btn-place-order"
                onClick={handlePlaceOrder}
                disabled={placingOrder || orderRestricted || (zoneCheck && zoneCheck !== 'loading' && zoneCheck.withinZone === false)}
                aria-busy={placingOrder ? 'true' : 'false'}>
                {placingOrder
                  ? t('checkout.processingOrder')
                  : <>
                      {paymentMethod === 'online' ? <IconCreditCard size={16} /> : <IconShieldCheck size={16} />}
                      {paymentMethod === 'online' ? t('checkout.payAndPlaceOrder') : t('checkout.placeOrder')}
                      {' · '}{formatCurrency(finalTotal)}
                    </>}
              </button>
              <p className="co-under-cta">{t('checkout.payCourierOnArrival')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Checkout — order success ─────────────────────────────────
  if (checkoutStep === 'success' && placedOrder) {
    const order = placedOrder;
    const dismiss = () => { setCheckoutStep(null); setPlacedOrder(null); setSuccessRecs([]); };
    const orderDate = new Date(order.createdAt || Date.now());
    const fmtTime = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

    const isCod = (placedOrder?.paymentMethod || order?.paymentMethod || 'cod') !== 'online';
    // For cash on delivery the courier collects payment at the door, so
    // "Payment Confirmed" belongs at the END of the journey -- only true once
    // the delivery is confirmed -- not up front. An online payment (if ever
    // enabled) is captured before fulfilment, so it keeps the leading position.
    const SUCCESS_STEPS = isCod
      ? [
          { key: 'received',   label: 'Order Received',    Icon: IconCheck },
          { key: 'processing', label: 'Seller Processing', Icon: IconPackage },
          { key: 'shipped',    label: 'Out for Delivery',  Icon: IconTruck },
          { key: 'delivered',  label: 'Delivered',         Icon: IconHome },
          { key: 'paid',       label: 'Payment Confirmed', Icon: IconCheckCircle },
        ]
      : [
          { key: 'received',   label: 'Order Received',    Icon: IconCheck },
          { key: 'confirmed',  label: 'Payment Confirmed', Icon: IconCreditCard },
          { key: 'processing', label: 'Seller Processing', Icon: IconPackage },
          { key: 'shipped',    label: 'Out for Delivery',  Icon: IconTruck },
          { key: 'delivered',  label: 'Delivered',         Icon: IconHome },
        ];
    // Maps the order's real status to the active step index above. Right after
    // checkout the status is 'received', which shows "Seller Processing" as the
    // in-progress step. For COD, a delivered order means the cash was collected
    // too, so it lands on the final "Payment Confirmed" step.
    const REAL_STATUS_TO_STEP = isCod
      ? { received: 1, processing: 1, shipped: 2, out_for_delivery: 2, delivered: 4 }
      : { received: 2, processing: 2, shipped: 3, out_for_delivery: 3, delivered: 4 };
    // An online order that has not settled yet must not show "Payment
    // Confirmed" as a completed step. The map above assumes it is, because
    // until now an online order could only exist as a label -- with a real
    // provider the customer is still holding their phone at this point, so the
    // tracker is held at "Order Received" until the poll says otherwise.
    const onlineUnsettled = !isCod && pendingPayment?.status !== 'paid';
    const statusIdx = onlineUnsettled
      ? 0
      : REAL_STATUS_TO_STEP[order.status] ?? (isCod ? 1 : 2);



    return (
      <div className={shellClassName}>
        <SubPageHeader title="Order Confirmed" user={user} darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} onBack={() => { dismiss(); navigate('/'); }} backLabel="Back to Store" mobileShowName hideThemeOnMobile />
        <div className="checkout-step-pad">
          <div className="order-success-wrap">

            <div className="order-success-hero">
              <div className="order-success-confetti">
                {Array.from({ length: 14 }, (_, i) => <span key={i} className={`confetti-piece confetti-${i % 6}`} style={{ '--i': i }} />)}
              </div>
              <div className="order-success-icon"><IconCheck size={30} /></div>
            </div>
            <h1 className="order-success-title">{t('orderSuccess.title')}</h1>
            <p className="order-success-sub">
              Thank you{order.customer?.name ? `, ${order.customer.name.split(' ')[0]}` : ''}! We've received your order and will start processing it right away.
            </p>
            {/* An online payment is NOT settled at this moment -- the customer
                still has to approve a prompt on their handset, and only the
                server can say whether that succeeded. Claiming "your payment is
                confirmed" here, as this did, would be telling them money had
                moved when it had not. The live state comes from the status
                poll; `paid` is the only branch allowed to say so. */}
            <div className={`order-success-trust${successNoticeHidden ? ' order-success-trust-hidden' : ''}`} role="status">
              <span><IconCheckCircle size={14} /></span>{' '}
              {isCod
                ? <>Your order is confirmed and we'll start processing it right away.</>
                : pendingPayment?.status === 'paid'
                  ? <>Your payment is confirmed and your order is secured.</>
                  : pendingPayment?.status === 'failed'
                    ? <>Your order is saved, but the payment didn't go through. You can pay again from your order history.</>
                    : pendingPayment?.status === 'timeout'
                      ? <>We haven't seen that payment yet. If you approved it, it may still be clearing — check your order history in a few minutes.</>
                      : <>{pendingPayment?.message || 'Check your phone and approve the payment prompt to complete this order.'}</>}
            </div>

            {/* Progress tracker */}
            <div className="order-success-tracker" id="order-success-stepper">
              {SUCCESS_STEPS.map((step, i) => {
                const isDone = i < statusIdx;
                const isActive = i === statusIdx;
                const isLast = i === SUCCESS_STEPS.length - 1;
                const StepIcon = step.Icon;
                return (
                  <Fragment key={step.key}>
                    <div className="ost-step">
                      <div className={`ost-dot${isDone ? ' ost-dot-done' : ''}${isActive ? ' ost-dot-active' : ''}`}>
                        {isDone ? <IconCheck size={14} /> : <StepIcon size={14} />}
                      </div>
                      <p className={`ost-label${isDone ? ' ost-label-done' : ''}${isActive ? ' ost-label-active' : ''}`}>{step.label}</p>
                      {isActive ? (
                        <span className="ost-pill-active"><IconClock size={12} /> In Progress</span>
                      ) : (
                        <p className="ost-sub">{isDone ? fmtTime(orderDate) : 'Pending'}</p>
                      )}
                    </div>
                    {!isLast && <div className={`ost-line${isDone ? ' ost-line-done' : ''}`} />}
                  </Fragment>
                );
              })}
            </div>

            {/* The four stat cards that were here -- Order ID, Payment
                Method, Est. Delivery, Items -- are gone. Every one of them
                is on the order's own row under My Orders, which is now the
                single place order detail lives; repeating them on a screen
                the customer sees once meant two places to keep in step. */}

            {/* Items */}
            <div className="order-success-items">
              <h3>{t('orderSuccess.itemsInOrder', { count: order.items?.length || 0 })}</h3>
              {(order.items || []).map((item, i) => (
                <div key={i} className="order-success-item-row">
                  <img src={mediaUrl(item.image) || PRODUCT_IMAGE_PLACEHOLDER} alt={item.name} onError={fallbackToPlaceholder} />
                  <div className="order-success-item-info">
                    <p className="order-success-item-name">{item.name}</p>
                    <p className="order-success-item-meta">Seller: <span className="osi-seller">{item.sellerName || 'AcyMart'}</span></p>
                    <span className="osi-qty-pill">Qty: {item.quantity}</span>
                  </div>
                  <span className="order-success-item-total">{formatCurrency(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Delivering to / Order summary */}
            <div className="order-success-cols">
              <div className="order-success-address">
                <h3><IconMapPin size={16} /> {t('orderSuccess.deliveringTo')}</h3>
                <div className="osa-body">
                  <div className="osa-info">
                    <p><strong>{order.customer?.name}</strong></p>
                    {order.customer?.phone && <p>{order.customer.phone}</p>}
                    <p>{order.customer?.address}</p>
                    <button className="btn-link" onClick={() => { dismiss(); navigateTo('address'); }}><IconEdit size={12} /> {t('orderSuccess.changeAddress')}</button>
                  </div>
                  <div className="order-success-map"><IconMap size={22} /><span className="order-success-map-pin"><IconMapPin size={14} /></span></div>
                </div>
              </div>
              <div className="order-success-summary">
                <h3>{t('checkout.orderSummary')}</h3>
                <div className="order-success-row"><span>{t('cart.subtotal')}</span><strong>{formatCurrency(order.subtotal)}</strong></div>
                <div className="order-success-row"><span>{t('orderSuccess.deliveryFee')}</span><strong className={order.shippingFee ? '' : 'order-success-free'}>{order.shippingFee ? formatCurrency(order.shippingFee) : t('cart.free')}</strong></div>
                {order.discount > 0 && <div className="order-success-row"><span>{order.coupon ? t('checkout.discountWithCode', { code: order.coupon.code }) : t('common.discount')}</span><strong>−{formatCurrency(order.discount)}</strong></div>}
                {order.rewardsDiscount > 0 && <div className="order-success-row"><span>{t('orderSuccess.rewardPoints')}</span><strong>−{formatCurrency(order.rewardsDiscount)}</strong></div>}
                {order.walletCredit > 0 && <div className="order-success-row"><span>{t('checkout.walletCredit')}</span><strong>−{formatCurrency(order.walletCredit)}</strong></div>}
                {order.tax > 0 && <div className="order-success-row"><span>{t('orderSuccess.tax')}</span><strong>{formatCurrency(order.tax)}</strong></div>}
                <hr />
                <div className="order-success-row total"><span>{t('orderSuccess.totalPaid')}</span><strong>{formatCurrency(order.total)}</strong></div>
              </div>
            </div>

            {/* Where to follow this up. The block that was here carried a
                "What's Next?" heading, a paragraph restating that the seller
                is preparing the order, and three mini-tiles (Email Updates /
                Delivery Confirmation / Reward Points) that were labels rather
                than links -- none of them did anything. One sentence pointing
                at the place the status actually lives is more use than four
                elements describing it. */}
            <p className="order-success-followup">
              Track this order any time under{' '}
              <button type="button" className="btn-link" onClick={() => { dismiss(); navigateTo('orders'); }}>
                My Orders
              </button>{' '}
              in your account, where you'll see its status as it changes.
            </p>

            {/* Track Order / Download Invoice / View My Orders / Continue
                Shopping have moved to the order's row under My Orders, so
                there is one place to act on an order rather than two that
                have to agree. The follow-up line above links there, and the
                bottom nav's Home tab is the way back to the store. */}

            {/* You may also like */}
            {successRecs.length > 0 && (
              <div className="order-success-recs">
                <div className="osr-head"><h3>You May Also Like</h3></div>
                <div className="osr-grid">
                  {successRecs.map(p => (
                    <div key={p.id} className="osr-card" onClick={() => { dismiss(); navigate(productPath(p)); }}>
                      <button className="osr-heart" onClick={e => { e.stopPropagation(); addToWishlist(p.id); }} aria-label="Add to wishlist"><IconHeart size={13} /></button>
                      <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" onError={fallbackToPlaceholder} />
                      <p className="osr-name">{p.name}</p>
                      <div className="osr-price-row">
                        <span className="osr-price">{formatCurrency(p.price)}</span>
                        {p.avgRating > 0 && <span className="osr-rating"><IconStar size={11} /> {p.avgRating.toFixed(1)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${shellClassName} app-shell-with-bottom-nav`}>
      <div className="site-header">
        {/* ── Announcement bar ─────────────────────────────────── */}
        {/* The packaged app gets a card-style banner instead of the web's
            single run-on line: on a 390px screen that line wrapped to two
            rows of small centred text with no hierarchy, so the discount and
            the delivery offer read as one sentence. Two labelled blocks
            either side of a rule separate them, and the app is the only
            surface that changes -- the website keeps its strip. */}
        <div className="ann-bar">
          {/* Every claim in this bar is now backed by data. It used to read
              "Flash Sale — 30% Off Everything · Free Delivery on Orders $50+"
              with a self-renewing countdown; there was no store-wide discount
              and no free-delivery threshold. Deals and their countdown now
              come from real product sales, and with none live the bar says
              something true instead: cash on delivery is accepted on every
              order (routes/ordersNew.js falls back to it unconditionally). */}
          {isInstalledApp() ? (
            <div className="ann-app-card">
              <span className="ann-app-badge" aria-hidden="true">
                {deals.dealCount > 0 ? <IconTag size={17} /> : <IconTruck size={17} />}
              </span>
              {deals.dealCount > 0 ? (
                <div className="ann-app-block">
                  <span className="ann-app-kicker"><IconZap size={11} /> Deals</span>
                  <span className="ann-app-headline">{deals.dealCount} on now</span>
                  {dealsRemaining && (
                    <span className="ann-app-timer">
                      Next ends in <span className="ann-countdown">{dealsRemaining}</span>
                    </span>
                  )}
                </div>
              ) : (
                <div className="ann-app-block">
                  <span className="ann-app-kicker"><IconTruck size={11} /> Delivery</span>
                  <span className="ann-app-headline">Priced by area</span>
                </div>
              )}
              <span className="ann-app-rule" aria-hidden="true" />
              <div className="ann-app-block ann-app-block-end">
                <span className="ann-app-kicker">Pay on delivery</span>
                <span className="ann-app-sub">Cash accepted</span>
              </div>
            </div>
          ) : (
            <div className="ann-bar-inner">
              <p className="ann-text">
                {deals.dealCount > 0 ? (
                  <>
                    <IconZap size={13} /> {deals.dealCount === 1 ? '1 deal on now' : `${deals.dealCount} deals on now`}
                    {dealsRemaining && <> · Next ends in <span className="ann-countdown">{dealsRemaining}</span></>}
                    {' '}· Cash on Delivery available
                  </>
                ) : (
                  <><IconTruck size={13} /> Cash on Delivery available on every order</>
                )}
              </p>
              <div className="ann-right">
                {!user && (
                  <button className="ann-auth-btn" onClick={() => setShowAuthModal('login')}>
                    <IconUser size={13} /> Sign In / Register
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Main navbar ──────────────────────────────────────── */}
        <header className="main-navbar">

          {/* Row 1: Logo + Search + Actions */}
          <div className="nb-top">

            {/* Logo. Long-pressing it in the packaged app opens the API
                connection screen -- the app hard-codes a server address at
                build time, and that has to be changeable on the device when
                the laptop running the server changes network. Hidden behind a
                long press rather than given a nav entry: shoppers have no use
                for it, and it must stay reachable from the storefront shell,
                which is all that renders when the address is wrong. */}
            {!showSearch && (
              <button
                className="nb-logo"
                onClick={() => {
                  // Suppressed when the press already opened the dev screen,
                  // so the app doesn't also navigate out from under it.
                  if (logoLongPressRef.current) { logoLongPressRef.current = false; return; }
                  navigate('/'); setSearchTerm(''); setSelectedCategory('All');
                }}
                onPointerDown={beginLogoPress}
                onPointerUp={endLogoPress}
                onPointerLeave={endLogoPress}
                onPointerCancel={endLogoPress}
                onContextMenu={(e) => { if (canOverrideApiBase()) e.preventDefault(); }}
              >
                <LogoIcon size={38} />
                <span className="nb-logo-text-block">
                  <span className="nb-logo-name">Acy<span className="logo-store-text">Mart</span></span>
                  <span className="nb-logo-tagline">Discover More. Shop Smarter.</span>
                </span>
              </button>
            )}

            {/* Persistent search bar (icon-only on mobile until tapped open) */}
            <div className="nb-search-wrap">
            <div className={`nb-search-bar${showSearch ? ' nb-search-expanded' : ''}`}>
              {showSearch && (
                <button type="button" className="nb-sb-back" onClick={() => setShowSearch(false)} aria-label="Close search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                  </svg>
                </button>
              )}
              <svg className="nb-sb-search-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchInputRef}
                type="search"
                className="nb-sb-input"
                placeholder={t('nav.searchPlaceholder')}
                aria-label={t('nav.searchAriaLabel')}
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setShowSuggestions(true); if (location.pathname !== '/') navigate('/'); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {searchTerm && <button className="nb-sb-clear" onClick={() => setSearchTerm('')} aria-label={t('nav.clearSearch')}><IconX size={12} /></button>}
              <div className="nb-sb-divider" />
              <select
                className="nb-sb-cat"
                value={selectedCategory}
                onChange={e => { setSelectedCategory(e.target.value); if (location.pathname !== '/') navigate('/'); }}
              >
                {categories.map(c => <option key={c} value={c}>{c === 'All' ? t('categories.allCategories') : translateCategory(c, t)}</option>)}
              </select>
              <svg className="nb-sb-caret" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6"/>
              </svg>
              <button className="nb-sb-btn" aria-label="Search">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            </div>

            {/* Search autocomplete */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="nb-autocomplete">
                {searchSuggestions.map(s => (
                  <button key={s.id} className="nb-ac-item" onMouseDown={() => { navigate(productPath(s)); setShowSuggestions(false); setSearchTerm(''); }}>
                    <img src={mediaUrl(s.image)} alt={s.name} className="nb-ac-img" onError={fallbackToPlaceholder} />
                    <div className="nb-ac-info">
                      <span className="nb-ac-name">{s.name}</span>
                      <span className="nb-ac-cat">{s.category}</span>
                    </div>
                    <span className="nb-ac-price">{formatCurrency(s.price)}</span>
                  </button>
                ))}
              </div>
            )}
            </div>{/* end nb-search-wrap */}

            {/* Mobile-only icon that expands the search bar above */}
            {!showSearch && (
              <button
                type="button"
                className="nb-mobile-search-btn"
                aria-label="Search"
                onClick={() => { setShowSearch(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            )}

            {/* Right actions */}
            {!showSearch && <div className="nb-actions">

              <LanguageSwitcher />

              {/* Account */}
              {user ? (
                <div
                  className="user-dropdown-container"
                  onMouseEnter={() => { setShowUserDropdown(true); setShowMoreDropdown(false); }}
                  onMouseLeave={() => setShowUserDropdown(false)}
                  onKeyDown={e => { if (e.key === 'Escape') setShowUserDropdown(false); }}
                >
                  <button
                    className="nb-action-btn user-dropdown-toggle"
                    aria-label="Account"
                    aria-haspopup="menu"
                    aria-expanded={showUserDropdown}
                    onFocus={() => setShowUserDropdown(true)}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <div className="nb-action-text">
                      <span className="nb-action-label">{t('nav.account')}</span>
                      <span className="nb-action-sub">{t('nav.helloName', { name: user.name.split(' ')[0] })}</span>
                    </div>
                  </button>
                  {showUserDropdown && (
                    <div className="user-dropdown-menu">
                      <button className="dropdown-item" onClick={() => { navigateTo('dashboard'); setShowUserDropdown(false); }}><IconBarChart size={14} /> {t('nav.myDashboard')}</button>
                      <button className="dropdown-item" onClick={() => { navigateTo('profile'); setShowUserDropdown(false); }}><IconUser size={14} /> {t('nav.myProfile')}</button>
                      {isSeller && (
                        <button className="dropdown-item" style={{ color: NAVY, fontWeight: 600 }} onClick={() => { navigateTo('seller-portal'); setShowUserDropdown(false); }}><IconStore size={14} /> {t('nav.sellerPortal')}</button>
                      )}
                      <button className="dropdown-item" onClick={() => { navigateTo('orders'); setShowUserDropdown(false); }}><IconPackage size={14} /> {t('nav.myOrders')}</button>
                      <button className="dropdown-item dropdown-item-sub" onClick={() => { navigateTo('track-order'); setShowUserDropdown(false); }}><IconTruck size={14} /> {t('nav.trackOrder')}</button>
                      <button className="dropdown-item" onClick={() => { navigateTo('wishlist'); setShowUserDropdown(false); }}><IconHeart size={14} /> {t('nav.wishlist')}</button>
                      <button className="dropdown-item" onClick={() => { navigateTo('address'); setShowUserDropdown(false); }}><IconMapPin size={14} /> {t('nav.myAddress')}</button>
                      <button className="dropdown-item" onClick={() => { navigateTo('settings'); setShowUserDropdown(false); }}><IconSettings size={14} /> {t('nav.settings')}</button>
                      <button className="dropdown-item" onClick={() => { navigateTo('notifications'); setShowUserDropdown(false); }}>
                        <IconBell size={14} /> {t('nav.notifications')}
                        {unreadNotifCount > 0 && <span className="dropdown-item-badge">{unreadNotifCount > 9 ? '9+' : unreadNotifCount}</span>}
                      </button>
                      <hr className="dropdown-divider" />
                      <button className="dropdown-item logout-item" onClick={handleLogout}><IconLogOut size={14} /> {t('nav.logout')}</button>
                    </div>
                  )}
                </div>
              ) : (
                <button className="nb-action-btn" onClick={() => setShowAuthModal('login')} aria-label={t('auth.signIn')}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <div className="nb-action-text">
                    <span className="nb-action-label">{t('nav.helloGuest')}</span>
                    <span className="nb-action-sub">{t('auth.signIn')}</span>
                  </div>
                </button>
              )}

              {/* Wishlist */}
              {user && (
                <button className="nb-action-btn" onClick={() => navigateTo('wishlist')} aria-label="Wishlist">
                  <div className="nb-action-icon-wrap">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    {wishlistCount > 0 && <span className="nb-badge nb-badge-count">{wishlistCount}</span>}
                  </div>
                  <div className="nb-action-text">
                    <span className="nb-action-label">Saved</span>
                    <span className="nb-action-sub">Wishlist</span>
                  </div>
                </button>
              )}

              {/* Cart */}
              <button className="nb-action-btn" onClick={openCartPanel} aria-label="Cart">
                <div className="nb-action-icon-wrap">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                  </svg>
                  {cartQuantity > 0 && <span className="nb-badge">{cartQuantity}</span>}
                </div>
                <div className="nb-action-text">
                  <span className="nb-action-label">Cart</span>
                  <span className="nb-action-sub">{cartQuantity > 0 ? formatCurrency(cartTotal) : 'Empty'}</span>
                </div>
              </button>

              {/* Dark mode */}
              <button className="nb-theme-btn" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle theme">
                {darkMode ? <IconSun size={16} /> : <IconMoon size={16} />}
              </button>
            </div>}
          </div>

          {/* Row 2: Category nav */}
          <div className="nb-cat-row">
            <div className="nb-cat-row-inner">
              <button
                className={`nb-all-cats-btn${selectedCategory === 'All' && !dealsOnly ? ' active' : ''}`}
                onClick={() => { setSelectedCategory('All'); if (location.pathname !== '/') navigate('/'); }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
                {t('categories.allCategories')}
              </button>

              <Link
                to="/deals"
                className={`nb-deals-link${dealsOnly ? ' active' : ''}`}
                onClick={() => setSelectedCategory('All')}
              >
                <IconFlame size={14} /> {t('categories.deals')}
              </Link>

              {/* Anchors, not buttons: this is the site's primary navigation,
                  and as <button onClick> it gave crawlers no path from the
                  homepage to any category page at all. */}
              <nav className="nb-cat-nav">
                {categories.filter(c => c !== 'All').map(cat => (
                  <Link
                    key={cat}
                    to={`/category/${encodeURIComponent(cat)}`}
                    className={`nb-cat-link${selectedCategory === cat && !dealsOnly ? ' active' : ''}`}
                  >
                    <span className="nb-cat-icon">{(() => { const CatIcon = getCategoryIcon(cat); return <CatIcon size={15} />; })()}</span>
                    {translateCategory(cat, t)}
                  </Link>
                ))}
              </nav>

              {/* More dropdown */}
              <div
                className="nb-more-container"
                onMouseEnter={() => { setShowMoreDropdown(true); setShowUserDropdown(false); }}
                onMouseLeave={() => setShowMoreDropdown(false)}
                onKeyDown={e => { if (e.key === 'Escape') setShowMoreDropdown(false); }}
              >
                <button
                  className={`nb-more-btn${showMoreDropdown ? ' open' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={showMoreDropdown}
                  onFocus={() => setShowMoreDropdown(true)}
                >
                  More
                  <svg className="nb-more-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    {showMoreDropdown ? <path d="M18 15l-6-6-6 6"/> : <path d="M6 9l6 6 6-6"/>}
                  </svg>
                </button>
                {showMoreDropdown && (
                  <div className="nb-more-menu">
                    <div className="nb-more-heading">More</div>
                    <button className="nb-more-item" onClick={() => { navigate('/become-a-seller'); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                      </span>
                      <span>Become a Seller</span>
                    </button>
                    <button className="nb-more-item" onClick={() => { navigate('/track'); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                      </span>
                      <span>Track Order</span>
                    </button>
                    <button className="nb-more-item" onClick={() => { setTrackingId(''); navigate('/track-application'); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                      </span>
                      <span>Track Application</span>
                    </button>
                    <button className="nb-more-item" onClick={() => { navigate('/account/notifications'); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                          {unreadNotifCount > 0 && <circle cx="19" cy="5" r="3" fill={ORANGE} stroke="none"/>}
                        </svg>
                      </span>
                      <span>Notifications</span>
                    </button>
                    <button className="nb-more-item" onClick={() => { onOpenChat(); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                        </svg>
                      </span>
                      <span>24x7 Customer Care</span>
                    </button>
                    <button className="nb-more-item" onClick={() => { navigate('/contact'); setShowMoreDropdown(false); }}>
                      <span className="nb-more-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                        </svg>
                      </span>
                      <span>Advertise on AcyMart</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

        </header>
      </div>

      <MobileTopBar
        savedAddress={savedAddress}
        onOpenAddress={() => (user ? navigateTo('address') : setShowAuthModal('login'))}
        categories={categories}
        selectedCategory={selectedCategory}
        dealsOnly={dealsOnly}
        onSelectCategory={(cat) => navigate(`/category/${encodeURIComponent(cat)}`)}
        onSelectDeals={() => { setSelectedCategory('All'); navigate('/deals'); }}
        recentlyViewedProducts={recentlyViewedProducts}
        userName={user?.name?.split(' ')[0]}
        onProductClick={(id) => navigate(`/product/${id}`)}
      />

      {renderAuthModal()}

      {(user?.role === 'seller' || user?.role === 'seller_suspended') && (
        <div style={{ background:NAVY, color:'var(--color-brand-white)', padding:'10px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.88rem' }}>
            <IconStore size={13} /> You have an active <strong>Seller Account</strong>. Manage your products, orders and earnings in the Seller Portal.
          </span>
          <button
            onClick={() => navigate('/seller')}
            style={{ background:'var(--color-brand-white)', color:NAVY, border:'none', borderRadius:8, padding:'6px 16px', fontWeight:700, fontSize:'0.83rem', cursor:'pointer', whiteSpace:'nowrap' }}
          >
            Go to Seller Portal <IconArrowRight size={12} />
          </button>
        </div>
      )}

      {/* Hero/Top Sales/Recommendations/Recently-Viewed are homepage-only
          modules -- /deals has its own hero + "Top Deals" header, and
          /category/:name has its own hero + "Top Sales in X" row (both
          below), so none of this belongs on those pages. */}
      {page === 'home' && (<>
      <HeroCarousel
        onBannerClick={(banner) => {
          if (banner.linkType === 'product' && banner.linkValue) {
            navigate(`/product/${banner.linkValue}`);
          } else if (banner.linkType === 'category' && banner.linkValue) {
            navigate(`/category/${encodeURIComponent(banner.linkValue)}`);
          }
        }}
      />

      {/* ── Top Sales: one carousel spanning every category (deals first,
          then rating) -- replaces the old separate "Flash Sales & Featured"
          strip and the one-row-per-category "Shop by Category" section with
          the single row the homepage flow calls for. ── */}
      <CategoryRow
        label="Top Sales"
        Icon={IconFlame}
        items={topSalesItems}
        onProductClick={(id) => navigate(`/product/${id}`)}
        onViewAll={() => { setSelectedCategory('All'); navigate('/deals'); }}
      />

      {/* ── Mobile home feed blocks ────────────────────────────────────────
          Three Flipkart-shaped sections that exist only on the phone: the
          desktop homepage already has the width for its carousels and does
          not need them. Gated on isMobileUI() rather than a media query
          because these are extra DOM, not a restyle -- rendering them on
          desktop and hiding them in CSS would ship markup nobody sees. ── */}
      {isMobileUI() && (<>
        <GreatFindsSection products={products} formatCurrency={formatCurrency} />

        <TileSection
          title="Gadgets and appliances"
          tone="a"
          items={buildTiles(products, {
            categories: ['Electronics', 'Accessories'],
            // The claim is derived from the product's own pricing, so it is
            // always true. Anything without a real saving says "Special
            // offer" rather than inventing a percentage.
            // The exact saving on THIS product, from the same maths the
            // product card uses -- not a rounded-down "Min. X%" claim about
            // a group, which is a different (and unverified) statement.
            claimFor: (p) => {
              const pct = exactDiscountPct(p);
              return pct ? `${pct}% Off` : 'Special offer';
            },
          })}
          onSeeAll={() => { setSelectedCategory('Electronics'); navigate('/category/Electronics'); }}
          seeAllLabel="See all electronics"
        />

        <TileSection
          title="Hair & Skincare Essentials"
          tone="b"
          items={buildTiles(products, {
            categories: ['Beauty'],
            // The exact saving on THIS product, from the same maths the
            // product card uses -- not a rounded-down "Min. X%" claim about
            // a group, which is a different (and unverified) statement.
            claimFor: (p) => {
              const pct = exactDiscountPct(p);
              return pct ? `${pct}% Off` : 'Special offer';
            },
          })}
          onSeeAll={() => { setSelectedCategory('Beauty'); navigate('/category/Beauty'); }}
          seeAllLabel="See all beauty"
        />
      </>)}

      {/* ── For You: purchase-history recommendations (logged-in, has history) ── */}
      {recommendations && ((recommendations.deals?.length > 0) || (recommendations.recommendations?.length > 0)) && (() => {
        const catLabel = recommendations.topCategory ? translateCategory(recommendations.topCategory, t) : '';
        const strips = [
          { key: 'deals', Icon: IconFlame, title: t('home.bestDealsIn', { category: catLabel }), items: recommendations.deals || [] },
          { key: 'recs',  Icon: IconSparkle, title: recommendations.topCategory ? t('home.becauseYouShop', { category: catLabel }) : t('home.recommendedForYou'), items: recommendations.recommendations || [] },
        ];
        // CategoryRow, the same component the Top Sales row uses, rather than
        // the hand-rolled strip these had: one carousel with arrows, dots,
        // pause-on-hover and the in-view auto-advance, one card with the badge,
        // rating pill and strikethrough. Two shapes of product row on one
        // homepage read as two different parts of the site.
        return strips.map(strip => (
          <CategoryRow
            key={strip.key}
            label={strip.title}
            Icon={strip.Icon}
            items={strip.items}
            onProductClick={(id) => navigate(`/product/${id}`)}
            // Only where there is somewhere true to send them: these rows are
            // built from one category, so "View all" means that category.
            onViewAll={recommendations.topCategory
              ? () => { setSelectedCategory(recommendations.topCategory); navigate(`/category/${encodeURIComponent(recommendations.topCategory)}`); }
              : undefined}
          />
        ));
      })()}

      {/* ── Recently Viewed ──
          Also CategoryRow now. It was the last hand-rolled strip on the page:
          its own 140px card, its own hover lift, no rating, no strikethrough --
          so the same product looked like a different product depending on which
          row it appeared in.
          No "View all": there is no page that lists what you have looked at, and
          a link that goes nowhere true is worse than no link. */}
      {recentlyViewedProducts.length > 0 && (
        <CategoryRow
          label={t('home.recentlyViewed', { defaultValue: 'Recently Viewed' })}
          Icon={IconClock}
          items={recentlyViewedProducts}
          onProductClick={(id) => navigate(`/product/${id}`)}
        />
      )}
      </>)}

      {/* ── Category landing page: its own hero banners (admin-targeted to this
          category, see the Banners admin tab's "Show On" field) plus one
          "Top Sales in X" row (same deals-first-then-rating ranking as the
          homepage's Top Sales row, just scoped to this category). Falls
          through to the same filterable grid below, pre-filtered to this
          category via the sync effect above -- so the grid's own header
          already reads the category name instead of "All Products". ── */}
      {page === 'category' && categorySlug && (() => {
        const catLabel = translateCategory(categorySlug, t);
        // Same rule as the homepage row, scoped to this category.
        const catTopSales = rankTopSales(
          products.filter(p => p.category === categorySlug),
          topSalesRules
        );
        return (
          <>
            <HeroCarousel
              category={categorySlug}
              onBannerClick={(banner) => {
                if (banner.linkType === 'product' && banner.linkValue) {
                  navigate(`/product/${banner.linkValue}`);
                } else if (banner.linkType === 'category' && banner.linkValue) {
                  navigate(`/category/${encodeURIComponent(banner.linkValue)}`);
                }
              }}
            />
            <CategoryRow label={`Top Sales in ${catLabel}`} Icon={IconFlame} items={catTopSales} onProductClick={(id) => navigate(`/product/${id}`)} />
          </>
        );
      })()}

      {/* Deals gets its own hero carousel too (admin-targeted banners with
          category "Deals"), same pattern as the category pages above -- the
          "Top Deals" heading right below is the grid's own header (see
          shop-hdr-title), not a separate curated row, since every item in
          this grid is already a deal. */}
      {dealsOnly && (
        <HeroCarousel
          category="Deals"
          onBannerClick={(banner) => {
            if (banner.linkType === 'product' && banner.linkValue) {
              navigate(`/product/${banner.linkValue}`);
            } else if (banner.linkType === 'category' && banner.linkValue) {
              navigate(`/category/${encodeURIComponent(banner.linkValue)}`);
            }
          }}
        />
      )}

      {/* The full filterable grid -- shown for the general "All Products"
          view, /deals, and now every category page too (pre-filtered to
          that category via the selectedCategory sync effect above). */}
      <div className="shop-layout">
        {/* ── LEFT: Filter Sidebar ── */}
        <aside className="shop-filters">
        <div className="sf-card">
          <div className="sf-header">
            <span className="sf-title">{t('filters.title')}</span>
            {(selectedCategory !== 'All' || priceRange[0] > 0 || priceRange[1] > 0 || minRating > 0 || inStockOnly) && (
              <button className="sf-clear-all" onClick={() => { setSelectedCategory('All'); setPriceRange([0,0]); setMinRating(0); setInStockOnly(false); }}>{t('filters.clearAll')}</button>
            )}
          </div>

          {/* Categories */}
          <div className="sf-section">
            <div className="sf-section-hd">
              <span>{t('filters.categories')}</span>
              <span className="sf-chevron">▾</span>
            </div>
            <div className="sf-list">
              {categories.filter(c => c !== 'All').map(cat => (
                <label key={cat} className="sf-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedCategory === cat}
                    onChange={() => setSelectedCategory(selectedCategory === cat ? 'All' : cat)}
                  />
                  <span className="sf-cat-name">{translateCategory(cat, t)}</span>
                  <span className="sf-count">{products.filter(p => p.category === cat).length}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div className="sf-section">
            <div className="sf-section-hd">
              <span>{t('filters.priceRange')}</span>
              <span className="sf-chevron">▾</span>
            </div>
            <div className="sf-price-wrap">
              <input
                type="range"
                min={0}
                max={priceMax}
                value={priceRange[1] || priceMax}
                className="sf-slider"
                onChange={e => setPriceRange([priceRange[0], Number(e.target.value)])}
              />
              <div className="sf-price-labels">
                <span>{formatCurrency(priceRange[0] || 0)}</span>
                <span>{formatCurrency(priceRange[1] || priceMax)}+</span>
              </div>
              <div className="sf-price-inputs">
                <input type="number" min="0" placeholder={t('filters.min')} className="sf-price-input"
                  value={priceRange[0] || ''}
                  onChange={e => setPriceRange([Number(e.target.value)||0, priceRange[1]])} />
                <span className="sf-price-sep">–</span>
                <input type="number" min="0" placeholder={t('filters.max')} className="sf-price-input"
                  value={priceRange[1] || ''}
                  onChange={e => setPriceRange([priceRange[0], Number(e.target.value)||0])} />
              </div>
            </div>
          </div>

          {/* Customer Rating */}
          <div className="sf-section">
            <div className="sf-section-hd">
              <span>{t('filters.customerRating')}</span>
              <span className="sf-chevron">▾</span>
            </div>
            <div className="sf-list">
              {[4, 3, 2, 1].map(r => (
                <label key={r} className="sf-checkbox-row">
                  <input
                    type="checkbox"
                    checked={minRating === r}
                    onChange={() => setMinRating(minRating === r ? 0 : r)}
                  />
                  <span className="sf-stars-row">
                    {Array.from({length:5}, (_,i) => (
                      <IconStar key={i} size={12} className={i < r ? 'rating-star-filled' : 'rating-star-empty'} />
                    ))}
                  </span>
                  <span className="sf-star-label">{t('filters.andUp')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* In-stock toggle */}
          <div className="sf-section">
            <label className="sf-checkbox-row sf-stock-row">
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={e => setInStockOnly(e.target.checked)}
              />
              <span className="sf-cat-name">{t('filters.inStockOnly')}</span>
            </label>
          </div>
        </div>
        </aside>

        {/* ── CENTER: Main product area ── */}
        <main className="shop-main">
          {/* Stats bar — sticky strip on desktop, auto-scrolling marquee on mobile.
              Rendered twice (second copy aria-hidden) so the marquee can loop
              seamlessly by translating exactly -50% of the track's width. */}
          <div className="shop-stats-bar">
            <div className="ssb-track">
              {[0, 1].map(copy => (
                <div className="ssb-items" key={copy} aria-hidden={copy === 1 || undefined}>
                  {statsBarItems.map((item, i) => (
                    <Fragment key={i}>
                      <div className="ssb-item">
                        <span className={`ssb-icon ${item.colorClass}`}>{item.icon}</span>
                        <div>
                          <p className="ssb-value">{item.value}</p>
                          <p className="ssb-label">{item.label}</p>
                        </div>
                      </div>
                      {i < statsBarItems.length - 1 && <div className="ssb-sep" />}
                    </Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Header row: title + sort */}
          <div className="shop-hdr-row">
            <div>
              <h2 className="shop-hdr-title">
                {dealsOnly
                  ? <><IconFlame size={18} style={{ color: ORANGE, verticalAlign: -2 }} /> Top Deals</>
                  : selectedCategory === 'All'
                    // On the phone this grid follows the curated feed blocks,
                    // so it reads as a continuation of them rather than the
                    // start of a catalogue: "More products for you" rather
                    // than "All Products".
                    ? (isMobileUI() ? 'More products for you' : t('categories.allProducts'))
                    : translateCategory(selectedCategory, t)}
              </h2>
              {dealsOnly && (
                <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  {filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'} currently on sale.
                </p>
              )}
            </div>
            <div className="shop-controls">
              <span className="shop-sort-lbl">{t('filters.sortBy')}</span>
              <select className="shop-sort-sel" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="default">{t('filters.sortPopular')}</option>
                <option value="trending">{t('filters.sortTrending')}</option>
                <option value="newest">{t('filters.sortNewest')}</option>
                <option value="price-asc">{t('filters.sortPriceAsc')}</option>
                <option value="price-desc">{t('filters.sortPriceDesc')}</option>
                <option value="rating">{t('filters.sortTopRated')}</option>
              </select>
            </div>
          </div>

          {/* Sort, as a chip row.

              The <select> above is hidden on mobile (see .shop-controls in
              mobile/feed.css): a native picker opening over six options to
              reorder an infinitely-scrolling list is a desktop interaction,
              and hiding it left the phone with no way to sort at all. This is
              the mobile-native form -- every option visible, one tap to apply,
              current state readable without opening anything.

              Rendered unconditionally and hidden on desktop by CSS rather than
              branched on isMobileUI(), so it follows a window resize instead
              of whatever the viewport happened to be when React mounted. It
              drives the same `sortBy` state as the select, so there is no
              second code path through the sorting logic. */}
          <div className="shop-sort-chips" role="group" aria-label={t('filters.sortBy')}>
            {SORT_CHIPS.map(({ value, key }) => (
              <button
                key={value}
                type="button"
                className={`shop-sort-chip${sortBy === value ? ' is-active' : ''}`}
                aria-pressed={sortBy === value}
                onClick={(e) => {
                  setSortBy(value);
                  // A chip tapped near the edge of the rail is often half
                  // cut off; bring it fully into view so the selection is
                  // legible after the tap that made it.
                  e.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
                }}
              >
                {t(key)}
              </button>
            ))}
          </div>

          {/* Active filter tags -- on a dedicated category page the category
              itself is already shown as the page title above, so it isn't
              repeated here as a removable chip (that's what the h2 above the
              grid is for); it still shows on /deals or the general shop view
              where "category" really is just an applied filter. */}
          {(searchTerm || (selectedCategory !== 'All' && page !== 'category') || priceRange[0] > 0 || priceRange[1] > 0 || minRating > 0 || inStockOnly) && (
            <div className="active-filters">
              <span className="active-filters-label">{t('filters.resultCount', { count: filteredProducts.length })}</span>
              {searchTerm && <span className="active-filter-tag">"{searchTerm}" <button onClick={() => setSearchTerm('')}><IconX size={11} /></button></span>}
              {selectedCategory !== 'All' && page !== 'category' && <span className="active-filter-tag">{translateCategory(selectedCategory, t)} <button onClick={() => setSelectedCategory('All')}><IconX size={11} /></button></span>}
              {(priceRange[0] > 0 || priceRange[1] > 0) && <span className="active-filter-tag">{formatCurrency(priceRange[0])}–{formatCurrency(priceRange[1])} <button onClick={() => setPriceRange([0,0])}><IconX size={11} /></button></span>}
              {minRating > 0 && <span className="active-filter-tag">{minRating}<IconStar size={10} />+ <button onClick={() => setMinRating(0)}><IconX size={11} /></button></span>}
              {inStockOnly && <span className="active-filter-tag">{t('filters.inStock')} <button onClick={() => setInStockOnly(false)}><IconX size={11} /></button></span>}
              <button className="active-filters-reset" onClick={() => { setSearchTerm(''); setSelectedCategory('All'); setPriceRange([0,0]); setMinRating(0); setInStockOnly(false); setSortBy('default'); }}>{t('filters.clearAllLower')}</button>
            </div>
          )}

          {error && <div className="alert danger">{error}</div>}

          {pageLoading ? (
            <div className={`pcard-grid${listTransitioning ? ' list-transitioning' : ''}`}>
              {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: '1/-1', padding: '60px 20px' }}>
              {dealsOnly && !searchTerm && selectedCategory === 'All' && priceRange[0] === 0 && priceRange[1] === 0 && minRating === 0 && !inStockOnly ? (
                <>
                  <p style={{ fontSize: '2rem', margin: '0 0 8px', display: 'flex', justifyContent: 'center' }}><IconFlame size={32} /></p>
                  <p style={{ fontWeight: 600, marginBottom: 6 }}>No active deals right now</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 14 }}>Check back soon — new sales are added regularly.</p>
                  <button className="btn" style={{ fontSize: '0.85rem', padding: '8px 20px' }} onClick={() => navigate('/')}>
                    Browse All Products
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '2rem', margin: '0 0 8px', display: 'flex', justifyContent: 'center' }}><IconSearch size={32} /></p>
                  <p style={{ fontWeight: 600, marginBottom: 6 }}>{t('filters.noProductsFound')}</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 14 }}>Try adjusting your filters or search term</p>
                  <button className="btn" style={{ fontSize: '0.85rem', padding: '8px 20px' }}
                    onClick={() => { setSearchTerm(''); setSelectedCategory('All'); setPriceRange([0,0]); setMinRating(0); setInStockOnly(false); setSortBy('default'); }}>
                    Clear all filters
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className={`pcard-grid${listTransitioning ? ' list-transitioning' : ''}`}>
              {pagedProducts.map((product) => {
                const saleActive = product.salePrice && product.saleEndsAt && new Date(product.saleEndsAt) > new Date();
                const effectivePrice = saleActive ? product.salePrice : product.price;
                const isOutOfStock = product.stock === 0;
                // Threshold matches the product detail page's "Only N left"
                // urgency cue (<= 10) so a product doesn't read as low-stock in
                // one place and normal in the other.
                const isLowStock = product.stock != null && product.stock > 0 && product.stock <= 10;
                // No discount badge. The percentage a shopper is saving is
                // disclosed once, in the checkout order summary -- not on the
                // card. The sale/originalPrice data behind it is untouched and
                // still drives the strikethrough price below and the totals at
                // checkout; it just isn't rendered as a ribbon here.
                //
                // Dropping the two 'deal' branches means a discounted product
                // now falls through to whichever badge it genuinely qualifies
                // for (Best Seller / Top Rated) or to none at all, so nothing
                // is left holding empty space.
                const badge =
                  isOutOfStock   ? { label: t('product.outOfStock'),      cls: 'oos'       } :
                  isLowStock     ? { label: t('product.onlyLeft', { count: product.stock }), cls: 'limited' } :
                  (product.avgRating >= 4.5 && (product.reviewCount||0) >= 50) ? { label: <><IconFlame size={11} /> {t('product.bestSeller')}</>, cls: 'bestseller' } :
                  (product.avgRating >= 4.0 && (product.reviewCount||0) >= 30) ? { label: <><IconStar size={11} /> {t('product.topRated')}</>,   cls: 'toprated'  } :
                  null;
                return (
                  <article
                    key={product.id}
                    className={`pcard${isOutOfStock ? ' pcard-oos' : ''}`}
                    onClick={() => navigate(productPath(product))}
                  >
                    {/* The image, and the things that overlay it: badge, rating, wishlist. */}
                    <div className="pcard-top">
                      <div className="pcard-img-wrap">
                        {badge && <span className={`pcard-badge pcard-badge-${badge.cls}`}>{badge.label}</span>}
                        {/* Only ever rendered for a product that actually has
                            a rating -- never an empty pill or a "0.0". */}
                        {product.avgRating > 0 && (
                          <span className="pcard-rating-badge product-rating-pill">
                            <IconStar size={11} /> {product.avgRating.toFixed(1)}
                          </span>
                        )}
                        <button
                          className="pcard-heart"
                          type="button"
                          title={t('product.addToWishlist')}
                          aria-label={t('product.addToWishlist')}
                          disabled={Boolean(wishlistPendingIds[product.id])}
                          aria-busy={wishlistPendingIds[product.id] ? 'true' : 'false'}
                          onClick={e => { e.stopPropagation(); addToWishlist(product.id); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                        {isOutOfStock && <div className="pcard-oos-overlay"><span>{t('product.outOfStock')}</span></div>}
                        <ProductImage src={mediaUrl(product.image)} alt={product.name} loading="lazy" />
                      </div>
                    </div>

                    {/* What the product is, then what you can do about it. The actions sit at
                        the foot of this block rather than directly under the
                        image, so a shopper reads the name, rating and price
                        before meeting a button. */}
                    <div className="pcard-body">
                      {/* A real <a href>, not just the card's onClick. Crawlers
                          only traverse anchors, so without this every product
                          was reachable exclusively via sitemap.xml -- no
                          internal links pointing at it from anywhere on the
                          site. The product name doubles as the anchor text,
                          which is the strongest relevance signal a link can
                          carry. Clicking the card anywhere still works exactly
                          as before; this additionally makes ctrl/middle-click
                          "open in new tab" work, which a div never could.

                          The card is an <article> with its own onClick rather
                          than an <a> wrapping everything, because the card
                          contains real <button>s (wishlist, compare, add to
                          cart) and nesting those inside an anchor is invalid
                          HTML. stopPropagation keeps the card's handler from
                          firing a second, redundant navigation on top of the
                          link's own. */}
                      <h3 className="pcard-title">
                        <Link
                          to={`/product/${product.id}`}
                          className="pcard-title-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {product.name}
                        </Link>
                      </h3>

                      <div className="pcard-rating-row">
                        <RatingStars rating={product.avgRating} count={product.reviewCount} size={12} />
                      </div>

                      {product.sellerName && (
                        <p className="pcard-seller">
                          {t('product.soldBy')} <span className="pcard-seller-name">{product.sellerName}</span>
                          <span className="pcard-verified"> <IconCheck size={11} /></span>
                        </p>
                      )}

                      <div className="pcard-price-row">
                        <span className="pcard-price">{formatCurrency(effectivePrice)}</span>
                        {saleActive ? (
                          <span className="pcard-original">{formatCurrency(product.price)}</span>
                        ) : product.originalPrice ? (
                          <span className="pcard-original">{formatCurrency(product.originalPrice)}</span>
                        ) : null}
                      </div>

                      {/* Availability is only worth a badge when it's not the
                          default. A plain "In Stock" pill on every card was
                          noise (and used to be the one green thing left in a
                          four-colour palette), so in-stock products show no
                          pill at all -- the enabled Add to Cart button is the
                          availability signal. The row itself is skipped
                          entirely when there's nothing to put in it, rather
                          than collapsing to an empty gap. */}
                      {(isOutOfStock || isLowStock || (product.sales || 0) > 0) && (
                        <div className="pcard-stock-row">
                          {isOutOfStock ? (
                            <span className="pcard-status-pill pcard-status-oos">
                              <IconAlertTriangle size={11} aria-hidden="true" />{t('product.outOfStock')}
                            </span>
                          ) : isLowStock ? (
                            <span className="pcard-status-pill pcard-status-low">
                              <span className="pcard-status-dot" />{t('product.onlyLeft', { count: product.stock })}
                            </span>
                          ) : null}
                          {(product.sales || 0) > 0 && (
                            <span className="pcard-sold">
                              {t('product.sold', { count: product.sales >= 1000 ? `${(product.sales/1000).toFixed(1)}K+` : product.sales })}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="pcard-actions">
                        <button
                          className="pcard-add-cart-btn"
                          type="button"
                          disabled={isOutOfStock}
                          onClick={e => {
                            e.stopPropagation();
                            // No size/colour picker on the card -- send them to
                            // the product page instead of silently adding without one.
                            if (product.sizes?.length > 0 || product.variants?.length > 0) { navigate(`/product/${product.id}`); return; }
                            addToCart(product);
                          }}
                        >
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                          </svg>
                          {t('product.addToCart')}
                        </button>
                        <div className="pcard-quick-row">
                          <button className="pcard-quick-btn" type="button" aria-label={t('product.quickView')}
                            title={t('product.quickView')}
                            onClick={e => { e.stopPropagation(); navigate(`/product/${product.id}`); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="3"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            </svg>
                            <span className="pcard-quick-label">{t('product.quickView')}</span>
                          </button>
                          <button className={`pcard-quick-btn${compareIds.includes(product.id) ? ' active' : ''}`} type="button" aria-label={t('product.compare')}
                            title={compareIds.includes(product.id) ? t('product.compareAdded') : t('product.compare')}
                            onClick={e => { e.stopPropagation(); toggleCompare(product.id); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                            </svg>
                            <span className="pcard-quick-label">{compareIds.includes(product.id) ? t('product.compareAdded') : t('product.compare')}</span>
                          </button>
                        </div>
                      </div>

                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* A sentinel the observer watches, plus a spinner while the next
              batch is being sliced in. When everything is shown it becomes a
              quiet end-of-list marker rather than disappearing, so the list
              has an ending instead of just stopping -- and so the footer stops
              retreating down the page. */}
          {filteredProducts.length > 0 && (
            <div className="shop-infinite" aria-live="polite">
              {shopHasMore ? (
                // The observed node, and it carries `key={shopVisible}` for a
                // reason that is easy to lose.
                //
                // IntersectionObserver reports threshold *crossings*, not a
                // standing state. On a wide grid a batch of 24 is only ~5 rows,
                // so after one load the sentinel is still inside the 600px
                // rootMargin -- it never stopped intersecting, so nothing was
                // ever crossed, so the callback never fired again and the list
                // stuck at 48. Re-keying it on each batch remounts the node,
                // which re-runs the callback ref and arms a fresh observer that
                // evaluates the current position immediately: still in view
                // means load again, and the chain continues until the sentinel
                // is finally pushed out of range.
                //
                // The ref sits here rather than on the wrapper so it unmounts
                // the moment there is nothing left to load. On the wrapper it
                // would keep firing against a list that cannot grow, and each
                // re-key would remount it again -- a loop with no exit.
                <span key={shopVisible} ref={shopSentinelRef} className="shop-infinite-loading">
                  <span className="shop-infinite-spinner" aria-hidden="true" />
                  Loading more products…
                </span>
              ) : (
                filteredProducts.length > SHOP_PAGE_SIZE && (
                  <span className="shop-infinite-end">
                    {filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'} · that's everything
                  </span>
                )
              )}
            </div>
          )}

        </main>
      </div>

      {/* The footer is a web-site structure: five link columns, legal links,
          payment and social marks. In an installed app that content is either
          unreachable or already covered by the bottom nav and account sheet,
          and it added a long scroll of links under every page. Hidden in the
          installed app only -- the website keeps it, including for SEO, where
          those internal links genuinely matter.
          isInstalledApp() rather than isNativePlatform(): a PWA opened from the
          home screen is the same app with the same bottom nav, and this footer
          under it was the single loudest reason the PWA still read as the
          website. It carries a phone-width condition, so a desktop-installed
          PWA -- which has no bottom nav -- keeps the footer it navigates by. */}
      {!isInstalledApp() && (
      <Footer
        onShopAll={() => { navigate('/'); setSelectedCategory('All'); }}
        onShopNew={() => { navigate('/'); setSelectedCategory('All'); setSortBy('newest'); }}
        onShopDeals={() => { setSelectedCategory('All'); navigate('/deals'); }}
        onShopTrending={() => { navigate('/'); setSelectedCategory('All'); setSortBy('trending'); }}
        onShopTopRated={() => { navigate('/'); setSelectedCategory('All'); setSortBy('rating'); }}
        onSitemap={() => window.open(`${API_BASE_URL}/sitemap.xml`, '_blank', 'noopener,noreferrer')}
        onBecomeSeller={() => navigateTo('seller-registration')}
        onMyOrders={() => navigateTo('orders')}
        onWishlist={() => navigateTo('wishlist')}
        onAddresses={() => navigateTo('address')}
        onSettings={() => navigateTo('settings')}
        onTrackOrder={() => navigateTo('track-order')}
        onPrivacy={() => navigateTo('privacy')}
        onTerms={() => navigateTo('terms')}
        onCookies={() => navigateTo('cookies')}
        onReturns={() => navigateTo('returns')}
        onShipping={() => navigateTo('shipping')}
        onCareers={() => navigateTo('careers')}
        onAbout={() => navigateTo('about')}
        onCommissionPlan={() => navigateTo('commission-plan')}
        onSellerGuidelines={() => navigateTo('seller-guidelines')}
        onNewsUpdates={() => navigateTo('news-updates')}
        onBrands={() => navigateTo('brands')}
        onContact={() => navigateTo('contact')}
        onFaq={() => navigateTo('faq')}
        onHelpCenter={() => navigateTo('helpCenter')}
        onSellerFaq={() => navigateTo('sellerFaq')}
        onGetApp={() => navigateTo('get-app')}
        onPlaceholder={pushToast}
      />
      )}

      {showMobileCatSheet && (
        <div
          className="bn-cat-sheet-overlay"
          onClick={() => setShowMobileCatSheet(false)}
          onKeyDown={e => { if (e.key === 'Escape') setShowMobileCatSheet(false); }}
        >
          <div
            ref={catSheetRef}
            tabIndex={-1}
            className="bn-cat-sheet"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Categories"
          >
            <div
              className="bn-cat-sheet-draghandle"
              onTouchStart={handleCatSheetDragStart}
              onTouchMove={handleCatSheetDragMove}
              onTouchEnd={handleCatSheetDragEnd}
            >
              <div className="bn-cat-sheet-handle" />
              <div className="bn-cat-sheet-hdr-row">
                <h3 className="bn-cat-sheet-title">Categories</h3>
                <button
                  type="button"
                  className="bn-cat-sheet-close"
                  onClick={() => setShowMobileCatSheet(false)}
                  aria-label="Close categories"
                >
                  <IconX size={18} />
                </button>
              </div>
            </div>
            <button
              className={`bn-cat-sheet-item${selectedCategory === 'All' && !dealsOnly ? ' active' : ''}`}
              onClick={() => { setSelectedCategory('All'); setShowMobileCatSheet(false); if (location.pathname !== '/') navigate('/'); }}
            >
              All Categories
            </button>
            <Link
              to="/deals"
              className={`bn-cat-sheet-item${dealsOnly ? ' active' : ''}`}
              onClick={() => { setSelectedCategory('All'); setShowMobileCatSheet(false); }}
            >
              <IconFlame size={14} /> Deals
            </Link>
            {categories.filter(c => c !== 'All').map(cat => (
              <Link
                key={cat}
                to={`/category/${encodeURIComponent(cat)}`}
                className={`bn-cat-sheet-item${selectedCategory === cat && !dealsOnly ? ' active' : ''}`}
                onClick={() => setShowMobileCatSheet(false)}
              >
                <span className="nb-cat-icon">{(() => { const CatIcon = getCategoryIcon(cat); return <CatIcon size={15} />; })()}</span>
                {cat}
              </Link>
            ))}
          </div>
        </div>
      )}

      <BottomNav
        active={page === 'home' ? 'home' : page}
        cartQuantity={cartQuantity}
        wishlistCount={wishlistCount}
        onNavigate={(target) => {
          if (target === 'home') { navigate('/'); }
          else if (target === 'wishlist') { navigateTo('wishlist'); }
          else if (target === 'cart') { openCartPanel(); }
        }}
        onOpenCategories={() => setShowMobileCatSheet(true)}
        onOpenAccount={handleOpenAccountSheet}
      />
      {showMobileAccountSheet && (
        <AccountSheet
          user={user}
          wishlistCount={wishlistCount}
          unreadNotifCount={unreadNotifCount}
          isSeller={isSeller}
          onNavigate={navigateTo}
          onLogout={handleLogout}
          onClose={() => setShowMobileAccountSheet(false)}
        />
      )}

      {compareIds.length > 0 && !showCompareModal && (
        <div className="compare-bar">
          <div className="compare-bar-thumbs">
            {compareIds.map((id) => {
              const p = products.find((prod) => prod.id === id);
              return p ? (
                <div key={id} className="compare-bar-thumb">
                  <img src={mediaUrl(p.image)} alt={p.name} onError={fallbackToPlaceholder} />
                  <button type="button" aria-label={t('compare.remove')} onClick={() => toggleCompare(id)}><IconX size={10} /></button>
                </div>
              ) : null;
            })}
          </div>
          <span className="compare-bar-title">{t('compare.barTitle', { count: compareIds.length, max: MAX_COMPARE })}</span>
          <div className="compare-bar-actions">
            <button type="button" className="compare-bar-clear" onClick={() => setCompareIds([])}>{t('compare.clearAll')}</button>
            <button type="button" className="btn compare-bar-btn" onClick={() => setShowCompareModal(true)}>{t('compare.compareNow')}</button>
          </div>
        </div>
      )}

      {showCompareModal && (
        <div className="compare-modal-overlay" onClick={() => setShowCompareModal(false)}>
          <div className="compare-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="compare-modal-title">
            <div className="compare-modal-header">
              <h2 id="compare-modal-title">{t('compare.title')}</h2>
              <button type="button" className="compare-modal-close" aria-label={t('compare.close')} onClick={() => setShowCompareModal(false)}><IconX size={16} /></button>
            </div>
            <div className="compare-modal-table-wrap">
              <table className="compare-modal-table">
                <tbody>
                  <tr>
                    <th>{t('compare.image')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p && <ProductImage src={mediaUrl(p.image)} alt={p.name} />}</td>;
                    })}
                  </tr>
                  <tr>
                    <th>{t('compare.name')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p ? <button type="button" className="compare-modal-link" onClick={() => { setShowCompareModal(false); navigate(`/product/${p.id}`); }}>{p.name}</button> : '—'}</td>;
                    })}
                  </tr>
                  <tr>
                    <th>{t('compare.price')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p ? formatCurrency(p.salePrice ?? p.price) : '—'}</td>;
                    })}
                  </tr>
                  <tr>
                    <th>{t('compare.rating')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p ? (p.avgRating ? `${p.avgRating.toFixed(1)} ★ (${p.reviewCount || 0})` : t('product.noRatingsYet')) : '—'}</td>;
                    })}
                  </tr>
                  <tr>
                    <th>{t('compare.category')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p ? translateCategory(p.category, t) : '—'}</td>;
                    })}
                  </tr>
                  <tr>
                    <th>{t('compare.stock')}</th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return <td key={id}>{p ? (p.stock === 0 ? t('product.outOfStock') : t('product.inStock')) : '—'}</td>;
                    })}
                  </tr>
                  <tr>
                    <th></th>
                    {compareIds.map((id) => {
                      const p = products.find((prod) => prod.id === id);
                      return (
                        <td key={id}>
                          {p && (
                            <div className="compare-modal-row-actions">
                              <button type="button" className="btn" disabled={p.stock === 0} onClick={() => addToCart(p)}>{t('product.addToCart')}</button>
                              <button type="button" className="compare-modal-remove" onClick={() => toggleCompare(id)}>{t('compare.remove')}</button>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {renderToasts()}
      {showDevApi && <DevApiSettings onClose={() => setShowDevApi(false)} />}
    </div>
  );
}

// The chat widget is mounted here, above AppShell's internal page branching,
// so it survives every one of AppShell's many early `return`s (one per page)
// instead of needing to be duplicated into each. Hidden on admin/seller
// surfaces -- this is a customer-support assistant, not a merchant tool, and
// those areas already have their own support paths.
function App() {
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const hideChat = /^\/(admin|super-admin|seller|portal-access)/.test(location.pathname);
  // The install banner is mounted here for the same reason as the chat widget,
  // but it is hidden on more surfaces. Suppressed on the merchant portals (an
  // install offer is noise while running a shop), on /app itself (it links
  // there -- offering it on its own destination is a dead end), and during
  // checkout, where nothing should compete with Place Order. InstallAppBanner
  // decides the rest for itself: it renders nothing on desktop, inside the
  // packaged app, or once the PWA is installed.
  const hideInstallOffer = /^\/(admin|super-admin|seller|portal-access|app|cart)/.test(location.pathname);

  return (
    <>
      <Suspense fallback={<div className="loading loading-inline">Loading...</div>}>
        <AppShell onOpenChat={() => setChatOpen(true)} />
      </Suspense>
      {!hideChat && <ChatWidget open={chatOpen} onOpenChange={setChatOpen} />}
      {!hideInstallOffer && <InstallAppBanner />}
      {/* Not gated on hideInstallOffer: an install offer is a suggestion that
          can wait for a better moment, but running a stale build is a state the
          person should be able to leave from wherever they are. */}
      <UpdateAppBanner />
      {/* Native-only, and it renders nothing on the web. Mounted outside the
          hide rules above because an out-of-date build has to be told about
          everywhere, including mid-checkout. */}
      <AppUpdateNotice />
      <PullToRefresh />
    </>
  );
}

export default App;
